/**
 * App.jsx — File Tracking Explorer
 * Tailored for file_index + routing_rules + message_hub schema
 *
 * Drop into src/App.jsx of a CRA or Vite React project.
 * API URL is injected at deploy time via /config.js (window.APP_CONFIG.API_URL)
 * For local dev, falls back to http://localhost:4000
 *
 * npm install  (no extra packages needed beyond React itself)
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

// API URL: injected at deploy time via /config.js, falls back to localhost for dev
const API = (window.APP_CONFIG && window.APP_CONFIG.API_URL) || process.env.REACT_APP_API_URL || "http://localhost:4000";

// Environment label — read from config.js (set at deploy time), with display name mapping
const ENV_RAW  = (window.APP_CONFIG && window.APP_CONFIG.ENV) || process.env.REACT_APP_ENV || "";
const ENV_LABEL = { test: "UAT", dev: "DEV", prod: "PROD", uat: "UAT" }[ENV_RAW.toLowerCase()] || ENV_RAW.toUpperCase() || null;

// ─── App Insights ─────────────────────────────────────────────────────────────
const _aiConnStr = (window.APP_CONFIG && window.APP_CONFIG.APPINSIGHTS_CONNECTION_STRING)
  || process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING
  || null;

const _ai = (() => {
  if (!_aiConnStr) return null;
  const ai = new ApplicationInsights({
    config: {
      connectionString: _aiConnStr,
      enableAutoRouteTracking: false, // we track tabs manually
      disableAjaxTracking: false,     // auto-track XHR/fetch calls
      disableExceptionTracking: false,
    },
  });
  ai.loadAppInsights();
  return ai;
})();

// Attach user identity once known — called from App after login
function aiSetUser(oid, upn) {
  if (!_ai) return;
  _ai.setAuthenticatedUserContext(oid, upn, true);
}

// Generic event tracker used throughout the app
function trackEvent(name, properties) {
  if (!_ai) return;
  try { _ai.trackEvent({ name }, properties); } catch (_) {}
}

// ── Auth helper ───────────────────────────────────────────────────────────────
let _msalInstance = null;

// ── apiFetch — attaches Bearer token, handles expiry automatically ──────────
// On silent token failure: attempts interactive re-login so user never sees
// a blank screen. On 401 from the backend: same re-login flow.
var _reloginInProgress = false;

function _triggerRelogin(reason) {
  if (_reloginInProgress) return; // prevent multiple simultaneous attempts
  _reloginInProgress = true;
  console.warn("[apiFetch] Triggering re-login:", reason);
  var isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  if (_msalInstance) {
    var account = _msalInstance.getActiveAccount() || (_msalInstance.getAllAccounts()[0] || null);
    if (isLocalhost) {
      // Clear stale local state then pop up fresh login
      _msalInstance.clearCache().catch(function() {}).finally(function() {
        _reloginInProgress = false;
        _msalInstance.loginPopup(loginRequest).then(function(result) {
          if (result && result.account) _msalInstance.setActiveAccount(result.account);
          window.location.reload();
        }).catch(function(e) {
          console.error("[apiFetch] Re-login popup failed:", e);
        });
      });
    } else {
      _msalInstance.clearCache().catch(function() {}).finally(function() {
        _msalInstance.loginRedirect(loginRequest);
      });
    }
  }
}

async function apiFetch(url, opts) {
  opts = opts || {};
  if (_msalInstance) {
    var account = _msalInstance.getActiveAccount() || (_msalInstance.getAllAccounts()[0] || null);
    if (account) {
      var apiScopes = loginRequest.scopes.filter(function(s) { return s.indexOf("api://") === 0; });
      var scopesToUse = apiScopes.length > 0 ? apiScopes : loginRequest.scopes;
      try {
        var tokenResp = await _msalInstance.acquireTokenSilent({ scopes: scopesToUse, account: account });
        opts = Object.assign({}, opts, {
          headers: Object.assign({}, opts.headers || {}, { Authorization: "Bearer " + tokenResp.accessToken })
        });
      } catch (silentErr) {
        // Silent refresh failed — token is expired or session is gone.
        // Trigger interactive re-login immediately rather than making
        // an unauthenticated call that returns a blank/error screen.
        console.warn("[apiFetch] Silent token refresh failed:", silentErr.message || silentErr);
        _triggerRelogin("silent refresh failed: " + (silentErr.message || silentErr));
        // Return a never-resolving promise so the caller never processes a bad response
        return new Promise(function() {});
      }
    }
  }

  var response = await fetch(url, opts);

  // If the backend returns 401, the token was rejected (e.g. audience mismatch
  // after a config change, or the token expired between acquisition and use).
  // Trigger re-login so the user gets a fresh token automatically.
  if (response.status === 401) {
    console.warn("[apiFetch] 401 received from", url, "— triggering re-login");
    _triggerRelogin("401 from API");
    return new Promise(function() {}); // prevent caller from processing the 401 body
  }

  return response;
}

// ─── User Preferences (saved filters) ────────────────────────────────────────
// Simple module-level cache so all SavedFiltersMenu instances share one fetch.
let _prefsCache = null;

function _fetchPrefs() {
  return apiFetch(`${API}/api/user-preferences`).then(r => r.json()).then(d => { _prefsCache = d || {}; return _prefsCache; });
}
function _savePrefs(prefs) {
  _prefsCache = prefs;
  return apiFetch(`${API}/api/user-preferences`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(prefs),
  }).then(r => r.json());
}

// ─── useAccess — fetches the user's access profile from /api/me ──────────────
// Returns { role, partners } — available globally via React context.
const AccessContext = React.createContext({ role: "none", partners: [] });

function useAccess() { return React.useContext(AccessContext); }

// ─── Hooks ────────────────────────────────────────────────────────────────────
function useDebounce(val, ms = 400) {
  const [v, setV] = useState(val);
  useEffect(() => { const t = setTimeout(() => setV(val), ms); return () => clearTimeout(t); }, [val, ms]);
  return v;
}

// Syncs a single URL search param with React state
function useUrlState(key, defaultVal) {
  const getFromUrl = () => {
    const p = new URLSearchParams(window.location.search);
    return p.has(key) ? p.get(key) : defaultVal;
  };
  const [val, setValRaw] = useState(getFromUrl);
  const setVal = useCallback((next) => {
    setValRaw(next);
    const p = new URLSearchParams(window.location.search);
    if (next === defaultVal || next === "" || next === null) p.delete(key);
    else p.set(key, next);
    const qs = p.toString();
    window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
  }, [key, defaultVal]);
  return [val, setVal];
}

// Auto-refresh: calls `fn` every `intervalMs`. Returns { countdown }
function useAutoRefresh(fn, intervalMs, enabled) {
  const [countdown, setCountdown] = useState(intervalMs / 1000);
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  useEffect(() => {
    if (!enabled || !intervalMs) { setCountdown(intervalMs / 1000); return; }
    setCountdown(intervalMs / 1000);
    const tick = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { fnRef.current(); return intervalMs / 1000; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [enabled, intervalMs]);
  return { countdown };
}

// ─── Constants ────────────────────────────────────────────────────────────────
const TIME_OPTIONS = [
  { label: "Last 1 hour",   hours: 1 },
  { label: "Last 6 hours",  hours: 6 },
  { label: "Last 24 hours", hours: 24 },
  { label: "Last 3 days",   hours: 72 },
  { label: "Last 7 days",   hours: 168 },
  { label: "Last 14 days",  hours: 336 },
  { label: "Last 30 days",  hours: 720 },
  { label: "Last 60 days",  hours: 1440 },
  { label: "Last 90 days",  hours: 2160 },
];
const CUSTOM_RANGE = "custom";
const MAX_RANGE_DAYS = 90;

// Custom range helpers — range is stored as ISO strings, edited as local datetime-local values
const pad2 = n => String(n).padStart(2, "0");
function toLocalInput(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function fmtRangeDate(iso) {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}
// Query-string params for the selected period: {from,to} when custom, else {hours}
function rangeParams(hours, from, to) {
  return from ? { from, to: to || new Date().toISOString() } : { hours };
}

// Columns shown in Files tab (order matters)
// Default widths (px) — file name columns are drag-resizable
const FILE_COL_DEFAULTS = {
  created_at:              155,
  last_updated:            155,
  file_name:               280,
  current_file_name:       280,
  status:                  120,
  inbound_partner_name:    170,
  outbound_partner_name:   170,
  tracking_correlation_id: 300,  // wide enough for full GUIDs/long IDs
  current_size:            110,
};

const FILE_COLS = [
  { key: "created_at",              label: "Created At",        sortable: true,  resizable: false },
  { key: "last_updated",            label: "Last Updated",      sortable: true,  resizable: false },
  { key: "file_name",               label: "File Name",         sortable: true,  resizable: true  },
  { key: "current_file_name",       label: "Current File Name", sortable: true,  resizable: true  },
  { key: "current_size",            label: "Size",              sortable: true,  resizable: false },
  { key: "status",                  label: "Status",            sortable: true,  resizable: false },
  { key: "inbound_partner_name",    label: "Inbound Partner",   sortable: true,  resizable: false },
  { key: "outbound_partner_name",   label: "Outbound Partner",  sortable: true,  resizable: false },
  { key: "tracking_correlation_id", label: "Correlation ID",    sortable: true,  resizable: true  },
];

const MSG_COLS = [
  { key: "id",            label: "ID",            sortable: true },
  { key: "created_date",  label: "Created",       sortable: true },
  { key: "modified_date", label: "Modified",      sortable: true },
  { key: "message_content", label: "Content",     sortable: false },
];

// Columns visible in the Partners table (summary view)
const PARTNER_DISPLAY_COLS = [
  { key: "partner_name", label: "Partner Name", sortable: true  },
  { key: "created_date", label: "Created",      sortable: true  },
  { key: "description",  label: "Description",  sortable: false },
  { key: "is_coke",      label: "Coke",         sortable: true  },
  { key: "is_internet",  label: "Internet",     sortable: true  },
  { key: "is_active",    label: "Active",       sortable: true  },
];

// All columns shown in the expanded detail panel
const PARTNER_DETAIL_FIELDS = [
  ["Partner ID",    "partner_id"],
  ["Partner Name",  "partner_name"],
  ["Description",   "description"],
  ["Is Active",     "is_active"],
  ["Is Coke",       "is_coke"],
  ["Is Internet",   "is_internet"],
  ["Is Deleted",    "is_deleted"],
  ["Created By",    "created_by"],
  ["Created Date",  "created_date"],
  ["Modified By",   "modified_by"],
  ["Modified Date", "modified_date"],
  ["Deleted By",    "deleted_by"],
  ["Deleted Date",  "deleted_date"],
];

// Contacts tab — summary columns
const CONTACT_DISPLAY_COLS = [
  { key: "name",         label: "Name",      sortable: true  },
  { key: "email",        label: "Email",     sortable: true  },
  { key: "phone_number", label: "Phone",     sortable: false },
  { key: "is_coke",      label: "Coke",      sortable: true  },
  { key: "is_technical", label: "Technical", sortable: true  },
  { key: "is_active",    label: "Active",    sortable: true  },
  { key: "partners",     label: "Partners",  sortable: false },
];

// All fields shown in expanded contact detail / modal
const CONTACT_DETAIL_FIELDS = [
  ["Contact ID",    "contact_id"],
  ["Name",          "name"],
  ["Email",         "email"],
  ["Phone",         "phone_number"],
  ["Is Coke",       "is_coke"],
  ["Is Technical",  "is_technical"],
  ["Is Active",     "is_active"],
  ["Is Deleted",    "is_deleted"],
  ["Created By",    "created_by"],
  ["Created Date",  "created_date"],
  ["Modified By",   "modified_by"],
  ["Modified Date", "modified_date"],
  ["Deleted By",    "deleted_by"],
  ["Deleted Date",  "deleted_date"],
];

// Connections tab — summary columns
const CONNECTION_DISPLAY_COLS = [
  { key: "connection_name",    label: "Connection Name", sortable: true  },
  { key: "protocol",          label: "Protocol",        sortable: true  },
  { key: "partner_name",      label: "Partner",         sortable: true  },
  { key: "is_active",         label: "Active",          sortable: true  },
  { key: "created_date",      label: "Created",         sortable: true  },
  { key: "modified_date",     label: "Modified",        sortable: true  },
];

// All fields shown in expanded connection detail panel
const CONNECTION_DETAIL_FIELDS = [
  ["Connection ID",              "connection_id"],
  ["Connection Name",            "connection_name"],
  ["Protocol",                   "protocol"],
  ["Partner ID",                 "partner_id"],
  ["Partner Name",               "partner_name"],
  ["Is Active",                  "is_active"],
  ["Is Deleted",                 "is_deleted"],
  ["Created By",                 "created_by"],
  ["Created Date",               "created_date"],
  ["Modified By",                "modified_by"],
  ["Modified Date",              "modified_date"],
  ["Deleted By",                 "deleted_by"],
  ["Deleted Date",               "deleted_date"],
  // Note: connection_details and extended_connection_details are rendered
  // as full scrollable text boxes above the grid — not in this list
];

// Flows tab — summary columns
const FLOW_DISPLAY_COLS = [
  { key: "flow_name",     label: "Flow Name",  sortable: true  },
  { key: "flow_type",     label: "Type",       sortable: true  },
  { key: "protocol",      label: "Protocol",   sortable: true  },
  { key: "partner_name",  label: "Partner",    sortable: true  },
  { key: "is_active",     label: "Active",     sortable: true  },
  { key: "frequency",     label: "Frequency",  sortable: true  },
  { key: "last_run_time", label: "Last Run",   sortable: true  },
  { key: "uploaded_files",label: "Uploaded",   sortable: true  },
  { key: "failed_files",  label: "Failed",     sortable: true  },
];

// All fields in expanded flow detail — grouped logically
const FLOW_DETAIL_FIELDS = [
  ["Flow ID",                  "flow_id"],
  ["Flow Name",                "flow_name"],
  ["Flow Type",                "flow_type"],
  ["Description",              "description"],
  ["Protocol",                 "protocol"],
  ["Config Version",           "config_version"],
  ["Default Routing Rule",     "default_routing_rule"],
  ["Partner ID",               "partner_id"],
  ["Source Partner ID",        "source_partner_id"],
  ["Target Partner ID",        "target_partner_id"],
  ["Connection ID",            "connection_id"],
  ["Connection Details",       "connection_details"],
  ["Extended Conn. Details",   "extended_connection_details"],
  ["SFTP Username",            "sftp_username"],
  ["Absolute Flow Path",       "absolute_flow_path"],
  ["Folder Path",              "folder_path"],
  ["File Processing Rules",    "file_processing_rules"],
  ["Delete Source File",       "delete_source_file"],
  ["Calculate Checksum",       "calculate_checksum"],
  ["Frequency",                "frequency"],
  ["Last Run Time",            "last_run_time"],
  ["Uploaded Files",           "uploaded_files"],
  ["Failed Files",             "failed_files"],
  ["Retry Attempts",           "retry_attempts"],
  ["Max Retry Attempts",       "max_retry_attempts"],
  ["Retry Delay (s)",          "retry_delay_seconds"],
  ["Is Active",                "is_active"],
  ["Is Deleted",               "is_deleted"],
  ["Created At",               "created_at"],
  ["Updated At",               "updated_at"],
  ["Created By",               "created_by"],
  ["Created Date",             "created_date"],
  ["Modified By",              "modified_by"],
  ["Modified Date",            "modified_date"],
  ["Deleted By",               "deleted_by"],
  ["Deleted Date",             "deleted_date"],
];


// Pipelines tab — summary columns
const PIPELINE_DISPLAY_COLS = [
  { key: "inbound_partner_name",  label: "Inbound Partner",  sortable: true  },
  { key: "inbound_flow_name",     label: "Inbound Flow",     sortable: true  },
  { key: "outbound_partner_name", label: "Outbound Partner", sortable: true  },
  { key: "outbound_flow_name",    label: "Outbound Flow",    sortable: true  },
  { key: "action",                label: "Action",           sortable: true  },
  { key: "priority",              label: "Priority",         sortable: true  },
  { key: "enabled",               label: "Enabled",          sortable: true  },
  { key: "is_active",             label: "Active",           sortable: true  },
  { key: "has_activity",          label: "Activity",         sortable: true  },
];

// Pipelines export — all selectable fields grouped by category
const PIPELINE_EXPORT_GROUPS = [
  {
    label: "Inbound",
    fields: [
      { key: "inbound_partner_name",       label: "Inbound Partner"     },
      { key: "inbound_partner_id",         label: "Inbound Partner ID"  },
      { key: "inbound_flow_name",          label: "Inbound Flow"        },
      { key: "inbound_flow_id",            label: "Inbound Flow ID"     },
      { key: "inbound_protocol",           label: "Inbound Protocol"    },
      { key: "inbound_absolute_flow_path", label: "Inbound Path"        },
      { key: "inbound_connection_name",            label: "Inbound Connection"          },
      { key: "inbound_connection_details",         label: "Inbound Connection Details"  },
      { key: "inbound_extended_connection_details",label: "Inbound Extended Conn Details"},
      { key: "inbound_frequency",                  label: "Inbound Frequency"           },
      { key: "inbound_last_run_time",      label: "Inbound Last Run"    },
      { key: "inbound_is_active",          label: "Inbound Active"      },
    ],
  },
  {
    label: "Outbound",
    fields: [
      { key: "outbound_partner_name",       label: "Outbound Partner"    },
      { key: "outbound_partner_id",         label: "Outbound Partner ID" },
      { key: "outbound_flow_name",          label: "Outbound Flow"       },
      { key: "outbound_flow_id",            label: "Outbound Flow ID"    },
      { key: "outbound_protocol",           label: "Outbound Protocol"   },
      { key: "outbound_absolute_flow_path", label: "Outbound Path"       },
      { key: "outbound_connection_name",            label: "Outbound Connection"          },
      { key: "outbound_connection_details",         label: "Outbound Connection Details"  },
      { key: "outbound_extended_connection_details",label: "Outbound Extended Conn Details"},
      { key: "outbound_frequency",                  label: "Outbound Frequency"           },
      { key: "outbound_last_run_time",      label: "Outbound Last Run"   },
      { key: "outbound_is_active",          label: "Outbound Active"     },
    ],
  },
  {
    label: "Pipeline",
    fields: [
      { key: "rule_id",          label: "Rule ID"         },
      { key: "action",           label: "Action"          },
      { key: "priority",         label: "Priority"        },
      { key: "enabled",          label: "Enabled"         },
      { key: "is_active",        label: "Active"          },
      { key: "has_activity",     label: "Has Activity"    },
      { key: "file_count",       label: "File Count"      },
      { key: "match_condition",  label: "Match Condition" },
      { key: "full_target_path", label: "Target Path"     },
    ],
  },
  {
    label: "Metadata",
    fields: [
      { key: "created_at",    label: "Created At"    },
      { key: "created_by",    label: "Created By"    },
      { key: "modified_date", label: "Modified Date" },
      { key: "modified_by",   label: "Modified By"   },
    ],
  },
];

const PIPELINE_EXPORT_DEFAULT_KEYS = [
  "inbound_partner_name", "inbound_flow_name",
  "outbound_partner_name", "outbound_flow_name",
  "action", "priority", "enabled", "is_active", "has_activity",
];

const LS_EXPORT_KEY = "pipeline_export_fields";

function loadExportFields() {
  try {
    const stored = localStorage.getItem(LS_EXPORT_KEY);
    if (stored) return JSON.parse(stored);
  } catch (_) {}
  return PIPELINE_EXPORT_DEFAULT_KEYS;
}

function saveExportFields(keys) {
  try { localStorage.setItem(LS_EXPORT_KEY, JSON.stringify(keys)); } catch (_) {}
}

function csvEscape(val) {
  if (val == null) return "";
  const s = String(val);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildCsv(rows, selectedKeys) {
  // Build label map and a canonical order from the group definitions
  const labelMap = {};
  const groupOrder = [];
  PIPELINE_EXPORT_GROUPS.forEach(g => g.fields.forEach(f => {
    labelMap[f.key] = f.label;
    groupOrder.push(f.key);
  }));

  // Sort selected keys by their position in the group definitions
  const keySet = new Set(selectedKeys);
  selectedKeys = groupOrder.filter(k => keySet.has(k));

  const header = selectedKeys.map(k => csvEscape(labelMap[k] || k)).join(",");
  const DATE_KEYS = new Set(["created_at","modified_date","inbound_last_run_time","outbound_last_run_time"]);
  const BOOL_KEYS = new Set(["enabled","is_active","has_activity","inbound_is_active","outbound_is_active"]);

  const dataRows = rows.map(row =>
    selectedKeys.map(k => {
      const v = row[k];
      if (v == null || v === "") return "";
      if (BOOL_KEYS.has(k)) return v === true || v === "true" ? "Yes" : "No";
      if (DATE_KEYS.has(k) && /^\d{4}-\d{2}-\d{2}T/.test(String(v))) return csvEscape(new Date(v).toLocaleString());
      if (typeof v === "object") return csvEscape(JSON.stringify(v));
      return csvEscape(v);
    }).join(",")
  );

  return [header, ...dataRows].join("\r\n");
}

function triggerCsvDownload(csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);
  a.href     = url;
  a.download = `pipelines_export_${date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Pipeline Export Modal ────────────────────────────────────────────────────
function PipelineExportModal({ onClose, onExport, exporting }) {
  const [selected, setSelected] = useState(() => new Set(loadExportFields()));

  const toggle = key => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      saveExportFields([...next]);
      return next;
    });
  };

  const toggleGroup = (fields, allOn) => {
    setSelected(prev => {
      const next = new Set(prev);
      fields.forEach(f => allOn ? next.delete(f.key) : next.add(f.key));
      saveExportFields([...next]);
      return next;
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.6)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, width: 620, maxWidth: "95vw", maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: ".95rem", color: "var(--text)" }}>Export Pipelines</div>
            <div style={{ fontSize: ".74rem", color: "var(--muted)", marginTop: 2 }}>Select fields to include in the CSV. Selection is saved automatically.</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {/* Field groups */}
        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1, display: "flex", flexDirection: "column", gap: 18 }}>
          {PIPELINE_EXPORT_GROUPS.map(group => {
            const allOn = group.fields.every(f => selected.has(f.key));
            return (
              <div key={group.label}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontFamily: "var(--mono)", fontSize: ".65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", color: "var(--accent)" }}>{group.label}</div>
                  <button onClick={() => toggleGroup(group.fields, allOn)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "var(--mono)", fontSize: ".65rem", color: "var(--muted)", padding: "1px 6px", borderRadius: 4, textDecoration: "underline" }}>
                    {allOn ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "6px 12px" }}>
                  {group.fields.map(f => (
                    <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", padding: "4px 6px", borderRadius: 4, background: selected.has(f.key) ? "rgba(0,229,160,0.06)" : "transparent", border: `1px solid ${selected.has(f.key) ? "rgba(0,229,160,0.25)" : "transparent"}` }}>
                      <input type="checkbox" checked={selected.has(f.key)} onChange={() => toggle(f.key)}
                        style={{ accentColor: "var(--accent)", width: 13, height: 13, flexShrink: 0 }} />
                      <span style={{ fontFamily: "var(--mono)", fontSize: ".73rem", color: selected.has(f.key) ? "var(--text)" : "var(--muted)" }}>{f.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid var(--border)", gap: 10 }}>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".72rem", color: "var(--muted)" }}>
            {selected.size} field{selected.size !== 1 ? "s" : ""} selected
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={onClose} style={{ background: "none" }}>Cancel</button>
            <button className="btn" onClick={() => onExport([...selected])} disabled={selected.size === 0 || exporting}
              style={{ background: "var(--accent)", color: "#000", fontWeight: 700, opacity: selected.size === 0 || exporting ? 0.5 : 1 }}>
              {exporting ? "Exporting…" : "Download CSV"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// All fields for the expanded detail panel
const PIPELINE_DETAIL_FIELDS = [
  ["Rule ID",               "rule_id"],
  ["Action",                "action"],
  ["Priority",              "priority"],
  ["Match Condition",       "match_condition"],
  ["Full Target Path",      "full_target_path"],
  ["Enabled",               "enabled"],
  ["Is Active",             "is_active"],
  ["Is Deleted",            "is_deleted"],
  ["Config Version",        "config_version"],
  ["Created At",            "created_at"],
  ["Updated At",            "updated_at"],
  ["Created By",            "created_by"],
  ["Created Date",          "created_date"],
  ["Modified By",           "modified_by"],
  ["Modified Date",         "modified_date"],
  ["Deleted By",            "deleted_by"],
  ["Deleted Date",          "deleted_date"],
];


const STATUS_COLORS = {
  completed:  { bg: "rgba(5,150,105,0.09)",  text: "#059669", border: "rgba(5,150,105,0.3)"  },
  delivered:  { bg: "rgba(16,185,129,0.10)", text: "var(--status-delivered-text,#059669)", border: "rgba(16,185,129,0.35)" },
  failed:     { bg: "rgba(220,38,38,0.08)",  text: "#dc2626", border: "rgba(220,38,38,0.3)"  },
  error:      { bg: "rgba(220,38,38,0.08)",  text: "#dc2626", border: "rgba(220,38,38,0.3)"  },
  processing: { bg: "rgba(59,130,246,0.09)", text: "#2563eb", border: "rgba(59,130,246,0.3)" },
  pending:    { bg: "rgba(245,158,11,0.09)", text: "#d97706", border: "rgba(245,158,11,0.3)" },
  default:    { bg: "var(--surface2)",       text: "var(--muted)", border: "var(--border)"    },
};
function statusColor(s) {
  const key = (s || "").toLowerCase();
  return STATUS_COLORS[key] || STATUS_COLORS.default;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(value) {
  if (value === null || value === undefined) return <span style={{ color: "var(--muted)" }}>—</span>;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleString(undefined, { dateStyle: "short", timeStyle: "medium" });
  }
  return String(value);
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).catch(() => {});
}

// ─── Tiny SVG icons ───────────────────────────────────────────────────────────
const Ico = {
  search:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  refresh:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  copy:     <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  sortDesc: <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 19l7-7H5l7 7z"/></svg>,
  sortAsc:  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5l7 7H5l7-7z"/></svg>,
  sortNone: <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" opacity="0.25"><path d="M12 5l7 7H5l7-7zM12 19l7-7H5l7 7z"/></svg>,
  filter:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  x:        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SortIcon({ col, sort, order }) {
  if (sort !== col) return Ico.sortNone;
  return order === "DESC" ? Ico.sortDesc : Ico.sortAsc;
}

function SkeletonRows({ cols, n = 8 }) {
  return Array.from({ length: n }).map((_, i) => (
    <tr key={i}>
      {cols.map(c => (
        <td key={c.key} style={{ padding: "12px 14px" }}>
          <div style={{
            height: 12, borderRadius: 4,
            width: `${45 + Math.random() * 45}%`,
            background: "linear-gradient(90deg,var(--surface3) 25%,var(--surface2) 50%,var(--surface3) 75%)",
            backgroundSize: "400% 100%",
            animation: `shimmer 1.4s ease ${i * 0.06}s infinite`,
          }}/>
        </td>
      ))}
    </tr>
  ));
}

function Pagination({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i);
  return (
    <div className="pagination">
      <button className="pg-btn" onClick={() => setPage(1)}           disabled={page === 1}>«</button>
      <button className="pg-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</button>
      {pages.map(p => (
        <button key={p} className={`pg-btn ${p === page ? "active" : ""}`} onClick={() => setPage(p)}>{p}</button>
      ))}
      <button className="pg-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</button>
      <button className="pg-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
      <span className="pg-info">Page {page} / {totalPages}</span>
    </div>
  );
}


// ─── SavedFiltersMenu ─────────────────────────────────────────────────────────
// Reusable bookmark button + dropdown for saving and loading named filter presets.
// Props:
//   tab            — string key for this tab's presets ("files", "pipelines", etc.)
//   currentFilters — plain object of filter values to save
//   onApply        — fn(filters) called when user loads a preset
function SavedFiltersMenu({ tab, currentFilters, onApply }) {
  const [open, setOpen]           = useState(false);
  const [prefs, setPrefs]         = useState(null);
  const [saving, setSaving]       = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [saveName, setSaveName]   = useState("");
  const [editIdx, setEditIdx]     = useState(null);  // index of preset being renamed
  const [editName, setEditName]   = useState("");
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false); setShowInput(false); setEditIdx(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Load prefs when dropdown opens
  useEffect(() => {
    if (!open) return;
    if (_prefsCache !== null) { setPrefs(_prefsCache); return; }
    _fetchPrefs().then(setPrefs).catch(() => setPrefs({}));
  }, [open]);

  const tabPresets = (prefs && prefs[tab]) || [];

  const persist = (updated) => {
    setPrefs({ ...updated });
    return _savePrefs(updated);
  };

  const handleApply = (preset) => {
    onApply(preset.filters);
    setOpen(false); setShowInput(false); setEditIdx(null);
  };

  const handleDelete = (e, idx) => {
    e.stopPropagation();
    persist({ ...prefs, [tab]: tabPresets.filter((_, i) => i !== idx) });
    if (editIdx === idx) setEditIdx(null);
  };

  const handleStartEdit = (e, idx) => {
    e.stopPropagation();
    setEditIdx(idx);
    setEditName(tabPresets[idx].name);
    setShowInput(false);
  };

  const handleRename = (idx) => {
    const name = editName.trim();
    if (!name || name === tabPresets[idx].name) { setEditIdx(null); return; }
    const updated = tabPresets.map((p, i) => i === idx ? { ...p, name } : p);
    persist({ ...prefs, [tab]: updated });
    setEditIdx(null);
  };

  const handleSave = () => {
    const name = saveName.trim();
    if (!name) return;
    const deduped = tabPresets.filter(p => p.name !== name);
    const updated = { ...prefs, [tab]: [...deduped, { name, filters: currentFilters }] };
    setSaving(true);
    persist(updated).then(() => {
      trackEvent("FilterPresetSaved", { tab, presetName: name });
      setSaveName(""); setShowInput(false); setSaving(false);
    }).catch(() => setSaving(false));
  };

  const iconBtn = (title, onClick, children) => (
    <button
      onClick={onClick}
      title={title}
      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: "2px 5px", borderRadius: 3, lineHeight: 1, display: "flex", alignItems: "center" }}
      onMouseEnter={e => e.currentTarget.style.color = title === "Delete" ? "var(--danger)" : "var(--text)"}
      onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}
    >
      {children}
    </button>
  );

  const pencilSvg = <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
  const checkSvg = <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
  const bookmarkSvg = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>;

  return (
    <div style={{ position: "relative" }} ref={menuRef}>
      <button
        className="btn"
        onClick={() => { setOpen(o => !o); setShowInput(false); setEditIdx(null); }}
        title="Saved filters"
        style={open ? { background: "var(--dim)", borderColor: "var(--accent)", color: "var(--accent)" } : {}}
      >
        {bookmarkSvg} Saved
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 300,
          background: "var(--surface)", border: "1px solid var(--border2)",
          borderRadius: 8, minWidth: 260, boxShadow: "0 8px 28px rgba(0,0,0,.22)",
          padding: "8px 0",
        }}>
          {/* Preset list */}
          {prefs === null ? (
            <div style={{ padding: "10px 16px", color: "var(--muted)", fontSize: ".8rem" }}>Loading…</div>
          ) : tabPresets.length === 0 ? (
            <div style={{ padding: "8px 16px", color: "var(--muted)", fontSize: ".8rem" }}>No saved filters yet</div>
          ) : (
            tabPresets.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", padding: "4px 10px", gap: 3 }}>
                {editIdx === i ? (
                  // ── Rename mode ───────────────────────────────────────────
                  <>
                    <input
                      className="inp"
                      style={{ flex: 1, fontSize: ".8rem", padding: "3px 7px", height: 28 }}
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") handleRename(i); if (e.key === "Escape") setEditIdx(null); }}
                      autoFocus
                      onClick={e => e.stopPropagation()}
                    />
                    {iconBtn("Confirm rename", e => { e.stopPropagation(); handleRename(i); }, checkSvg)}
                    {iconBtn("Cancel", e => { e.stopPropagation(); setEditIdx(null); }, <span style={{ fontSize: ".75rem" }}>✕</span>)}
                  </>
                ) : (
                  // ── Normal mode ───────────────────────────────────────────
                  <>
                    <button
                      onClick={() => handleApply(p)}
                      style={{
                        flex: 1, textAlign: "left", background: "none", border: "none",
                        color: "var(--text)", fontSize: ".83rem", cursor: "pointer",
                        padding: "4px 6px", borderRadius: 4, fontFamily: "inherit",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--surface2)"}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}
                    >
                      {p.name}
                    </button>
                    {iconBtn("Rename", e => handleStartEdit(e, i), pencilSvg)}
                    {iconBtn("Delete", e => handleDelete(e, i), <span style={{ fontSize: ".75rem" }}>✕</span>)}
                  </>
                )}
              </div>
            ))
          )}

          {/* Save section */}
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 6, padding: "8px 10px 0" }}>
            {showInput ? (
              <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                <input
                  className="inp"
                  style={{ flex: 1, fontSize: ".8rem", padding: "4px 8px", height: 30 }}
                  placeholder="Filter name…"
                  value={saveName}
                  onChange={e => setSaveName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") { setShowInput(false); setSaveName(""); } }}
                  autoFocus
                />
                <button
                  className="btn"
                  onClick={handleSave}
                  disabled={saving || !saveName.trim()}
                  style={{ fontSize: ".75rem", padding: "4px 10px", height: 30 }}
                >
                  {saving ? "…" : "Save"}
                </button>
              </div>
            ) : (
              <button
                className="btn"
                onClick={() => { setShowInput(true); setEditIdx(null); }}
                style={{ width: "100%", fontSize: ".8rem", justifyContent: "center", gap: 5 }}
              >
                + Save current filters
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ReprocessConfirmDialog ───────────────────────────────────────────────────
function ReprocessConfirmDialog({ files, onConfirm, onCancel }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err,  setErr]  = useState(null);

  const handle = () => {
    setBusy(true);
    apiFetch(`${API}/api/files/reprocess`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files: files.map(f => ({ file_index_id: f.file_index_id, tracking_correlation_id: f.tracking_correlation_id, file_name: f.file_name || f.current_file_name || "" })) }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setBusy(false); setDone(true);
      })
      .catch(e => { setBusy(false); setErr(e.message); });
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center",
    }}
      onClick={e => { if (!busy && !done && e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border2)", borderRadius: 10,
        padding: "28px 28px 22px", minWidth: 380, maxWidth: 520, boxShadow: "0 16px 48px rgba(0,0,0,.35)",
      }}>
        {done ? (
          <>
            <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--success)", marginBottom: 10 }}>
              Reprocess queued
            </div>
            <div style={{ fontSize: ".85rem", color: "var(--text2)", marginBottom: 20 }}>
              {files.length} file{files.length > 1 ? "s have" : " has"} been reset for reprocessing.
            </div>
            <button className="btn" onClick={onConfirm} style={{ float: "right" }}>Close</button>
            <div style={{ clear: "both" }} />
          </>
        ) : (
          <>
            <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>
              Reprocess {files.length} file{files.length > 1 ? "s" : ""}?
            </div>
            <div style={{ fontSize: ".82rem", color: "var(--text2)", marginBottom: 14 }}>
              This will update file index table entries and set the <strong>status_id</strong> to <strong>"15"</strong> and <strong>retry_attempts</strong> to <strong>"0"</strong>.
            </div>
            <div style={{
              maxHeight: 180, overflowY: "auto", marginBottom: 18,
              border: "1px solid var(--border)", borderRadius: 6,
              background: "var(--bg)",
            }}>
              {files.map((f, i) => (
                <div key={i} style={{
                  padding: "7px 12px", fontSize: ".78rem", fontFamily: "var(--mono)",
                  borderBottom: i < files.length - 1 ? "1px solid var(--border)" : "none",
                  color: "var(--accent2)", wordBreak: "break-all",
                }}>
                  {f.file_name || f.current_file_name || "—"}
                </div>
              ))}
            </div>
            {err && <div style={{ color: "var(--danger)", fontSize: ".8rem", marginBottom: 12 }}>{err}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
              <button
                className="btn"
                onClick={handle}
                disabled={busy}
                style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#fff", fontWeight: 600 }}
              >
                {busy ? "Reprocessing…" : "Confirm Reprocess"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Files Tab ────────────────────────────────────────────────────────────────
function FilesTab({ hours, from, to, setHours, refreshTick }) {
  const access = useAccess();
  const isAdmin = access.role === "admin";
  const visibleCols = FILE_COLS.filter(c =>
    (c.key !== "current_size" && c.key !== "tracking_correlation_id") || isAdmin
  );

  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [statuses, setStatuses]     = useState([]);

  const [search, setSearch]         = useState("");
  const [statusFilter, setStatus]   = useState("");
  const [inboundFilter, setInbound] = useState("");
  const [outboundFilter, setOutbound]= useState("");
  const [sort, setSort]             = useState("last_updated");
  const [order, setOrder]           = useState("DESC");
  const [page, setPage]             = useState(1);
  const [expandedRow, setExpanded]  = useState(null);
  const [colWidths, setColWidths]    = useState({});

  const [selected, setSelected]       = useState(new Set());   // Set of file_index_id
  const [reprocessFiles, setReprocess] = useState(null);       // array of rows to confirm

  const dSearch   = useDebounce(search,   400);
  const dInbound  = useDebounce(inboundFilter, 500);
  const dOutbound = useDebounce(outboundFilter, 500);
  const abortRef  = useRef(null);

  // Fetch status list once
  useEffect(() => {
    apiFetch(`${API}/api/files/statuses`)
      .then(r => r.json())
      .then(setStatuses)
      .catch(() => {});
  }, []);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [hours, from, to, dSearch, statusFilter, dInbound, dOutbound, sort, order]);

  const fetchData = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);

    const p = new URLSearchParams({
      ...rangeParams(hours, from, to), search: dSearch, status: statusFilter,
      inbound_partner: dInbound, outbound_partner: dOutbound,
      sort, order, page, pageSize: 50,
    });

    apiFetch(`${API}/api/files?${p}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => {
        setRows(res.data);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        setLoading(false);
      })
      .catch(err => {
        if (err.name !== "AbortError") { setError(err.message); setLoading(false); }
      });
  }, [hours, from, to, dSearch, statusFilter, dInbound, dOutbound, sort, order, page, refreshTick]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = col => {
    if (sort === col) setOrder(o => o === "DESC" ? "ASC" : "DESC");
    else { setSort(col); setOrder("DESC"); }
  };

  const activeFilters = [statusFilter, dInbound, dOutbound].filter(Boolean).length;

  return (
    <div className="tab-body">
      {/* Filter bar */}
      <div className="filter-bar">
        {/* Global search */}
        <div className="input-wrap">
          <span className="input-icon">{Ico.search}</span>
          <input
            className="inp"
            placeholder="Search file name, partner, correlation ID, status…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="clear-btn" onClick={() => setSearch("")}>{Ico.x}</button>
          )}
        </div>

        {/* Status */}
        <div className="select-wrap">
          <select className="sel" value={statusFilter} onChange={e => setStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Inbound partner */}
        <div className="input-wrap narrow">
          <span className="input-icon">{Ico.filter}</span>
          <input
            className="inp"
            placeholder="Inbound partner…"
            value={inboundFilter}
            onChange={e => setInbound(e.target.value)}
          />
          {inboundFilter && <button className="clear-btn" onClick={() => setInbound("")}>{Ico.x}</button>}
        </div>

        {/* Outbound partner */}
        <div className="input-wrap narrow">
          <span className="input-icon">{Ico.filter}</span>
          <input
            className="inp"
            placeholder="Outbound partner…"
            value={outboundFilter}
            onChange={e => setOutbound(e.target.value)}
          />
          {outboundFilter && <button className="clear-btn" onClick={() => setOutbound("")}>{Ico.x}</button>}
        </div>

        <SavedFiltersMenu
          tab="files"
          currentFilters={{ hours: String(hours), from: from || "", to: to || "", search, statusFilter, inboundFilter, outboundFilter }}
          onApply={f => {
            if (f.hours         !== undefined && setHours) setHours(f.hours, f.from, f.to);
            if (f.search        !== undefined) setSearch(f.search);
            if (f.statusFilter  !== undefined) setStatus(f.statusFilter);
            if (f.inboundFilter !== undefined) setInbound(f.inboundFilter);
            if (f.outboundFilter!== undefined) setOutbound(f.outboundFilter);
          }}
        />

        <button className={`btn ${loading ? "spin" : ""}`} onClick={fetchData}>
          {Ico.refresh} {loading ? "Loading…" : "Refresh"}
        </button>

        {isAdmin && selected.size > 0 && (
          <button
            className="btn"
            onClick={() => {
              const targets = rows.filter(r => selected.has(r.file_index_id));
              const _acct = _msalInstance && (_msalInstance.getActiveAccount() || _msalInstance.getAllAccounts()[0]);
              trackEvent("ReprocessInitiated", {
                upn:       _acct?.username || "unknown",
                fileCount: String(targets.length),
                fileNames: targets.map(f => f.file_name || "unknown").join(","),
              });
              setReprocess(targets);
            }}
            style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#fff", fontWeight: 600 }}
          >
            ↺ Reprocess ({selected.size})
          </button>
        )}

        <div className="result-count">
          {!loading && !error && <><strong>{total.toLocaleString()}</strong> row{total !== 1 ? "s" : ""}</>}
          {activeFilters > 0 && <span className="filter-badge">{activeFilters} filter{activeFilters > 1 ? "s" : ""}</span>}
        </div>
      </div>

      {reprocessFiles && (
        <ReprocessConfirmDialog
          files={reprocessFiles}
          onConfirm={() => { setReprocess(null); setSelected(new Set()); fetchData(); }}
          onCancel={() => setReprocess(null)}
        />
      )}

      {/* Table with resizable file name columns */}
      <div className="tbl-wrap">
        {error ? (
          <div className="empty-state error">{error}</div>
        ) : (
          <table style={{ tableLayout: "fixed" }}>
            <colgroup>
              {isAdmin && <col style={{ width: 36 }} />}
              {visibleCols.map(c => (
                <col key={c.key} style={{ width: colWidths[c.key] || FILE_COL_DEFAULTS[c.key] || 160 }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {isAdmin && (
                  <th style={{ width: 36, padding: "0 0 0 10px" }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      title="Select all on this page"
                      checked={rows.length > 0 && rows.every(r => selected.has(r.file_index_id))}
                      onChange={e => {
                        if (e.target.checked) setSelected(new Set(rows.map(r => r.file_index_id)));
                        else setSelected(new Set());
                      }}
                    />
                  </th>
                )}
                {visibleCols.map(c => (
                  <th
                    key={c.key}
                    className={sort === c.key ? "sorted" : ""}
                    onClick={() => c.sortable && handleSort(c.key)}
                    style={{ cursor: c.sortable ? "pointer" : "default", position: "relative", overflow: "visible" }}
                  >
                    <span className="th-inner">
                      {c.label}
                      {c.sortable && <SortIcon col={c.key} sort={sort} order={order} />}
                    </span>
                    {/* Drag handle for resizable columns */}
                    {c.resizable && (
                      <span
                        onMouseDown={e => {
                          e.stopPropagation();
                          const startX = e.clientX;
                          const startW = colWidths[c.key] || FILE_COL_DEFAULTS[c.key];
                          const onMove = mv => {
                            const next = Math.max(120, startW + mv.clientX - startX);
                            setColWidths(w => ({ ...w, [c.key]: next }));
                          };
                          const onUp = () => {
                            window.removeEventListener("mousemove", onMove);
                            window.removeEventListener("mouseup", onUp);
                          };
                          window.addEventListener("mousemove", onMove);
                          window.addEventListener("mouseup", onUp);
                        }}
                        style={{
                          position: "absolute", right: 0, top: 0, bottom: 0,
                          width: 6, cursor: "col-resize",
                          background: "transparent",
                          borderRight: "2px solid var(--border2)",
                          zIndex: 2,
                        }}
                        title="Drag to resize"
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <SkeletonRows cols={isAdmin ? [{ key: "_chk" }, ...visibleCols] : visibleCols} />
                : rows.length === 0
                ? <tr><td colSpan={visibleCols.length + (isAdmin ? 1 : 0)}><div className="empty-state">No results found for the selected filters.</div></td></tr>
                : rows.map((row, i) => {
                    const isFailed  = /fail|error/i.test(row.status || "");
                    const isChecked = selected.has(row.file_index_id);
                    return (
                    <React.Fragment key={row.tracking_correlation_id + i}>
                      <tr
                        className={`${expandedRow === i ? "row-expanded" : ""} ${isFailed ? "row-failed" : ""} ${isChecked ? "row-selected" : ""}`}
                        onClick={() => setExpanded(expandedRow === i ? null : i)}
                        style={{ animationDelay: `${Math.min(i, 20) * 0.02}s`, cursor: "pointer" }}
                      >
                        {isAdmin && (
                          <td style={{ width: 36, padding: "0 0 0 10px" }} onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={e => {
                                const next = new Set(selected);
                                e.target.checked ? next.add(row.file_index_id) : next.delete(row.file_index_id);
                                setSelected(next);
                              }}
                            />
                          </td>
                        )}
                        {visibleCols.map(c => (
                          <td key={c.key} style={{
                            overflow: "hidden",
                            ...(c.key === "file_name" || c.key === "current_file_name"
                              ? { whiteSpace: "normal", wordBreak: "break-all", lineHeight: 1.4, paddingTop: 8, paddingBottom: 8 }
                              : c.key === "tracking_correlation_id"
                              ? { whiteSpace: "normal", wordBreak: "break-all", lineHeight: 1.4, paddingTop: 8, paddingBottom: 8 }
                              : { whiteSpace: "nowrap", textOverflow: "ellipsis" })
                          }}>
                            {c.key === "status" ? (
                              <StatusBadge value={row[c.key]} />
                            ) : c.key === "tracking_correlation_id" ? (
                              <CopyCell value={row[c.key]} />
                            ) : c.key === "file_name" ? (
                              <CopyCell value={row[c.key]} />
                            ) : c.key === "current_file_name" ? (
                              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <CopyCell value={row.current_file_name_display} />
                                <DownloadBtn fileIndexId={row.file_index_id} blobPath={row.current_blob_path} />
                              </span>
                            ) : c.key === "inbound_partner_name" || c.key === "outbound_partner_name" ? (
                              <CopyCell value={row[c.key]} />
                            ) : c.key === "current_size" ? (
                              row[c.key] == null ? "—" :
                              row[c.key] >= 1073741824 ? `${(row[c.key] / 1073741824).toFixed(2)} GB` :
                              row[c.key] >= 1048576    ? `${(row[c.key] / 1048576).toFixed(2)} MB` :
                              row[c.key] >= 1024       ? `${(row[c.key] / 1024).toFixed(2)} KB` :
                              `${row[c.key]} B`
                            ) : (
                              fmt(row[c.key])
                            )}
                          </td>
                        ))}
                      </tr>
                      {expandedRow === i && (
                        <tr key={`exp-${i}`} className="expanded-row">
                          <td colSpan={visibleCols.length + (isAdmin ? 1 : 0)}>
                            <ExpandedDetail row={row} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })
              }
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

function StatusBadge({ value }) {
  const c = statusColor(value);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 9px", borderRadius: 99,
      fontSize: ".72rem", fontWeight: 600,
      letterSpacing: ".01em",
      background: c.bg, color: c.text,
      border: `1px solid ${c.border}`,
    }}>
      {value || "—"}
    </span>
  );
}

function CopyCell({ value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    copyToClipboard(value || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "var(--mono)", fontSize: ".74rem" }}>
      <span style={{ wordBreak: "break-all", whiteSpace: "normal", lineHeight: 1.4 }} title={value}>
        {value || "—"}
      </span>
      {value && (
        <button onClick={handleCopy} className="icon-btn" title="Copy" style={{ flexShrink: 0 }}>
          {copied ? <span style={{ color: "var(--accent)", fontSize: 9 }}>✓</span> : Ico.copy}
        </button>
      )}
    </span>
  );
}

// Copy icon button only (no value text — used next to labels)
function CopyIconBtn({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button className="icon-btn" title="Copy" onClick={e => { e.stopPropagation(); copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }} style={{ flexShrink: 0 }}>
      {copied ? <span style={{ color: "var(--accent)", fontSize: 9 }}>✓</span> : Ico.copy}
    </button>
  );
}

function DownloadBtn({ fileIndexId, blobPath }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError  ] = useState(null);

  const access = useAccess();
  if (!blobPath || !access.canDownload) return null; // hide if no blob stored or download not permitted

  const handleDownload = async (e) => {
    e.stopPropagation();
    setLoading(true);
    setError(null);
    trackEvent("FileDownload", { fileIndexId: String(fileIndexId) });
    try {
      const r = await apiFetch(`${API}/api/files/${fileIndexId}/download`);
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${r.status}`);
      }
      const { url } = await r.json();
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("[DownloadBtn]", err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      className="icon-btn"
      title={error ? `Error: ${error}` : "Download file"}
      disabled={loading}
      style={{ flexShrink: 0, color: error ? "#dc2626" : loading ? "var(--muted)" : "var(--accent)" }}
    >
      {loading ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ animation: "spin 1s linear infinite" }}>
          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
        </svg>
      ) : error ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      )}
    </button>
  );
}

function FileCopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    copyToClipboard(value || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  if (!value) return null;
  return (
    <button onClick={handleCopy} className="icon-btn" title="Copy" style={{ flexShrink: 0 }}>
      {copied ? <span style={{ color: "var(--accent)", fontSize: 9 }}>OK</span> : Ico.copy}
    </button>
  );
}

function ExpandedDetail({ row }) {
  // ── Reusable long-text block ────────────────────────────────────────────────
  const LongField = ({ label, value }) => {
    var _s = safeStr(value);
    if (!_s || _s === "null" || _s === "undefined") return null;
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{
          fontFamily: "var(--mono)", fontSize: ".6rem", fontWeight: 700,
          color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".09em", marginBottom: 4,
        }}>{label}</div>
        <div style={{
          fontFamily: "var(--mono)", fontSize: ".74rem", color: "var(--text)",
          background: "var(--bg)", border: "1px solid var(--border)",
          borderRadius: 4, padding: "8px 11px",
          whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.55,
          maxHeight: 130, overflowY: "auto",
        }}>{_s}</div>
      </div>
    );
  };

  // ── Compact key-value pair ──────────────────────────────────────────────────
  const Field = ({ label, value, mono = true, accent = false, copyable = false }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = (e) => {
      e.stopPropagation();
      copyToClipboard(String(value ?? ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{
          fontFamily: "var(--mono)", fontSize: ".59rem", fontWeight: 700,
          color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".09em",
        }}>{label}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{
            fontFamily: mono ? "var(--mono)" : "var(--font)",
            fontSize: ".74rem",
            color: accent ? "var(--accent)" : "var(--text)",
            wordBreak: "break-all",
            whiteSpace: "normal",
            lineHeight: 1.45,
            flex: 1,
          }} title={String(value ?? "")}>
            {value != null && value !== "" ? String(value) : <span style={{ color: "var(--muted)" }}>—</span>}
          </div>
          {copyable && value != null && value !== "" && (
            <button onClick={handleCopy} className="icon-btn" title="Copy" style={{ flexShrink: 0 }}>
              {copied ? <span style={{ color: "var(--accent)", fontSize: 9 }}>OK</span> : Ico.copy}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── Protocol pill ────────────────────────────────────────────────────────────
  const ProtoPill = ({ value }) => value ? (
    <span style={{
      display: "inline-block",
      background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.35)",
      borderRadius: 99, padding: "2px 9px",
      fontFamily: "var(--mono)", fontSize: ".68rem", fontWeight: 600,
      color: "#2563eb", letterSpacing: ".04em", textTransform: "uppercase",
    }}>{value}</span>
  ) : <span style={{ color: "var(--muted)", fontFamily: "var(--mono)", fontSize: ".74rem" }}>—</span>;

  // ── Flow+Connection card (used for both inbound and outbound) ─────────────
  const FlowCard = ({ side, flowId, flowName, partnerName, absPath, extConnDetails, fileProcessingRules, connName, connId, protocol, connDetails, retryAttempts, maxRetryAttempts, frequency, lastRunTime, flowType, isActive }) => {
    var _protoLabel = (function(){
      var _pr = String(protocol||"").toLowerCase();
      if (_pr === "thorn-sftp") return "SFTP Server";
      if (_pr === "sftp")       return "SFTP Client";
      if (_pr === "smb")        return "SMB Client";
      if (_pr === "blob")       return "Azure Client";
      if (_pr === "s3")         return "AWS Client";
      return protocol;
    })();
    var _activeLabel = isActive === true || isActive === "true" ? "Enabled" : "Disabled";
    var _activeColor = isActive === true || isActive === "true"
      ? { bg: "rgba(5,150,105,0.10)", border: "rgba(5,150,105,0.35)", text: "#059669" }
      : { bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.30)", text: "#dc2626" };
    return (
    <div style={{
      flex: 1, minWidth: 280,
      background: "var(--surface2)",
      border: "1px solid var(--border)",
      borderTop: `2px solid var(--border2)`,
      borderTopColor: "var(--border2)",
      borderRadius: 4,
      padding: "14px 16px",
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      {/* Card header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{
          fontFamily: "var(--font)", fontSize: ".68rem", fontWeight: 900,
          textTransform: "uppercase", letterSpacing: ".1em",
          color: "var(--muted)", opacity: 1,
        }}>{side} Flow</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ProtoPill value={_protoLabel} />
          <span style={{
            display: "inline-block",
            background: _activeColor.bg, border: "1px solid " + _activeColor.border,
            borderRadius: 99, padding: "2px 9px",
            fontFamily: "var(--mono)", fontSize: ".68rem", fontWeight: 600,
            color: _activeColor.text, letterSpacing: ".04em",
          }}>{_activeLabel}</span>
        </div>
      </div>

      {/* Name */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>Name</div>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".88rem", fontWeight: 700, color: "var(--text)", lineHeight: 1.3, wordBreak: "break-word", flex: 1 }}>
          {flowName || <span style={{ color: "var(--muted)" }}>—</span>}
        </div>
        {flowName && <FileCopyBtn value={flowName} />}
      </div>

      {/* Flow ID */}
      {flowId != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>Flow ID</div>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".72rem", color: "var(--text)", wordBreak: "break-all", flex: 1 }}>{flowId}</div>
          <FileCopyBtn value={String(flowId)} />
        </div>
      )}

      {/* Partner name */}
      {partnerName && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>Partner</div>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".78rem", fontWeight: 700, color: "var(--text)", flex: 1 }}>{partnerName}</div>
          <FileCopyBtn value={partnerName} />
        </div>
      )}

      {/* Connection name */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        background: "var(--bg)", borderRadius: 3,
        border: "1px solid var(--border)", padding: "7px 10px",
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>
        <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em" }}>Connection</div>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {connName || <span style={{ color: "var(--muted)" }}>No connection linked</span>}
          </div>
          {connId != null && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>ID</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--muted)", flex: 1 }}>{connId}</div>
              <FileCopyBtn value={String(connId)} />
            </div>
          )}
        </div>
      </div>

      {/* Long-text fields */}
      <LongField label="Connection Details"           value={connDetails} />
      <LongField label="Extended Connection Details"  value={extConnDetails} />
      {(function() {
          var _v = fileProcessingRules;
          if (_v === null || _v === undefined) return null;
          if (typeof _v === 'string' && (_v.trim() === '' || _v.trim() === 'null' || _v.trim() === 'undefined')) return null;
          if (typeof _v === 'object' && !Array.isArray(_v) && Object.keys(_v).length === 0) return null;
          if (Array.isArray(_v) && _v.length === 0) return null;
          return <LongField label="File Processing Rules" value={_v} />;
        })()}
      {(function(){
        var _v = retryAttempts;
        if (_v === null || _v === undefined) return null;
        var _s = String(_v).trim();
        if (_s === "" || _s === "null" || _s === "undefined") return null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>Retry Attempts</div>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)", flex: 1 }}>{_v}</div>
          </div>
        );
      })()}
      {(function(){
        var _v = maxRetryAttempts;
        if (_v === null || _v === undefined) return null;
        var _s = String(_v).trim();
        if (_s === "" || _s === "null" || _s === "undefined") return null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>Max Retry Attempts</div>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)", flex: 1 }}>{_v}</div>
          </div>
        );
      })()}
      {(function(){
        var _v = frequency;
        if (_v === null || _v === undefined) return null;
        var _s = String(_v).trim();
        if (_s === "" || _s === "null" || _s === "undefined") return null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>Frequency</div>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)", flex: 1 }}>{_v}</div>
          </div>
        );
      })()}
      {(function(){
        var _v = lastRunTime;
        if (_v === null || _v === undefined) return null;
        var _s = String(_v).trim();
        if (_s === "" || _s === "null" || _s === "undefined") return null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>Last Run Time</div>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)", flex: 1 }}>{new Date(_v).toLocaleString()}</div>
          </div>
        );
      })()}
      <LongField label="Absolute Flow Path"           value={absPath} />
    </div>
    );
  }

  return (
    <div className="detail-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── File names — full width ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {[["File Name", row.file_name], ["Current File Name", row.current_file_name_display]].map(([label, val]) => (
          val ? (
            <div key={label}>
              <div style={{
                fontFamily: "var(--mono)", fontSize: ".6rem", fontWeight: 700,
                color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".09em", marginBottom: 5,
              }}>{label}</div>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "var(--surface3)", borderRadius: 4, padding: "8px 12px",
                border: "1px solid var(--border)",
              }}>
                <div style={{
                  fontFamily: "var(--mono)", fontSize: ".78rem", color: "var(--accent2)",
                  wordBreak: "break-all", lineHeight: 1.5, flex: 1,
                }}>{typeof val === "object" && val !== null ? JSON.stringify(val, null, 2) : String(val ?? "")}</div>
                <FileCopyBtn value={typeof val === "object" && val !== null ? JSON.stringify(val) : String(val ?? "")} />
              </div>
            </div>
          ) : null
        ))}
      </div>

      {/* ── Quick metadata row ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: "8px 20px",
        background: "var(--surface2)",
        borderRadius: 4, padding: "10px 14px",
        border: "1px solid var(--border)",
      }}>
        <Field label="Status"         value={row.status}                    mono={false} accent />
        <Field label="Correlation ID" value={row.tracking_correlation_id} copyable />
        <Field label="File Index ID"  value={row.file_index_id}            copyable />

        <Field label="Routing Rule"   value={row.routing_rule_id}          copyable />
        <Field label="Created At"     value={row.created_at    ? new Date(row.created_at).toLocaleString()   : null} />
        <Field label="Last Updated"   value={row.last_updated  ? new Date(row.last_updated).toLocaleString() : null} />
      </div>

      {/* ── Inbound / Outbound flow+connection cards ── */}
      {(() => { console.log("ROW KEYS:", Object.keys(row)); console.log("FLOW IDs:", { flow_id: row.flow_id, inbound_flow_id: row.inbound_flow_id, outbound_flow_id: row.outbound_flow_id }); return null; })()}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <FlowCard
          side="Inbound"
          flowId              ={row.inbound_flow_id ?? row.flow_id}
          flowName            ={row.inbound_flow_name}
          partnerName         ={row.inbound_partner_name}
          absPath             ={row.inbound_absolute_flow_path}
          extConnDetails      ={row.inbound_extended_connection_details}
          fileProcessingRules ={row.inbound_file_processing_rules}
          connName            ={row.inbound_connection_name}
          connId              ={row.inbound_connection_id}
          protocol            ={row.inbound_protocol}
          connDetails         ={row.inbound_connection_details}
          retryAttempts       ={row.inbound_retry_attempts}
          maxRetryAttempts    ={row.inbound_max_retry_attempts}
          frequency           ={row.inbound_frequency}
          lastRunTime         ={row.inbound_last_run_time}
          flowType            ={row.inbound_flow_type}
          isActive            ={row.inbound_is_active}
        />
        <FlowCard
          side="Outbound"
          flowId              ={row.outbound_flow_id}
          flowName            ={row.outbound_flow_name}
          partnerName         ={row.outbound_partner_name}
          absPath             ={row.outbound_absolute_flow_path}
          extConnDetails      ={row.outbound_extended_connection_details}
          fileProcessingRules ={row.outbound_file_processing_rules}
          connName            ={row.outbound_connection_name}
          connId              ={row.outbound_connection_id}
          protocol            ={row.outbound_protocol}
          connDetails         ={row.outbound_connection_details}
          retryAttempts       ={row.outbound_retry_attempts}
          maxRetryAttempts    ={row.outbound_max_retry_attempts}
          frequency           ={row.outbound_frequency}
          lastRunTime         ={row.outbound_last_run_time}
          flowType            ={row.outbound_flow_type}
          isActive            ={row.outbound_is_active}
        />
      </div>
    </div>
  );
}


// ─── Modal ────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxWidth }) {
  useEffect(() => {
    const handler = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" style={maxWidth ? { maxWidth } : undefined}>
        <div className="modal-hdr">
          <div className="modal-title">{title}</div>
          <button onClick={onClose} className="icon-btn" style={{ fontSize: 20, padding: "2px 6px" }}>×</button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── ContactsModal — shown when clicking "View Contacts" on a partner row ─────
function ContactsModal({ partner, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    apiFetch(`${API}/api/partners/${partner.partner_id}/contacts`)
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setContacts(res.data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [partner.partner_id]);

  return (
    <Modal title={`Contacts — ${partner.partner_name}`} onClose={onClose} maxWidth="1100px">
      {loading && <div className="empty-state">Loading contacts…</div>}
      {error   && <div className="empty-state error">{error}</div>}
      {!loading && !error && contacts.length === 0 && (
        <div className="empty-state">No contacts linked to this partner.</div>
      )}
      {!loading && !error && contacts.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem" }}>
          <thead>
            <tr>
              {["Name","Email","Phone","Coke","Technical","Active","Linked Since"].map(h => (
                <th key={h} style={{
                  padding: "8px 12px", textAlign: "left",
                  fontFamily: "var(--mono)", fontSize: ".67rem",
                  textTransform: "uppercase", letterSpacing: ".07em",
                  color: "var(--muted)",
                  borderBottom: "1px solid var(--border)",
                  whiteSpace: "nowrap",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contacts.map(c => (
              <tr key={c.contact_id} style={{ borderBottom: "1px solid var(--border)" }}>
                <td style={{ padding: "9px 12px", fontWeight: 600, whiteSpace: "normal", wordBreak: "break-word", minWidth: 160 }}>
                  <CopyCell value={maskName(c.name)} />
                </td>
                <td style={{ padding: "9px 12px", fontFamily: "var(--mono)", fontSize: ".75rem", whiteSpace: "nowrap" }}>
                  <CopyCell value={maskEmail(c.email)} />
                </td>
                <td style={{ padding: "9px 12px", fontFamily: "var(--mono)", fontSize: ".75rem" }}>{maskPhone(c.phone_number)}</td>
                <td style={{ padding: "9px 12px" }}><BoolBadge value={c.is_coke} /></td>
                <td style={{ padding: "9px 12px" }}><BoolBadge value={c.is_technical} /></td>
                <td style={{ padding: "9px 12px" }}><BoolBadge value={c.is_active} /></td>
                <td style={{ padding: "9px 12px", fontFamily: "var(--mono)", fontSize: ".73rem", color: "var(--muted)" }}>
                  {c.link_created_date ? new Date(c.link_created_date).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// ─── PartnersModal — shown when clicking "View Partners" on a contact row ─────
function PartnersModal({ contact, onClose }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    apiFetch(`${API}/api/contacts/${contact.contact_id}/partners`)
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setPartners(res.data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [contact.contact_id]);

  return (
    <Modal title={`Partners — ${maskName(contact.name)}`} onClose={onClose}>
      {loading && <div className="empty-state">Loading partners…</div>}
      {error   && <div className="empty-state error">{error}</div>}
      {!loading && !error && partners.length === 0 && (
        <div className="empty-state">No partners linked to this contact.</div>
      )}
      {!loading && !error && partners.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem" }}>
          <thead>
            <tr>
              {["Partner Name","Description","Coke","Internet","Active","Linked Since"].map(h => (
                <th key={h} style={{
                  padding: "8px 12px", textAlign: "left",
                  fontFamily: "var(--mono)", fontSize: ".67rem",
                  textTransform: "uppercase", letterSpacing: ".07em",
                  color: "var(--muted)",
                  borderBottom: "1px solid var(--border)",
                  whiteSpace: "nowrap",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {partners.map(p => (
              <tr key={p.partner_id} style={{ borderBottom: "1px solid var(--border)" }}>
                <td style={{ padding: "9px 12px", fontWeight: 600 }}>{p.partner_name || "—"}</td>
                <td style={{ padding: "9px 12px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--muted)", fontSize: ".75rem" }} title={p.description}>{p.description || "—"}</td>
                <td style={{ padding: "9px 12px" }}><BoolBadge value={p.is_coke} /></td>
                <td style={{ padding: "9px 12px" }}><BoolBadge value={p.is_internet} /></td>
                <td style={{ padding: "9px 12px" }}><BoolBadge value={p.is_active} /></td>
                <td style={{ padding: "9px 12px", fontFamily: "var(--mono)", fontSize: ".73rem", color: "var(--muted)" }}>
                  {p.link_created_date ? new Date(p.link_created_date).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// ─── FlowsModal — shown from Partners tab ────────────────────────────────────
function FlowsModal({ partner, onClose }) {
  const [flows, setFlows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    apiFetch(`${API}/api/partners/${partner.partner_id}/flows`)
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setFlows(res.data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [partner.partner_id]);

  const LongField = ({ label, value }) => {
    var _s = safeStr(value);
    if (!_s || _s === "null" || _s === "undefined") return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: ".64rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em" }}>{label}</span>
          <CopyIconBtn value={_s} />
        </div>
        <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 12px", fontFamily: "var(--mono)", fontSize: ".74rem", color: "var(--text)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, maxHeight: 120, overflowY: "auto" }}>{_s}</div>
      </div>
    );
  };

  return (
    <Modal title={`Flows — ${partner.partner_name}`} onClose={onClose}>
      {loading && <div className="empty-state">Loading flows…</div>}
      {error   && <div className="empty-state error">{error}</div>}
      {!loading && !error && flows.length === 0 && <div className="empty-state">No flows linked to this partner.</div>}
      {!loading && !error && flows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {flows.map((f, i) => (
            <div key={f.flow_id} style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
              {/* Card header — always visible, click to expand */}
              <div
                onClick={() => setExpanded(expanded === i ? null : i)}
                style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
              >
                <span style={{ fontWeight: 700, fontSize: ".88rem", flex: 1 }}>{f.flow_name || "—"}</span>
                {f.flow_type && <span style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 99, padding: "2px 9px", fontFamily: "var(--mono)", fontSize: ".67rem", color: "var(--muted)" }}>{f.flow_type}</span>}
                {f.protocol  && <span style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 99, padding: "2px 9px", fontFamily: "var(--mono)", fontSize: ".67rem", color: "var(--accent2)" }}>{f.protocol}</span>}
                <BoolBadge value={f.is_active} />
                <span style={{ color: "var(--muted)", fontSize: ".75rem", marginLeft: "auto" }}>{expanded === i ? "▲" : "▼"}</span>
              </div>

              {/* Expanded detail */}
              {expanded === i && (
                <div style={{ padding: "0 16px 16px", borderTop: "1px solid var(--border)" }}>
                  <div style={{ paddingTop: 14 }}>
                    <LongField label="Description"              value={f.description} />
                    <LongField label="Connection Details"       value={f.connection_details} />
                    <LongField label="Extended Conn. Details"   value={f.extended_connection_details} />
                    <LongField label="File Processing Rules"    value={f.file_processing_rules} />
                    <LongField label="Absolute Flow Path"       value={f.absolute_flow_path} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "10px 20px", marginTop: 4 }}>
                    {[
                      ["Flow ID",             f.flow_id],
                      ["Flow Type",           f.flow_type],
                      ["Config Version",      f.config_version],
                      ["Default Routing Rule",f.default_routing_rule],
                      ["Source Partner ID",   f.source_partner_id],
                      ["Target Partner ID",   f.target_partner_id],
                      ["Connection ID",       f.connection_id],
                      ["SFTP Username",       f.sftp_username],
                      ["Folder Path",         f.folder_path],
                      ["Frequency",           f.frequency],
                      ["Last Run Time",       f.last_run_time ? new Date(f.last_run_time).toLocaleString() : null],
                      ["Uploaded Files",      f.uploaded_files],
                      ["Failed Files",        f.failed_files],
                      ["Retry Attempts",      f.retry_attempts],
                      ["Max Retry Attempts",  f.max_retry_attempts],
                      ["Retry Delay (s)",     f.retry_delay_seconds],
                      ["Delete Source File",  f.delete_source_file],
                      ["Calculate Checksum",  f.calculate_checksum],
                      ["Is Deleted",          f.is_deleted],
                      ["Created By",          f.created_by],
                      ["Created Date",        f.created_date  ? new Date(f.created_date).toLocaleString()  : null],
                      ["Modified By",         f.modified_by],
                      ["Modified Date",       f.modified_date ? new Date(f.modified_date).toLocaleString() : null],
                      ["Deleted By",          f.deleted_by],
                      ["Deleted Date",        f.deleted_date  ? new Date(f.deleted_date).toLocaleString()  : null],
                      ["Created At",          f.created_at    ? new Date(f.created_at).toLocaleString()    : null],
                      ["Updated At",          f.updated_at    ? new Date(f.updated_at).toLocaleString()    : null],
                    ].map(([label, val]) => {
                      const isId = ["Flow ID","Source Partner ID","Target Partner ID","Connection ID","Created By","Modified By","Deleted By"].includes(label);
                      return (
                        <div key={label}>
                          <div style={{ fontFamily: "var(--mono)", fontSize: ".63rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 3 }}>{label}</div>
                          <div style={{ fontFamily: "var(--mono)", fontSize: ".74rem", color: "var(--text)", wordBreak: "break-all" }}>
                            {typeof val === "boolean"
                              ? <BoolBadge value={val} />
                              : isId && val != null
                                ? <CopyCell value={String(val)} />
                                : val != null ? String(val) : <span style={{ color: "var(--muted)" }}>—</span>
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─── ConnectionsModal — shown from Partners tab ───────────────────────────────
function ConnectionsModal({ partner, onClose }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    apiFetch(`${API}/api/partners/${partner.partner_id}/connections`)
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setConnections(res.data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [partner.partner_id]);

  return (
    <Modal title={`Connections — ${partner.partner_name}`} onClose={onClose}>
      {loading && <div className="empty-state">Loading connections…</div>}
      {error   && <div className="empty-state error">{error}</div>}
      {!loading && !error && connections.length === 0 && (
        <div className="empty-state">No connections linked to this partner.</div>
      )}
      {!loading && !error && connections.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {connections.map((c, i) => (
            <div key={c.connection_id} style={{
              background: "var(--surface2)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "16px 20px",
            }}>
              {/* Card header */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 20, marginBottom: 14, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontFamily: "var(--mono)", fontSize: ".64rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 }}>Connection Name</div>
                  <span style={{ fontWeight: 700, fontSize: ".9rem" }}>{c.connection_name || "—"}</span>
                </div>
                <div>
                  <div style={{ fontFamily: "var(--mono)", fontSize: ".64rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 }}>Protocol</div>
                  {c.protocol ? (
                    <span style={{
                      background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.3)",
                      borderRadius: 99, padding: "2px 10px",
                      fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--accent2)",
                    }}>{c.protocol}</span>
                  ) : <span style={{ color: "var(--muted)" }}>—</span>}
                </div>
                <div>
                  <div style={{ fontFamily: "var(--mono)", fontSize: ".64rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 }}>Active</div>
                  <BoolBadge value={c.is_active} />
                </div>
                {c.is_deleted && (
                  <div>
                    <div style={{ fontFamily: "var(--mono)", fontSize: ".64rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 }}>Deleted</div>
                    <BoolBadge value={c.is_deleted} />
                  </div>
                )}
              </div>

              {/* Connection Details — full text, copyable */}
              {c.connection_details && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ fontFamily: "var(--mono)", fontSize: ".67rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em" }}>Connection Details</span>
                    <CopyIconBtn value={c.connection_details} />
                  </div>
                  <div style={{
                    background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6,
                    padding: "10px 14px", fontFamily: "var(--mono)", fontSize: ".76rem", color: "var(--text)",
                    whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, maxHeight: 160, overflowY: "auto",
                  }}>{c.connection_details}</div>
                </div>
              )}

              {/* All remaining fields in a 2-col grid */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "10px 20px",
              }}>
                {[
                  ["Connection ID", c.connection_id],
                  ["Is Deleted",    c.is_deleted],
                  ["Created By",    c.created_by],
                  ["Created Date",  c.created_date   ? new Date(c.created_date).toLocaleString()   : null],
                  ["Modified By",   c.modified_by],
                  ["Modified Date", c.modified_date  ? new Date(c.modified_date).toLocaleString()  : null],
                  ["Deleted By",    c.deleted_by],
                  ["Deleted Date",  c.deleted_date   ? new Date(c.deleted_date).toLocaleString()   : null],
                ].map(([label, val]) => {
                  const isId = ["Connection ID","Created By","Modified By","Deleted By"].includes(label);
                  return (
                    <div key={label}>
                      <div style={{ fontFamily: "var(--mono)", fontSize: ".64rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 3 }}>{label}</div>
                      <div style={{ fontFamily: "var(--mono)", fontSize: ".75rem", color: "var(--text)", wordBreak: "break-all" }}>
                        {typeof val === "boolean"
                          ? <BoolBadge value={val} />
                          : isId && val != null
                            ? <CopyCell value={String(val)} />
                            : val || <span style={{ color: "var(--muted)" }}>—</span>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─── PartnerInfoModal — shown from Connections tab ────────────────────────────
function PartnerInfoModal({ connection, onClose }) {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    apiFetch(`${API}/api/partners/${connection.partner_id}`)
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setPartner(res.data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [connection.partner_id]);

  const fields = partner ? [
    ["Partner ID",    partner.partner_id],
    ["Partner Name",  partner.partner_name],
    ["Description",   partner.description],
    ["Is Active",     partner.is_active],
    ["Is Coke",       partner.is_coke],
    ["Is Internet",   partner.is_internet],
    ["Is Deleted",    partner.is_deleted],
    ["Created By",    partner.created_by],
    ["Created Date",  partner.created_date ? new Date(partner.created_date).toLocaleString() : null],
    ["Modified By",   partner.modified_by],
    ["Modified Date", partner.modified_date ? new Date(partner.modified_date).toLocaleString() : null],
  ] : [];

  return (
    <Modal title={`Partner Info — ${connection.connection_name}`} onClose={onClose}>
      {loading && <div className="empty-state">Loading partner…</div>}
      {error   && <div className="empty-state error">{error}</div>}
      {!loading && !error && partner && (
        <div className="detail-grid">
          {fields.map(([label, val]) => (
            <div key={label} className="detail-field">
              <div className="detail-label">{label}</div>
              <div className="detail-value">
                {typeof val === "boolean"
                  ? <BoolBadge value={val} />
                  : val
                    ? String(val)
                    : <span style={{ color: "var(--muted)" }}>—</span>
                }
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─── Bool Badge ───────────────────────────────────────────────────────────────
function BoolBadge({ value }) {
  const isTrue = value === true || value === "true";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 9px", borderRadius: 99,
      fontSize: ".72rem", fontWeight: 600,
      letterSpacing: ".01em",
      background: isTrue ? "rgba(5,150,105,0.09)" : "var(--surface2)",
      color:      isTrue ? "#059669"               : "var(--muted)",
      border:     `1px solid ${isTrue ? "rgba(5,150,105,0.35)" : "var(--border)"}`,
    }}>
      {isTrue ? "Yes" : "No"}
    </span>
  );
}

// ─── Partners Tab ─────────────────────────────────────────────────────────────
function PartnersTab({ refreshTick = 0 }) {
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const [search, setSearch]         = useState("");
  const [isActive, setIsActive]     = useState("");
  const [isCoke, setIsCoke]         = useState("");
  const [isInternet, setIsInternet] = useState("");
  const [sort, setSort]             = useState("partner_name");
  const [order, setOrder]           = useState("ASC");
  const [page, setPage]             = useState(1);
  const [expandedRow, setExpanded]  = useState(null);
  const [contactsModal, setContactsModal] = useState(null);
  const [connectionsModal, setConnectionsModal] = useState(null);
  const [flowsModal, setFlowsModal] = useState(null);

  const dSearch  = useDebounce(search, 400);
  const abortRef = useRef(null);

  useEffect(() => { setPage(1); }, [dSearch, isActive, isCoke, isInternet, sort, order]);

  const fetchData = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);

    const p = new URLSearchParams({ search: dSearch, sort, order, page, pageSize: 50 });
    if (isActive   !== "") p.set("is_active",   isActive);
    if (isCoke     !== "") p.set("is_coke",     isCoke);
    if (isInternet !== "") p.set("is_internet", isInternet);

    apiFetch(`${API}/api/partners?${p}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => {
        setRows(res.data);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        setLoading(false);
      })
      .catch(err => {
        if (err.name !== "AbortError") { setError(err.message); setLoading(false); }
      });
  }, [dSearch, isActive, isCoke, isInternet, sort, order, page, refreshTick]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = col => {
    if (sort === col) setOrder(o => o === "ASC" ? "DESC" : "ASC");
    else { setSort(col); setOrder("ASC"); }
  };

  const BoolFilter = ({ label, value, onChange }) => (
    <div className="select-wrap">
      <select className="sel" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">All — {label}</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </div>
  );

  return (
    <div className="tab-body">
      {/* Contacts modal */}
      {contactsModal && (
        <ContactsModal partner={contactsModal} onClose={() => setContactsModal(null)} />
      )}
      {/* Connections modal */}
      {connectionsModal && (
        <ConnectionsModal partner={connectionsModal} onClose={() => setConnectionsModal(null)} />
      )}
      {flowsModal && (
        <FlowsModal partner={flowsModal} onClose={() => setFlowsModal(null)} />
      )}

      <div className="filter-bar">
        {/* Search */}
        <div className="input-wrap">
          <span className="input-icon">{Ico.search}</span>
          <input
            className="inp"
            placeholder="Search partner name or description…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="clear-btn" onClick={() => setSearch("")}>{Ico.x}</button>}
        </div>

        {/* Boolean filters */}
        <BoolFilter label="Active"   value={isActive}   onChange={setIsActive}   />
        <BoolFilter label="Coke"     value={isCoke}     onChange={setIsCoke}     />
        <BoolFilter label="Internet" value={isInternet} onChange={setIsInternet} />

        <button className={`btn ${loading ? "spin" : ""}`} onClick={fetchData}>
          {Ico.refresh} {loading ? "Loading…" : "Refresh"}
        </button>

        <div className="result-count">
          {!loading && !error && <><strong>{total.toLocaleString()}</strong> partner{total !== 1 ? "s" : ""}</>}
        </div>
      </div>

      <div className="tbl-wrap">
        {error ? (
          <div className="empty-state error">{error}</div>
        ) : (
          <table>
            <thead>
              <tr>
                {PARTNER_DISPLAY_COLS.map(c => (
                  <th
                    key={c.key}
                    className={sort === c.key ? "sorted" : ""}
                    onClick={() => c.sortable && handleSort(c.key)}
                    style={{ cursor: c.sortable ? "pointer" : "default" }}
                  >
                    <span className="th-inner">
                      {c.label}
                      {c.sortable && <SortIcon col={c.key} sort={sort} order={order} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <SkeletonRows cols={PARTNER_DISPLAY_COLS} />
                : rows.length === 0
                ? <tr><td colSpan={PARTNER_DISPLAY_COLS.length}><div className="empty-state">No partners found.</div></td></tr>
                : rows.map((row, i) => (
                    <>
                      <tr
                        key={row.partner_id}
                        className={expandedRow === i ? "row-expanded" : ""}
                        onClick={() => setExpanded(expandedRow === i ? null : i)}
                        style={{ animationDelay: `${Math.min(i, 20) * 0.02}s`, cursor: "pointer" }}
                      >
                        <td style={{ fontWeight: 600 }}>{row.partner_name || "—"}</td>
                        <td>{fmt(row.created_date)}</td>
                        <td style={{ maxWidth: 300 }} title={row.description}>{row.description || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td><BoolBadge value={row.is_coke}     /></td>
                        <td><BoolBadge value={row.is_internet} /></td>
                        <td><BoolBadge value={row.is_active}   /></td>
                      </tr>
                      {expandedRow === i && (
                        <tr key={`exp-${i}`} className="expanded-row">
                          <td colSpan={PARTNER_DISPLAY_COLS.length}>
                            <div className="detail-panel">
                              {/* Action buttons */}
                              <div style={{ marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button
                                  className="btn"
                                  style={{}}
                                  onClick={e => { e.stopPropagation(); setContactsModal(row); }}
                                >
                                  👥 View Contacts
                                </button>
                                <button
                                  className="btn"
                                  style={{}}
                                  onClick={e => { e.stopPropagation(); setConnectionsModal(row); }}
                                >
                                  🔌 View Connections
                                </button>
                                <button
                                  className="btn"
                                  style={{}}
                                  onClick={e => { e.stopPropagation(); setFlowsModal(row); }}
                                >
                                  🔀 View Flows
                                </button>
                              </div>
                              <div className="detail-grid">
                                {PARTNER_DETAIL_FIELDS.map(([label, key]) => (
                                  <div key={key} className="detail-field">
                                    <div className="detail-label">{label}</div>
                                    <div className="detail-value" style={(key === "partner_id" || key === "created_by" || key === "modified_by") ? { overflow: "visible", whiteSpace: "normal", wordBreak: "break-all" } : {}}>
                                      {typeof row[key] === "boolean"
                                        ? <BoolBadge value={row[key]} />
                                        : (key === "partner_id" || key === "created_by" || key === "modified_by")
                                          ? <CopyCell value={row[key] != null ? String(row[key]) : null} />
                                          : row[key]
                                            ? (key.includes("date") || key.includes("Date")
                                                ? new Date(row[key]).toLocaleString()
                                                : String(row[key]))
                                            : <span style={{ color: "var(--muted)" }}>—</span>
                                      }
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
              }
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

// ─── Contacts Tab ─────────────────────────────────────────────────────────────
function ContactsTab({ refreshTick = 0 }) {
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const [search, setSearch]         = useState("");
  const [isActive, setIsActive]     = useState("");
  const [isCoke, setIsCoke]         = useState("");
  const [isTechnical, setIsTechnical] = useState("");
  const [sort, setSort]             = useState("name");
  const [order, setOrder]           = useState("ASC");
  const [page, setPage]             = useState(1);
  const [expandedRow, setExpanded]  = useState(null);
  const [partnersModal, setPartnersModal] = useState(null); // holds the contact row

  const dSearch  = useDebounce(search, 400);
  const abortRef = useRef(null);

  useEffect(() => { setPage(1); }, [dSearch, isActive, isCoke, isTechnical, sort, order]);

  const fetchData = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);

    const p = new URLSearchParams({ search: dSearch, sort, order, page, pageSize: 50 });
    if (isActive    !== "") p.set("is_active",    isActive);
    if (isCoke      !== "") p.set("is_coke",      isCoke);
    if (isTechnical !== "") p.set("is_technical", isTechnical);

    apiFetch(`${API}/api/contacts?${p}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setRows(res.data); setTotal(res.total); setTotalPages(res.totalPages); setLoading(false); })
      .catch(err => { if (err.name !== "AbortError") { setError(err.message); setLoading(false); } });
  }, [dSearch, isActive, isCoke, isTechnical, sort, order, page, refreshTick]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = col => {
    if (sort === col) setOrder(o => o === "ASC" ? "DESC" : "ASC");
    else { setSort(col); setOrder("ASC"); }
  };

  const BoolFilter = ({ label, value, onChange }) => (
    <div className="select-wrap">
      <select className="sel" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">All — {label}</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </div>
  );

  return (
    <div className="tab-body">
      {/* Partners modal */}
      {partnersModal && (
        <PartnersModal contact={partnersModal} onClose={() => setPartnersModal(null)} />
      )}

      <div className="filter-bar">
        <div className="input-wrap">
          <span className="input-icon">{Ico.search}</span>
          <input
            className="inp"
            placeholder="Search name, email, or phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="clear-btn" onClick={() => setSearch("")}>{Ico.x}</button>}
        </div>

        <BoolFilter label="Active"    value={isActive}    onChange={setIsActive}    />
        <BoolFilter label="Coke"      value={isCoke}      onChange={setIsCoke}      />
        <BoolFilter label="Technical" value={isTechnical} onChange={setIsTechnical} />

        <button className={`btn ${loading ? "spin" : ""}`} onClick={fetchData}>
          {Ico.refresh} {loading ? "Loading…" : "Refresh"}
        </button>

        <div className="result-count">
          {!loading && !error && <><strong>{total.toLocaleString()}</strong> contact{total !== 1 ? "s" : ""}</>}
        </div>
      </div>

      <div className="tbl-wrap">
        {error ? (
          <div className="empty-state error">{error}</div>
        ) : (
          <table>
            <thead>
              <tr>
                {CONTACT_DISPLAY_COLS.map(c => (
                  <th
                    key={c.key}
                    className={sort === c.key ? "sorted" : ""}
                    onClick={() => c.sortable && handleSort(c.key)}
                    style={{ cursor: c.sortable ? "pointer" : "default" }}
                  >
                    <span className="th-inner">
                      {c.label}
                      {c.sortable && <SortIcon col={c.key} sort={sort} order={order} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <SkeletonRows cols={CONTACT_DISPLAY_COLS} />
                : rows.length === 0
                ? <tr><td colSpan={CONTACT_DISPLAY_COLS.length}><div className="empty-state">No contacts found.</div></td></tr>
                : rows.map((row, i) => (
                    <>
                      <tr
                        key={row.contact_id}
                        className={expandedRow === i ? "row-expanded" : ""}
                        onClick={() => setExpanded(expandedRow === i ? null : i)}
                        style={{ animationDelay: `${Math.min(i, 20) * 0.02}s`, cursor: "pointer" }}
                      >
                        <td style={{ fontWeight: 600 }}>{maskName(row.name)}</td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".75rem" }}>{maskEmail(row.email)}</td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".75rem" }}>{maskPhone(row.phone_number)}</td>
                        <td><BoolBadge value={row.is_coke}      /></td>
                        <td><BoolBadge value={row.is_technical} /></td>
                        <td><BoolBadge value={row.is_active}    /></td>
                        <td>
                          {/* Partner chips */}
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {(row.partners || []).slice(0, 3).map(p => (
                              <span key={p.partner_id} style={{
                                background: "var(--surface2)",
                                border: "1px solid var(--border)",
                                borderRadius: 99,
                                padding: "1px 8px",
                                fontSize: ".67rem",
                                fontFamily: "var(--mono)",
                                whiteSpace: "nowrap",
                                color: "var(--accent2)",
                              }}>{p.partner_name}</span>
                            ))}
                            {(row.partners || []).length > 3 && (
                              <span style={{ fontSize: ".67rem", color: "var(--muted)", fontFamily: "var(--mono)", padding: "1px 4px" }}>
                                +{row.partners.length - 3} more
                              </span>
                            )}
                            {(row.partners || []).length === 0 && <span style={{ color: "var(--muted)" }}>—</span>}
                          </div>
                        </td>
                      </tr>
                      {expandedRow === i && (
                        <tr key={`exp-${i}`} className="expanded-row">
                          <td colSpan={CONTACT_DISPLAY_COLS.length}>
                            <div className="detail-panel">
                              {/* View Partners button */}
                              <div style={{ marginBottom: 16 }}>
                                <button
                                  className="btn"
                                  style={{}}
                                  onClick={e => { e.stopPropagation(); setPartnersModal(row); }}
                                >
                                  🏢 View Partners
                                </button>
                              </div>
                              <div className="detail-grid">
                                {CONTACT_DETAIL_FIELDS.map(([label, key]) => (
                                  <div key={key} className="detail-field">
                                    <div className="detail-label">{label}</div>
                                    <div className="detail-value">
                                      {typeof row[key] === "boolean"
                                        ? <BoolBadge value={row[key]} />
                                        : key === "name"         ? maskName(row[key])
                                        : key === "email"        ? maskEmail(row[key])
                                        : key === "phone_number" ? maskPhone(row[key])
                                        : row[key]
                                          ? (key.includes("date") || key.includes("Date")
                                              ? new Date(row[key]).toLocaleString()
                                              : String(row[key]))
                                          : <span style={{ color: "var(--muted)" }}>—</span>
                                      }
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
              }
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

// ─── Connections Tab ──────────────────────────────────────────────────────────
function ConnectionsTab({ refreshTick = 0 }) {
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const [search, setSearch]         = useState("");
  const [protocol, setProtocol]     = useState("");
  const [protocols, setProtocols]   = useState([]);
  const [isActive, setIsActive]     = useState("");
  const [sort, setSort]             = useState("connection_name");
  const [order, setOrder]           = useState("ASC");
  const [page, setPage]             = useState(1);
  const [expandedRow, setExpanded]  = useState(null);
  const [partnerModal, setPartnerModal] = useState(null); // holds the connection row

  const dSearch  = useDebounce(search, 400);
  const abortRef = useRef(null);

  // Fetch distinct protocols for filter dropdown
  useEffect(() => {
    apiFetch(`${API}/api/connections/protocols`)
      .then(r => r.json())
      .then(setProtocols)
      .catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [dSearch, protocol, isActive, sort, order]);

  const fetchData = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);

    const p = new URLSearchParams({ search: dSearch, sort, order, page, pageSize: 50 });
    if (protocol !== "") p.set("protocol", protocol);
    if (isActive !== "") p.set("is_active", isActive);

    apiFetch(`${API}/api/connections?${p}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setRows(res.data); setTotal(res.total); setTotalPages(res.totalPages); setLoading(false); })
      .catch(err => { if (err.name !== "AbortError") { setError(err.message); setLoading(false); } });
  }, [dSearch, protocol, isActive, sort, order, page, refreshTick]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = col => {
    if (sort === col) setOrder(o => o === "ASC" ? "DESC" : "ASC");
    else { setSort(col); setOrder("ASC"); }
  };

  return (
    <div className="tab-body">
      {/* Partner info modal */}
      {partnerModal && (
        <PartnerInfoModal connection={partnerModal} onClose={() => setPartnerModal(null)} />
      )}

      <div className="filter-bar">
        {/* Search */}
        <div className="input-wrap">
          <span className="input-icon">{Ico.search}</span>
          <input
            className="inp"
            placeholder="Search connection name or details…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="clear-btn" onClick={() => setSearch("")}>{Ico.x}</button>}
        </div>

        {/* Protocol filter */}
        <div className="select-wrap">
          <select className="sel" value={protocol} onChange={e => setProtocol(e.target.value)}>
            <option value="">All Protocols</option>
            {protocols.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Active filter */}
        <div className="select-wrap">
          <select className="sel" value={isActive} onChange={e => setIsActive(e.target.value)}>
            <option value="">All — Active</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>

        <button className={`btn ${loading ? "spin" : ""}`} onClick={fetchData}>
          {Ico.refresh} {loading ? "Loading…" : "Refresh"}
        </button>

        <div className="result-count">
          {!loading && !error && <><strong>{total.toLocaleString()}</strong> connection{total !== 1 ? "s" : ""}</>}
        </div>
      </div>

      <div className="tbl-wrap">
        {error ? (
          <div className="empty-state error">{error}</div>
        ) : (
          <table>
            <thead>
              <tr>
                {CONNECTION_DISPLAY_COLS.map(c => (
                  <th
                    key={c.key}
                    className={sort === c.key ? "sorted" : ""}
                    onClick={() => c.sortable && handleSort(c.key)}
                    style={{ cursor: c.sortable ? "pointer" : "default" }}
                  >
                    <span className="th-inner">
                      {c.label}
                      {c.sortable && <SortIcon col={c.key} sort={sort} order={order} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <SkeletonRows cols={CONNECTION_DISPLAY_COLS} />
                : rows.length === 0
                ? <tr><td colSpan={CONNECTION_DISPLAY_COLS.length}><div className="empty-state">No connections found.</div></td></tr>
                : rows.map((row, i) => (
                    <>
                      <tr
                        key={row.connection_id}
                        className={expandedRow === i ? "row-expanded" : ""}
                        onClick={() => setExpanded(expandedRow === i ? null : i)}
                        style={{ animationDelay: `${Math.min(i, 20) * 0.02}s`, cursor: "pointer" }}
                      >
                        <td style={{ fontWeight: 600 }}>{row.connection_name || "—"}</td>
                        <td>
                          {row.protocol ? (
                            <span style={{
                              background: "var(--dim)", border: "1px solid var(--accent)",
                              borderRadius: 3, padding: "2px 10px",
                              fontFamily: "var(--mono)", fontSize: ".7rem", color: "var(--accent)",
                            }}>{row.protocol}</span>
                          ) : "—"}
                        </td>
                        <td style={{ fontWeight: 600 }}>{row.partner_name || "—"}</td>
                        <td><BoolBadge value={row.is_active} /></td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".75rem", color: "var(--muted)" }}>{fmt(row.created_date)}</td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".75rem", color: "var(--muted)" }}>{fmt(row.modified_date)}</td>
                      </tr>
                      {expandedRow === i && (
                        <tr key={`exp-${i}`} className="expanded-row">
                          <td colSpan={CONNECTION_DISPLAY_COLS.length}>
                            <div className="detail-panel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                              {/* View Partner button */}
                              <div>
                                <button
                                  className="btn"
                                  style={{}}
                                  onClick={e => { e.stopPropagation(); setPartnerModal(row); }}
                                >
                                  🤝 View Partner Info
                                </button>
                              </div>

                              {/* Long text fields — full display with scroll */}
                              {["connection_details", "extended_connection_details"].map(key => {
                                const labels = { connection_details: "Connection Details", extended_connection_details: "Extended Connection Details" };
                                return row[key] ? (
                                  <div key={key}>
                                    <div style={{
                                      fontSize: ".66rem", fontWeight: 600, color: "var(--muted)",
                                      textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 5,
                                    }}>{labels[key]}</div>
                                    <div style={{
                                      fontFamily: "var(--mono)", fontSize: ".78rem",
                                      color: "var(--text)", lineHeight: 1.65,
                                      background: "var(--surface2)",
                                      border: "1px solid var(--border)",
                                      borderRadius: "var(--r)",
                                      padding: "10px 14px",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                      maxHeight: 220,
                                      overflowY: "auto",
                                      overflowX: "auto",
                                    }}>{safeStr(row[key])}</div>
                                  </div>
                                ) : null;
                              })}

                              {/* Remaining fields in compact grid — excluding long text keys */}
                              <div className="detail-grid">
                                {CONNECTION_DETAIL_FIELDS
                                  .filter(([, key]) => key !== "connection_details" && key !== "extended_connection_details")
                                  .map(([label, key]) => {
                                    const isIdKey = key === "connection_id" || key === "partner_id";
                                    return (
                                    <div key={key} className="detail-field">
                                      <div className="detail-label">{label}</div>
                                      <div className="detail-value" style={isIdKey ? { overflow: "visible", whiteSpace: "normal", wordBreak: "break-all" } : {}}>
                                        {typeof row[key] === "boolean"
                                          ? <BoolBadge value={row[key]} />
                                          : isIdKey
                                            ? <CopyCell value={row[key] != null ? String(row[key]) : null} />
                                            : row[key]
                                              ? (key.includes("date") || key.includes("Date")
                                                  ? new Date(row[key]).toLocaleString()
                                                  : String(row[key]))
                                              : <span style={{ color: "var(--muted)" }}>—</span>
                                        }
                                      </div>
                                    </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
              }
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

// ─── Pipelines Tab ────────────────────────────────────────────────────────────
function PipelinesTab({ refreshTick = 0, hours = 24, from, to, setHours }) {
  const access   = useAccess();
  const isAdmin  = access.role === "admin";

  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const [search, setSearch]           = useState("");
  const [inboundFilter, setInbound]   = useState("");
  const [outboundFilter, setOutbound] = useState("");
  const [enabledFilter, setEnabled]   = useState("");
  const [isActive, setIsActive]       = useState("");
  const [hasActivity, setHasActivity] = useState("");
  const [sort, setSort]               = useState("inbound_partner_name");
  const [order, setOrder]             = useState("ASC");
  const [page, setPage]               = useState(1);
  const [expandedRow, setExpanded]    = useState(null);
  const [showExport, setShowExport]   = useState(false);
  const [exporting, setExporting]     = useState(false);

  const dSearch   = useDebounce(search,        400);
  const dInbound  = useDebounce(inboundFilter, 500);
  const dOutbound = useDebounce(outboundFilter,500);
  const abortRef  = useRef(null);

  useEffect(() => { setPage(1); }, [dSearch, dInbound, dOutbound, enabledFilter, isActive, hasActivity, hours, from, to, sort, order]);

  const fetchData = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);

    const p = new URLSearchParams({ search: dSearch, sort, order, page, pageSize: 50 });
    Object.entries(rangeParams(hours || 24, from, to)).forEach(([k, v]) => p.set(k, v));
    if (dInbound  !== "") p.set("inbound_partner",  dInbound);
    if (dOutbound !== "") p.set("outbound_partner", dOutbound);
    if (enabledFilter !== "") p.set("enabled",      enabledFilter);
    if (isActive      !== "") p.set("is_active",    isActive);
    if (hasActivity   !== "") p.set("has_activity", hasActivity);

    apiFetch(`${API}/api/pipelines?${p}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setRows(res.data); setTotal(res.total); setTotalPages(res.totalPages); setLoading(false); })
      .catch(err => { if (err.name !== "AbortError") { setError(err.message); setLoading(false); } });
  }, [dSearch, dInbound, dOutbound, enabledFilter, isActive, hasActivity, hours, from, to, sort, order, page, refreshTick]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = col => {
    if (sort === col) setOrder(o => o === "ASC" ? "DESC" : "ASC");
    else { setSort(col); setOrder("ASC"); }
  };

  const handleExport = selectedKeys => {
    setExporting(true);
    const p = new URLSearchParams({ search: dSearch, sort, order, page: 1, pageSize: 1000 });
    Object.entries(rangeParams(hours || 24, from, to)).forEach(([k, v]) => p.set(k, v));
    if (dInbound  !== "") p.set("inbound_partner",  dInbound);
    if (dOutbound !== "") p.set("outbound_partner", dOutbound);
    if (enabledFilter !== "") p.set("enabled",      enabledFilter);
    if (isActive      !== "") p.set("is_active",    isActive);
    if (hasActivity   !== "") p.set("has_activity", hasActivity);

    apiFetch(`${API}/api/pipelines?${p}`)
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => {
        const csv = buildCsv(res.data, selectedKeys);
        triggerCsvDownload(csv);
        setShowExport(false);
      })
      .catch(err => alert(`Export failed: ${err.message}`))
      .finally(() => setExporting(false));
  };

  const PIPELINE_ID_KEYS = new Set(["rule_id", "inbound_partner_id", "outbound_partner_id", "created_by"]);

  const renderDetailVal = (key, val) => {
    if (typeof val === "boolean") return <BoolBadge value={val} />;
    if (val == null || val === "") return <span style={{ color: "var(--muted)" }}>—</span>;
    const isDate = key.includes("_at") || key.includes("date") || key.includes("Date");
    if (isDate && /^\d{4}-\d{2}-\d{2}T/.test(String(val))) return new Date(val).toLocaleString();
    if (PIPELINE_ID_KEYS.has(key)) return <CopyCell value={String(val)} />;
    return String(val);
  };

  // Fields with potentially long content rendered as scrollable boxes
  const LONG_KEYS = new Set(["match_condition", "full_target_path"]);

  // Pill badge for action column
  const ActionBadge = ({ value }) => {
    const colors = {
      route:    { bg: "rgba(5,150,105,0.09)",  border: "rgba(5,150,105,0.4)",   text: "#059669"  },
      forward:  { bg: "rgba(59,130,246,0.09)", border: "rgba(59,130,246,0.4)",  text: "#2563eb"  },
      reject:   { bg: "rgba(220,38,38,0.08)",  border: "rgba(220,38,38,0.35)",  text: "#dc2626"  },
      transform:{ bg: "rgba(124,58,237,0.08)", border: "rgba(124,58,237,0.35)", text: "#7c3aed"  },
      default:  { bg: "var(--surface2)",        border: "var(--border)",          text: "var(--muted)" },
    };
    const c = colors[(value || "").toLowerCase()] || colors.default;
    return value ? (
      <span style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 99, padding: "2px 10px", fontFamily: "var(--mono)", fontSize: ".69rem", fontWeight: 600, color: c.text }}>{value}</span>
    ) : <span style={{ color: "var(--muted)" }}>—</span>;
  };

  // Priority pill — higher = warmer colour
  const PriorityBadge = ({ value }) => {
    const n = parseInt(value);
    const color = isNaN(n) ? "var(--muted)" : n <= 1 ? "#dc2626" : n <= 3 ? "#d97706" : "#059669";
    return value != null
      ? <span style={{ fontFamily: "var(--mono)", fontWeight: 700, fontSize: ".8rem", color }}>{value}</span>
      : <span style={{ color: "var(--muted)" }}>—</span>;
  };

  // Flow arrow — shows inbound → outbound pipeline visually in expanded panel
  const PipelineArrow = ({ row }) => (
    <div style={{
      display: "flex", alignItems: "center", gap: 0,
      background: "var(--bg)", border: "1px solid var(--border)",
      borderRadius: 10, padding: "14px 20px", marginBottom: 18, flexWrap: "wrap",
    }}>
      {/* Inbound side */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em" }}>Inbound Partner</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ fontWeight: 700, fontSize: ".88rem", color: "var(--text)" }}>{row.inbound_partner_name || "—"}</div>
          <FileCopyBtn value={row.inbound_partner_name} />
        </div>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginTop: 6 }}>Inbound Flow</div>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".78rem", color: "var(--text)" }}>{row.inbound_flow_name || "—"}</div>
      </div>

      {/* Arrow */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "0 16px", minWidth: 120 }}>
        <ActionBadge value={row.action} />
        <div style={{ display: "flex", alignItems: "center", gap: 0, width: "100%", margin: "6px 0" }}>
          <div style={{ flex: 1, height: 2, background: "linear-gradient(90deg, var(--border2), var(--muted))" }} />
          <div style={{ color: "var(--text2)", fontSize: 18, lineHeight: 1 }}>▶</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <PriorityBadge value={row.priority} />
          <BoolBadge value={row.enabled} />
        </div>
      </div>

      {/* Outbound side */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160, textAlign: "right" }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em" }}>Outbound Partner</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ fontWeight: 700, fontSize: ".88rem", color: "var(--text)" }}>{row.outbound_partner_name || "—"}</div>
          <FileCopyBtn value={row.outbound_partner_name} />
        </div>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginTop: 6 }}>Outbound Flow</div>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".78rem", color: "var(--text)" }}>{row.outbound_flow_name || "—"}</div>
      </div>
    </div>
  );

  // Flow + connection detail card (inbound or outbound) — mirrors Files tab FlowCard
  const PipelineFlowCard = ({ side, accentColor, flowName, flowId, partnerName, partnerId, connName, connId, connDetails, extConnDetails, fileProcessingRules, absPath, protocol, retryAttempts, maxRetryAttempts, frequency, lastRunTime, flowType, isActive }) => {
    var ac = accentColor || "var(--accent)";
    var borderTopStyle = "2px solid " + ac;
    var accentTextStyle = { fontFamily: "var(--mono)", fontSize: ".88rem", fontWeight: 700, color: ac, lineHeight: 1.3, wordBreak: "break-word" };

    var LongBox = function(props) {
      if (props.value === null || props.value === undefined || props.value === "") return null;
      var display = typeof props.value === "object" ? JSON.stringify(props.value, null, 2) : String(props.value);
      return (
        <div>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".09em", marginBottom: 4 }}>{props.label}</div>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".74rem", color: "var(--text)", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, padding: "8px 11px", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.55, maxHeight: 130, overflowY: "auto" }}>{display}</div>
        </div>
      );
    };

    var _protoLabel = (function(){
      var _pr = String(protocol||"").toLowerCase();
      if (_pr === "thorn-sftp") return "SFTP Server";
      if (_pr === "sftp")       return "SFTP Client";
      if (_pr === "smb")        return "SMB Client";
      if (_pr === "blob")       return "Azure Client";
      if (_pr === "s3")         return "AWS Client";
      return protocol;
    })();
    var _activeLabel = isActive === true || isActive === "true" ? "Enabled" : "Disabled";
    var _activeColor = isActive === true || isActive === "true"
      ? { bg: "rgba(5,150,105,0.10)", border: "rgba(5,150,105,0.35)", text: "#059669" }
      : { bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.30)", text: "#dc2626" };

    return (
      <div style={{ flex: 1, minWidth: 280, background: "var(--surface2)", border: "1px solid var(--border)", borderTop: borderTopStyle, borderRadius: 4, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Header: side label + protocol */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={{ fontFamily: "var(--font)", fontSize: ".68rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".1em", color: ac }}>{side}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {_protoLabel && (
              <span style={{ display: "inline-block", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.35)", borderRadius: 99, padding: "2px 9px", fontFamily: "var(--mono)", fontSize: ".68rem", fontWeight: 600, color: "#2563eb", letterSpacing: ".04em", textTransform: "uppercase" }}>{_protoLabel}</span>
            )}
            <span style={{
              display: "inline-block",
              background: _activeColor.bg, border: "1px solid " + _activeColor.border,
              borderRadius: 99, padding: "2px 9px",
              fontFamily: "var(--mono)", fontSize: ".68rem", fontWeight: 600,
              color: _activeColor.text, letterSpacing: ".04em",
            }}>{_activeLabel}</span>
          </div>
        </div>

        {/* Name */}
        <div>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 3 }}>Name</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ ...accentTextStyle, flex: 1 }}>{flowName || <span style={{ color: "var(--muted)" }}>{"—"}</span>}</div>
            {flowName && <FileCopyBtn value={flowName} />}
          </div>
        </div>

        {/* Flow ID */}
        <div>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 3 }}>Flow ID</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".72rem", color: "var(--text)", wordBreak: "break-all", flex: 1 }}>
              {flowId != null ? String(flowId) : <span style={{ color: "var(--muted)" }}>{"—"}</span>}
            </div>
            {flowId != null && <FileCopyBtn value={String(flowId)} />}
          </div>
        </div>

        {/* Partner */}
        <div>
          <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 3 }}>Partner</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".78rem", fontWeight: 700, color: "var(--text)", flex: 1 }}>
              {partnerName || <span style={{ color: "var(--muted)" }}>{"—"}</span>}
            </div>
            {partnerName && <FileCopyBtn value={partnerName} />}
          </div>
          {partnerId != null && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>Partner ID</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--muted)", wordBreak: "break-all", flex: 1 }}>{partnerId}</div>
              <FileCopyBtn value={String(partnerId)} />
            </div>
          )}
        </div>

        {/* Connection name + ID */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--bg)", borderRadius: 3, border: "1px solid var(--border)", padding: "7px 10px" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em" }}>Connection</div>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {connName || <span style={{ color: "var(--muted)" }}>No connection linked</span>}
            </div>
            {connId != null && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 }}>ID</div>
                <div style={{ fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--muted)", flex: 1 }}>{connId}</div>
                <FileCopyBtn value={String(connId)} />
              </div>
            )}
          </div>
        </div>

        {/* Connection Details */}
        <LongBox label="Connection Details" value={connDetails} />
        {/* Extended Connection Details */}
        <LongBox label="Extended Connection Details" value={extConnDetails} />
        {/* File Processing Rules — only shown when data present */}
        {(function() {
          var _v = fileProcessingRules;
          if (_v === null || _v === undefined) return null;
          if (typeof _v === 'string' && (_v.trim() === '' || _v.trim() === 'null' || _v.trim() === 'undefined')) return null;
          if (typeof _v === 'object' && !Array.isArray(_v) && Object.keys(_v).length === 0) return null;
          if (Array.isArray(_v) && _v.length === 0) return null;
          return <LongBox label="File Processing Rules" value={_v} />;
        })()}
        {(function(){
          var _v = retryAttempts;
          if (_v === null || _v === undefined) return null;
          var _s = String(_v).trim();
          if (_s === "" || _s === "null" || _s === "undefined") return null;
          return (
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".09em", marginBottom: 3 }}>Retry Attempts</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)" }}>{_v}</div>
            </div>
          );
        })()}
        {(function(){
          var _v = maxRetryAttempts;
          if (_v === null || _v === undefined) return null;
          var _s = String(_v).trim();
          if (_s === "" || _s === "null" || _s === "undefined") return null;
          return (
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".09em", marginBottom: 3 }}>Max Retry Attempts</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)" }}>{_v}</div>
            </div>
          );
        })()}
        {(function(){
          var _v = frequency;
          if (_v === null || _v === undefined) return null;
          var _s = String(_v).trim();
          if (_s === "" || _s === "null" || _s === "undefined") return null;
          return (
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".09em", marginBottom: 3 }}>Frequency</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)" }}>{_v}</div>
            </div>
          );
        })()}
        {(function(){
          var _v = lastRunTime;
          if (_v === null || _v === undefined) return null;
          var _s = String(_v).trim();
          if (_s === "" || _s === "null" || _s === "undefined") return null;
          return (
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".6rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".09em", marginBottom: 3 }}>Last Run Time</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".76rem", fontWeight: 600, color: "var(--text)" }}>{new Date(_v).toLocaleString()}</div>
            </div>
          );
        })()}
        {/* Absolute Path */}
        <LongBox label="Absolute Path" value={absPath} />
      </div>
    );
  };

  return (
    <div className="tab-body">
      <div className="filter-bar">
        {/* Global search */}
        <div className="input-wrap">
          <span className="input-icon">{Ico.search}</span>
          <input className="inp" placeholder="Search partner, flow, action, condition…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="clear-btn" onClick={() => setSearch("")}>{Ico.x}</button>}
        </div>

        {/* Inbound partner filter */}
        <div className="input-wrap narrow">
          <span className="input-icon">{Ico.filter}</span>
          <input className="inp" placeholder="Inbound partner…" value={inboundFilter} onChange={e => setInbound(e.target.value)} />
          {inboundFilter && <button className="clear-btn" onClick={() => setInbound("")}>{Ico.x}</button>}
        </div>

        {/* Outbound partner filter */}
        <div className="input-wrap narrow">
          <span className="input-icon">{Ico.filter}</span>
          <input className="inp" placeholder="Outbound partner…" value={outboundFilter} onChange={e => setOutbound(e.target.value)} />
          {outboundFilter && <button className="clear-btn" onClick={() => setOutbound("")}>{Ico.x}</button>}
        </div>

        {/* Enabled filter */}
        <div className="select-wrap">
          <select className="sel" value={enabledFilter} onChange={e => setEnabled(e.target.value)}>
            <option value="">All — Enabled</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>

        {/* Active filter */}
        <div className="select-wrap">
          <select className="sel" value={isActive} onChange={e => setIsActive(e.target.value)}>
            <option value="">All — Active</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>

        {/* Activity filter */}
        <div className="select-wrap">
          <select className="sel" value={hasActivity} onChange={e => setHasActivity(e.target.value)}>
            <option value="">All — Activity</option>
            <option value="true">Has Activity</option>
            <option value="false">No Activity</option>
          </select>
        </div>

        <SavedFiltersMenu
          tab="pipelines"
          currentFilters={{ hours: String(hours), from: from || "", to: to || "", search, inboundFilter, outboundFilter, enabledFilter, isActive, hasActivity }}
          onApply={f => {
            if (f.hours         !== undefined && setHours) setHours(f.hours, f.from, f.to);
            if (f.search        !== undefined) setSearch(f.search);
            if (f.inboundFilter !== undefined) setInbound(f.inboundFilter);
            if (f.outboundFilter!== undefined) setOutbound(f.outboundFilter);
            if (f.enabledFilter !== undefined) setEnabled(f.enabledFilter);
            if (f.isActive      !== undefined) setIsActive(f.isActive);
            if (f.hasActivity   !== undefined) setHasActivity(f.hasActivity);
          }}
        />

        {isAdmin && (
          <button className="btn" onClick={() => setShowExport(true)} title="Export pipelines to CSV"
            style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export
          </button>
        )}

        <button className={`btn ${loading ? "spin" : ""}`} onClick={fetchData}>
          {Ico.refresh} {loading ? "Loading…" : "Refresh"}
        </button>

        <div className="result-count">
          {!loading && !error && <><strong>{total.toLocaleString()}</strong> pipeline{total !== 1 ? "s" : ""}</>}
        </div>
      </div>

      <div className="tbl-wrap">
        {error ? (
          <div className="empty-state error">{error}</div>
        ) : (
          <table>
            <thead>
              <tr>
                {PIPELINE_DISPLAY_COLS.map(c => (
                  <th key={c.key} className={sort === c.key ? "sorted" : ""}
                    onClick={() => c.sortable && handleSort(c.key)}
                    style={{ cursor: c.sortable ? "pointer" : "default" }}>
                    <span className="th-inner">{c.label}{c.sortable && <SortIcon col={c.key} sort={sort} order={order} />}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <SkeletonRows cols={PIPELINE_DISPLAY_COLS} />
                : rows.length === 0
                ? <tr><td colSpan={PIPELINE_DISPLAY_COLS.length}><div className="empty-state">No pipelines found.</div></td></tr>
                : rows.map((row, i) => (
                    <>
                      <tr key={row.rule_id}
                        className={expandedRow === i ? "row-expanded" : ""}
                        onClick={() => setExpanded(expandedRow === i ? null : i)}
                        style={{ animationDelay: `${Math.min(i, 20) * 0.02}s`, cursor: "pointer" }}>
                        <td style={{ fontWeight: 600, color: "var(--accent)"  }}>{row.inbound_partner_name  || "—"}</td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".74rem" }}>{row.inbound_flow_name   || "—"}</td>
                        <td style={{ fontWeight: 600, color: "var(--accent2)" }}>{row.outbound_partner_name || "—"}</td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".74rem" }}>{row.outbound_flow_name  || "—"}</td>
                        <td><ActionBadge value={row.action} /></td>
                        <td><PriorityBadge value={row.priority} /></td>
                        <td><BoolBadge value={row.enabled}   /></td>
                        <td><BoolBadge value={row.is_active} /></td>
                        <td>
                          {row.has_activity
                            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(5,150,105,0.09)", border: "1px solid rgba(5,150,105,0.35)", borderRadius: 99, padding: "2px 10px", fontFamily: "var(--mono)", fontSize: ".69rem", fontWeight: 600, color: "#059669" }}>✓ Active</span>
                            : <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(100,116,139,0.08)", border: "1px solid rgba(100,116,139,0.25)", borderRadius: 99, padding: "2px 10px", fontFamily: "var(--mono)", fontSize: ".69rem", fontWeight: 600, color: "var(--muted)" }}>— None</span>
                          }
                        </td>
                      </tr>

                      {expandedRow === i && (
                        <tr key={`exp-${i}`} className="expanded-row">
                          <td colSpan={PIPELINE_DISPLAY_COLS.length}>
                            <div className="detail-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                              {/* Visual pipeline arrow diagram */}
                              <PipelineArrow row={row} />

                              {/* Inbound / Outbound flow + connection cards — same as Files tab */}
                              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                                <PipelineFlowCard
                                  side="Inbound"
                                  accentColor="var(--accent)"
                                  flowName             ={row.inbound_flow_name}
                                  flowId               ={row.inbound_flow_id}
                                  partnerName          ={row.inbound_partner_name}
                                  partnerId            ={row.inbound_partner_id}
                                  connName             ={row.inbound_connection_name}
                                  connId               ={row.inbound_connection_id}
                                  connDetails          ={row.inbound_connection_details}
                                  extConnDetails       ={row.inbound_extended_connection_details}
                                  fileProcessingRules  ={row.inbound_file_processing_rules}
                                  absPath              ={row.inbound_absolute_flow_path}
                                  protocol             ={row.inbound_protocol}
                                  retryAttempts        ={row.inbound_retry_attempts}
                                  maxRetryAttempts     ={row.inbound_max_retry_attempts}
                                  frequency            ={row.inbound_frequency}
                                  lastRunTime          ={row.inbound_last_run_time}
                                  flowType             ={row.inbound_flow_type}
                                  isActive             ={row.inbound_is_active}
                                />
                                <PipelineFlowCard
                                  side="Outbound"
                                  accentColor="var(--accent2)"
                                  flowName             ={row.outbound_flow_name}
                                  flowId               ={row.outbound_flow_id}
                                  partnerName          ={row.outbound_partner_name}
                                  partnerId            ={row.outbound_partner_id}
                                  connName             ={row.outbound_connection_name}
                                  connId               ={row.outbound_connection_id}
                                  connDetails          ={row.outbound_connection_details}
                                  extConnDetails       ={row.outbound_extended_connection_details}
                                  fileProcessingRules  ={row.outbound_file_processing_rules}
                                  absPath              ={row.outbound_absolute_flow_path}
                                  protocol             ={row.outbound_protocol}
                                  retryAttempts        ={row.outbound_retry_attempts}
                                  maxRetryAttempts     ={row.outbound_max_retry_attempts}
                                  frequency            ={row.outbound_frequency}
                                  lastRunTime          ={row.outbound_last_run_time}
                                  flowType             ={row.outbound_flow_type}
                                  isActive             ={row.outbound_is_active}
                                />
                              </div>

                              {/* Long text fields — match condition + target path */}
                              {["match_condition", "full_target_path"].map(key => {
                                const label = PIPELINE_DETAIL_FIELDS.find(([,k]) => k === key)?.[0] || key;
                                return row[key] ? (
                                  <div key={key}>
                                    <div style={{ fontSize: ".66rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 5 }}>{label}</div>
                                    <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: "var(--r)", padding: "10px 14px", fontFamily: "var(--mono)", fontSize: ".78rem", color: "var(--text)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, maxHeight: 160, overflowY: "auto" }}>{safeStr(row[key])}</div>
                                  </div>
                                ) : null;
                              })}

                              {/* Compact metadata grid */}
                              <div className="detail-grid">
                                {PIPELINE_DETAIL_FIELDS
                                  .filter(([, key]) => !LONG_KEYS.has(key))
                                  .map(([label, key]) => (
                                    <div key={key} className="detail-field">
                                      <div className="detail-label">{label}</div>
                                      <div className="detail-value" style={PIPELINE_ID_KEYS.has(key) ? { overflow: "visible", whiteSpace: "normal", wordBreak: "break-all" } : {}}>{renderDetailVal(key, row[key])}</div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
              }
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />

      {showExport && (
        <PipelineExportModal
          onClose={() => setShowExport(false)}
          onExport={handleExport}
          exporting={exporting}
        />
      )}
    </div>
  );
}

// ─── Flows Tab ────────────────────────────────────────────────────────────────
function FlowsTab({ refreshTick = 0 }) {
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const [search, setSearch]         = useState("");
  const [flowType, setFlowType]     = useState("");
  const [flowTypes, setFlowTypes]   = useState([]);
  const [protocol, setProtocol]     = useState("");
  const [protocols, setProtocols]   = useState([]);
  const [isActive, setIsActive]     = useState("");
  const [sort, setSort]             = useState("flow_name");
  const [order, setOrder]           = useState("ASC");
  const [page, setPage]             = useState(1);
  const [expandedRow, setExpanded]  = useState(null);
  const [partnerModal, setPartnerModal] = useState(null); // {partner_id, connection_name: flow_name}

  const dSearch  = useDebounce(search, 400);
  const abortRef = useRef(null);

  // Fetch filter dropdown values on mount
  useEffect(() => {
    apiFetch(`${API}/api/flows/types`).then(r => r.json()).then(setFlowTypes).catch(() => {});
    apiFetch(`${API}/api/connections/protocols`).then(r => r.json()).then(setProtocols).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [dSearch, flowType, protocol, isActive, sort, order]);

  const fetchData = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);

    const p = new URLSearchParams({ search: dSearch, sort, order, page, pageSize: 50 });
    if (flowType !== "") p.set("flow_type", flowType);
    if (protocol !== "") p.set("protocol",  protocol);
    if (isActive !== "") p.set("is_active", isActive);

    apiFetch(`${API}/api/flows?${p}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setRows(res.data); setTotal(res.total); setTotalPages(res.totalPages); setLoading(false); })
      .catch(err => { if (err.name !== "AbortError") { setError(err.message); setLoading(false); } });
  }, [dSearch, flowType, protocol, isActive, sort, order, page, refreshTick]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = col => {
    if (sort === col) setOrder(o => o === "ASC" ? "DESC" : "ASC");
    else { setSort(col); setOrder("ASC"); }
  };

  // Helper to render full detail value
  const ID_KEYS = new Set(["flow_id", "partner_id", "source_partner_id", "target_partner_id", "connection_id"]);

  const renderDetailVal = (key, val) => {
    if (typeof val === "boolean") return <BoolBadge value={val} />;
    if (val == null || val === "") return <span style={{ color: "var(--muted)" }}>—</span>;
    const isDate = key.includes("time") || key.includes("_at") || key.includes("date") || key.includes("Date");
    if (isDate && /^\d{4}-\d{2}-\d{2}T/.test(String(val))) return new Date(val).toLocaleString();
    // ID fields — display in full and copyable
    if (ID_KEYS.has(key)) return <CopyCell value={String(val)} />;
    return String(val);
  };

  // Long text fields rendered in a scrollable box
  const LONG_KEYS = new Set(["connection_details", "extended_connection_details", "file_processing_rules", "description", "absolute_flow_path"]);

  return (
    <div className="tab-body">
      {/* Partner info modal — reuse PartnerInfoModal by passing a fake connection obj */}
      {partnerModal && (
        <PartnerInfoModal
          connection={partnerModal}
          onClose={() => setPartnerModal(null)}
        />
      )}

      <div className="filter-bar">
        <div className="input-wrap">
          <span className="input-icon">{Ico.search}</span>
          <input className="inp" placeholder="Search flow name, partner, description…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="clear-btn" onClick={() => setSearch("")}>{Ico.x}</button>}
        </div>

        {/* Flow type */}
        <div className="select-wrap">
          <select className="sel" value={flowType} onChange={e => setFlowType(e.target.value)}>
            <option value="">All Types</option>
            {flowTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Protocol */}
        <div className="select-wrap">
          <select className="sel" value={protocol} onChange={e => setProtocol(e.target.value)}>
            <option value="">All Protocols</option>
            {protocols.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Active */}
        <div className="select-wrap">
          <select className="sel" value={isActive} onChange={e => setIsActive(e.target.value)}>
            <option value="">All — Active</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>

        <button className={`btn ${loading ? "spin" : ""}`} onClick={fetchData}>
          {Ico.refresh} {loading ? "Loading…" : "Refresh"}
        </button>

        <div className="result-count">
          {!loading && !error && <><strong>{total.toLocaleString()}</strong> flow{total !== 1 ? "s" : ""}</>}
        </div>
      </div>

      <div className="tbl-wrap">
        {error ? (
          <div className="empty-state error">{error}</div>
        ) : (
          <table>
            <thead>
              <tr>
                {FLOW_DISPLAY_COLS.map(c => (
                  <th key={c.key} className={sort === c.key ? "sorted" : ""}
                    onClick={() => c.sortable && handleSort(c.key)}
                    style={{ cursor: c.sortable ? "pointer" : "default" }}>
                    <span className="th-inner">{c.label}{c.sortable && <SortIcon col={c.key} sort={sort} order={order} />}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <SkeletonRows cols={FLOW_DISPLAY_COLS} />
                : rows.length === 0
                ? <tr><td colSpan={FLOW_DISPLAY_COLS.length}><div className="empty-state">No flows found.</div></td></tr>
                : rows.map((row, i) => (
                    <>
                      <tr key={row.flow_id}
                        className={expandedRow === i ? "row-expanded" : ""}
                        onClick={() => setExpanded(expandedRow === i ? null : i)}
                        style={{ animationDelay: `${Math.min(i, 20) * 0.02}s`, cursor: "pointer" }}>
                        <td style={{ fontWeight: 600, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4, paddingTop: 8, paddingBottom: 8 }}>
                          <CopyCell value={row.flow_name} />
                        </td>
                        <td>
                          {row.flow_type ? (
                            <span style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 99, padding: "2px 9px", fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--muted)" }}>{row.flow_type}</span>
                          ) : "—"}
                        </td>
                        <td>
                          {row.protocol ? (
                            <span style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 99, padding: "2px 9px", fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--accent2)" }}>{row.protocol}</span>
                          ) : "—"}
                        </td>
                        {/* Clickable partner name */}
                        <td>
                          {row.partner_name ? (
                            <button
                              onClick={e => { e.stopPropagation(); setPartnerModal({ partner_id: row.partner_id, connection_name: row.flow_name }); }}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontFamily: "var(--font)", fontSize: ".82rem", fontWeight: 600, padding: 0, textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}
                            >{row.partner_name}</button>
                          ) : "—"}
                        </td>
                        <td><BoolBadge value={row.is_active} /></td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".74rem" }}>{row.frequency || "—"}</td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".73rem", color: "var(--muted)" }}>{row.last_run_time ? new Date(row.last_run_time).toLocaleString() : "—"}</td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".74rem", color: "var(--success)" }}>{row.uploaded_files ?? "—"}</td>
                        <td style={{ fontFamily: "var(--mono)", fontSize: ".74rem", color: row.failed_files > 0 ? "var(--danger)" : "var(--muted)" }}>{row.failed_files ?? "—"}</td>
                      </tr>

                      {expandedRow === i && (
                        <tr key={`exp-${i}`} className="expanded-row">
                          <td colSpan={FLOW_DISPLAY_COLS.length}>
                            <div className="detail-panel">
                              {/* Long text fields first */}
                              {["description","connection_details","extended_connection_details","file_processing_rules","absolute_flow_path"].map(key => {
                                const label = FLOW_DETAIL_FIELDS.find(([,k]) => k === key)?.[0] || key;
                                return row[key] ? (
                                  <div key={key} style={{ marginBottom: 14 }}>
                                    <div style={{ fontFamily: "var(--mono)", fontSize: ".64rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 5 }}>{label}</div>
                                    <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "9px 13px", fontFamily: "var(--mono)", fontSize: ".75rem", color: "var(--text)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, maxHeight: 140, overflowY: "auto" }}>{safeStr(row[key])}</div>
                                  </div>
                                ) : null;
                              })}

                              {/* All remaining fields in grid */}
                              <div className="detail-grid">
                                {FLOW_DETAIL_FIELDS
                                  .filter(([, key]) => !LONG_KEYS.has(key))
                                  .map(([label, key]) => (
                                    <div key={key} className="detail-field">
                                      <div className="detail-label">{label}</div>
                                      <div className="detail-value" style={ID_KEYS.has(key) ? { overflow: "visible", whiteSpace: "normal", wordBreak: "break-all" } : {}}>{renderDetailVal(key, row[key])}</div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
              }
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

// ─── Message Hub Tab ──────────────────────────────────────────────────────────
function MessageHubTab({ hours, from, to, refreshTick = 0 }) {
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState("");
  const [sort, setSort]             = useState("id");
  const [order, setOrder]           = useState("DESC");
  const [page, setPage]             = useState(1);
  const dSearch   = useDebounce(search, 400);
  const abortRef  = useRef(null);

  useEffect(() => { setPage(1); }, [hours, from, to, dSearch, sort, order]);

  const fetchData = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    const p = new URLSearchParams({ ...rangeParams(hours, from, to), search: dSearch, sort, order, page, pageSize: 50 });
    apiFetch(`${API}/api/message-hub?${p}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(res => { setRows(res.data); setTotal(res.total); setTotalPages(res.totalPages); setLoading(false); })
      .catch(err => { if (err.name !== "AbortError") { setError(err.message); setLoading(false); }});
  }, [hours, from, to, dSearch, sort, order, page, refreshTick]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = col => {
    if (sort === col) setOrder(o => o === "DESC" ? "ASC" : "DESC");
    else { setSort(col); setOrder("DESC"); }
  };

  return (
    <div className="tab-body">
      <div className="filter-bar">
        <div className="input-wrap">
          <span className="input-icon">{Ico.search}</span>
          <input
            className="inp"
            placeholder="Search message content…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="clear-btn" onClick={() => setSearch("")}>{Ico.x}</button>}
        </div>
        <button className={`btn ${loading ? "spin" : ""}`} onClick={fetchData}>{Ico.refresh} {loading ? "Loading…" : "Refresh"}</button>
        <div className="result-count">
          {!loading && !error && <><strong>{total.toLocaleString()}</strong> message{total !== 1 ? "s" : ""}</>}
        </div>
      </div>
      <div className="tbl-wrap">
        {error ? (
          <div className="empty-state error">{error}</div>
        ) : (
          <table>
            <thead>
              <tr>
                {MSG_COLS.map(c => (
                  <th key={c.key} className={sort === c.key ? "sorted" : ""}
                    onClick={() => c.sortable && handleSort(c.key)}
                    style={{ cursor: c.sortable ? "pointer" : "default" }}>
                    <span className="th-inner">{c.label} {c.sortable && <SortIcon col={c.key} sort={sort} order={order}/>}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <SkeletonRows cols={MSG_COLS} />
                : rows.length === 0
                ? <tr><td colSpan={MSG_COLS.length}><div className="empty-state">No messages found.</div></td></tr>
                : rows.map((row, i) => (
                    <tr key={row.id} style={{ animationDelay: `${Math.min(i, 20) * 0.02}s` }}>
                      <td style={{ fontFamily: "var(--mono)", fontSize: "0.75rem" }}>{row.id}</td>
                      <td>{fmt(row.created_date)}</td>
                      <td>{fmt(row.modified_date)}</td>
                      <td style={{ maxWidth: 480, fontFamily: "var(--mono)", fontSize: "0.73rem" }}>
                        <span title={row.message_content}>{row.message_content}</span>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        )}
      </div>
      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

// ─── PII Masking Utilities ────────────────────────────────────────────────────
// Masking removed — app is secured via Azure AD authentication
function maskName(name)   { return name  || "—"; }
function maskEmail(email) { return email || "—"; }
function maskPhone(phone) { return phone || "—"; }
  
// ─── Root App ─────────────────────────────────────────────────────────────────
const REFRESH_OPTIONS = [
  { label: "Off",  ms: 0     },
  { label: "15s",  ms: 15000 },
  { label: "30s",  ms: 30000 },
  { label: "1m",   ms: 60000 },
  { label: "5m",   ms: 300000},
];


// ── AdministrationTab (admin only) ────────────────────────────────────────────
function AdministrationTab() {
  const [section, setSection] = useState("user-admin");

  const NAV_STYLE = (active) => ({
    display: "block", width: "100%", textAlign: "left",
    padding: "9px 16px", marginBottom: 2, borderRadius: 6, border: "none", cursor: "pointer",
    fontFamily: "var(--mono)", fontSize: ".78rem", fontWeight: active ? 700 : 400,
    background: active ? "var(--accent)" : "transparent",
    color: active ? "#fff" : "var(--text)",
  });
  const NAV_GROUP = { fontFamily: "var(--mono)", fontSize: ".62rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)",
    padding: "14px 4px 6px", marginTop: 8 };
  const NAV_SUB = (active) => ({
    ...NAV_STYLE(active), paddingLeft: 28, fontSize: ".73rem",
  });

  return (
    <div style={{ display: "flex", gap: 0, minHeight: 500 }}>
      {/* Sidebar */}
      <div style={{ width: 210, borderRight: "1px solid var(--border)", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: ".65rem", fontWeight: 700,
          textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)", marginBottom: 12, paddingLeft: 4 }}>
          Administration
        </div>

        {/* Users group */}
        <div style={NAV_GROUP}>Users</div>
        <button style={NAV_STYLE(section === "user-admin")} onClick={() => setSection("user-admin")}>
          👤 User Admin
        </button>

        {/* Utilities group */}
        <div style={NAV_GROUP}>Utilities</div>
        <button style={NAV_SUB(section === "connectivity")} onClick={() => setSection("connectivity")}>
          🔌 Connectivity Tests
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: "24px 28px", minWidth: 0 }}>
        {section === "user-admin"   && <UserAdminSection />}
        {section === "connectivity" && <ConnectivityTestSection />}
      </div>
    </div>
  );
}

// ── ConnectivityTestSection ───────────────────────────────────────────────────
const PROTOCOL_TABS = [
  { id: "SFTP_PASSWORD", label: "SFTP (Password)",  protocol: "SFTP" },
  { id: "SFTP_SSH",      label: "SFTP (SSH Key)",   protocol: "SFTP" },
  { id: "Blob",          label: "Azure Blob",        protocol: "Blob" },
  { id: "S3",            label: "S3",                protocol: "S3"   },
  { id: "SMB",           label: "SMB",               protocol: "SMB"  },
];

function ConnectivityTestSection() {
  const [proto,   setProto]   = useState("SFTP_PASSWORD");
  const [form,    setForm]    = useState({});
  const [busy,    setBusy]    = useState(false);
  const [result,  setResult]  = useState(null); // { success, valid, message, error }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setResult(null);
  }

  function switchProto(id) { setProto(id); setForm({}); setResult(null); }

  async function handleTest(e) {
    e.preventDefault();
    setBusy(true); setResult(null);
    const tab      = PROTOCOL_TABS.find(t => t.id === proto);
    const protocol = tab.protocol;
    let connection_details = {};

    if (proto === "SFTP_PASSWORD") {
      connection_details = {
        host: form.host, port: parseInt(form.port) || 22,
        username: form.username, password: form.password,
        ...(form.keyvault && { keyvault: form.keyvault }),
      };
    } else if (proto === "SFTP_SSH") {
      connection_details = {
        host: form.host, port: parseInt(form.port) || 22,
        username: form.username, ssh_privatekey: form.ssh_privatekey,
        ...(form.passphrase            && { passphrase:            form.passphrase }),
        ...(form.server_host_key_algs  && { server_host_key_algs:  form.server_host_key_algs }),
        ...(form.kex_algs              && { kex_algs:              form.kex_algs }),
        ...(form.keyvault              && { keyvault:              form.keyvault }),
      };
    } else if (proto === "Blob") {
      connection_details = {
        account_name: form.account_name, connection_string: form.connection_string,
        ...(form.keyvault && { keyvault: form.keyvault }),
      };
    } else if (proto === "S3") {
      connection_details = {
        s3_region: form.s3_region, aws_accesskey_id: form.aws_accesskey_id,
        aws_accesskey_secret: form.aws_accesskey_secret,
        ...(form.s3_bucket_name && { s3_bucket_name: form.s3_bucket_name }),
        ...(form.keyvault       && { keyvault:       form.keyvault }),
      };
    } else if (proto === "SMB") {
      connection_details = {
        server: form.server, username: form.username,
        password: form.password, domain: form.domain,
        ...(form.share    && { share:    form.share }),
        ...(form.keyvault && { keyvault: form.keyvault }),
      };
    }

    try {
      const r = await apiFetch(`${API}/api/onboarding/v1/admin/connections/validate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ protocol, connection_details }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.message || `HTTP ${r.status}`);
      setResult({ success: d.success, valid: d.data?.valid, message: d.message, error: d.data?.error });
    } catch (err) {
      setResult({ success: false, valid: false, message: err.message });
    } finally {
      setBusy(false);
    }
  }

  const F = ADM_FIELD, L = ADM_LABEL, I = ADM_INPUT, H = ADM_HINT;

  return (
    <div style={{ maxWidth: 600 }}>
      <h2 style={{ fontFamily: "var(--mono)", fontSize: ".85rem", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)", marginBottom: 20 }}>
        Connectivity Tests
      </h2>

      {/* Protocol selector tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 24,
        borderBottom: "1px solid var(--border)", paddingBottom: 14 }}>
        {PROTOCOL_TABS.map(t => (
          <button key={t.id} onClick={() => switchProto(t.id)}
            style={{ fontFamily: "var(--mono)", fontSize: ".73rem", fontWeight: proto === t.id ? 700 : 400,
              padding: "6px 14px", borderRadius: 6, border: "1px solid",
              borderColor: proto === t.id ? "var(--accent)" : "var(--border)",
              background: proto === t.id ? "var(--accent)" : "transparent",
              color: proto === t.id ? "#fff" : "var(--text)", cursor: "pointer" }}>
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleTest}>

        {/* ── SFTP (Password) ── */}
        {proto === "SFTP_PASSWORD" && (<>
          <div style={F}><label style={L}>Host <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="host" value={form.host||""} onChange={handleChange} placeholder="sftp.example.com" required /></div>
          <div style={{display:"flex",gap:12}}>
            <div style={{...F,flex:1}}><label style={L}>Port <span style={{color:"#ef4444"}}>*</span></label>
              <input style={I} name="port" value={form.port||""} onChange={handleChange} placeholder="22" type="number" required /></div>
            <div style={{...F,flex:2}}><label style={L}>Username <span style={{color:"#ef4444"}}>*</span></label>
              <input style={I} name="username" value={form.username||""} onChange={handleChange} placeholder="sftpuser" required /></div>
          </div>
          <div style={F}><label style={L}>Password (KV secret name) <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="password" value={form.password||""} onChange={handleChange} placeholder="my-sftp-password-secret" required />
            <div style={H}>Provide the Key Vault secret name — not the plain-text password.</div></div>
          <div style={F}><label style={L}>Key Vault <span style={{fontWeight:400}}>(optional — uses default if blank)</span></label>
            <input style={I} name="keyvault" value={form.keyvault||""} onChange={handleChange} placeholder="kv-my-vault" /></div>
        </>)}

        {/* ── SFTP (SSH Key) ── */}
        {proto === "SFTP_SSH" && (<>
          <div style={F}><label style={L}>Host <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="host" value={form.host||""} onChange={handleChange} placeholder="sftp.example.com" required /></div>
          <div style={{display:"flex",gap:12}}>
            <div style={{...F,flex:1}}><label style={L}>Port <span style={{color:"#ef4444"}}>*</span></label>
              <input style={I} name="port" value={form.port||""} onChange={handleChange} placeholder="22" type="number" required /></div>
            <div style={{...F,flex:2}}><label style={L}>Username <span style={{color:"#ef4444"}}>*</span></label>
              <input style={I} name="username" value={form.username||""} onChange={handleChange} placeholder="sftpuser" required /></div>
          </div>
          <div style={F}><label style={L}>SSH Private Key (KV secret name) <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="ssh_privatekey" value={form.ssh_privatekey||""} onChange={handleChange} placeholder="my-private-key-secret" required />
            <div style={H}>Provide the Key Vault secret name containing the private key.</div></div>
          <div style={F}><label style={L}>Passphrase (KV secret name) <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="passphrase" value={form.passphrase||""} onChange={handleChange} placeholder="my-passphrase-secret" /></div>
          <div style={F}><label style={L}>Server Host Key Algorithms <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="server_host_key_algs" value={form.server_host_key_algs||""} onChange={handleChange} placeholder="ssh-rsa,rsa-sha2-256,rsa-sha2-512" />
            <div style={H}>Comma-separated list of accepted server host key algorithms.</div></div>
          <div style={F}><label style={L}>KEX Algorithms <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="kex_algs" value={form.kex_algs||""} onChange={handleChange} placeholder="diffie-hellman-group14-sha1,curve25519-sha256" /></div>
          <div style={F}><label style={L}>Key Vault <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="keyvault" value={form.keyvault||""} onChange={handleChange} placeholder="kv-my-vault" /></div>
        </>)}

        {/* ── Azure Blob ── */}
        {proto === "Blob" && (<>
          <div style={F}><label style={L}>Storage Account Name <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="account_name" value={form.account_name||""} onChange={handleChange} placeholder="mystorageaccount" required /></div>
          <div style={F}><label style={L}>Connection String (KV secret name) <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="connection_string" value={form.connection_string||""} onChange={handleChange} placeholder="blob-connection-string-secret" required />
            <div style={H}>Provide the Key Vault secret name — not the connection string itself.</div></div>
          <div style={F}><label style={L}>Key Vault <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="keyvault" value={form.keyvault||""} onChange={handleChange} placeholder="kv-my-vault" /></div>
        </>)}

        {/* ── S3 ── */}
        {proto === "S3" && (<>
          <div style={F}><label style={L}>Region <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="s3_region" value={form.s3_region||""} onChange={handleChange} placeholder="us-east-1" required /></div>
          <div style={F}><label style={L}>Access Key ID <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="aws_accesskey_id" value={form.aws_accesskey_id||""} onChange={handleChange} placeholder="AKIAIOSFODNN7EXAMPLE" required /></div>
          <div style={F}><label style={L}>Access Key Secret (KV secret name) <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="aws_accesskey_secret" value={form.aws_accesskey_secret||""} onChange={handleChange} placeholder="aws-secret-key-name" required />
            <div style={H}>Provide the Key Vault secret name — not the plain-text secret.</div></div>
          <div style={F}><label style={L}>Bucket Name <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="s3_bucket_name" value={form.s3_bucket_name||""} onChange={handleChange} placeholder="my-bucket" /></div>
          <div style={F}><label style={L}>Key Vault <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="keyvault" value={form.keyvault||""} onChange={handleChange} placeholder="kv-my-vault" /></div>
        </>)}

        {/* ── SMB ── */}
        {proto === "SMB" && (<>
          <div style={F}><label style={L}>Server <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="server" value={form.server||""} onChange={handleChange} placeholder="fileserver.company.com" required /></div>
          <div style={{display:"flex",gap:12}}>
            <div style={{...F,flex:1}}><label style={L}>Domain <span style={{color:"#ef4444"}}>*</span></label>
              <input style={I} name="domain" value={form.domain||""} onChange={handleChange} placeholder="COMPANY" required /></div>
            <div style={{...F,flex:2}}><label style={L}>Username <span style={{color:"#ef4444"}}>*</span></label>
              <input style={I} name="username" value={form.username||""} onChange={handleChange} placeholder="smbuser" required /></div>
          </div>
          <div style={F}><label style={L}>Password (KV secret name) <span style={{color:"#ef4444"}}>*</span></label>
            <input style={I} name="password" value={form.password||""} onChange={handleChange} placeholder="smb-password-secret" required />
            <div style={H}>Provide the Key Vault secret name — not the plain-text password.</div></div>
          <div style={F}><label style={L}>Share <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="share" value={form.share||""} onChange={handleChange} placeholder="myshare" /></div>
          <div style={F}><label style={L}>Key Vault <span style={{fontWeight:400}}>(optional)</span></label>
            <input style={I} name="keyvault" value={form.keyvault||""} onChange={handleChange} placeholder="kv-my-vault" /></div>
        </>)}

        {/* Result banner */}
        {result && (
          <div style={{ margin: "20px 0", padding: "14px 18px", borderRadius: 8,
            border: `1px solid ${result.valid ? "#10b981" : "#ef4444"}`,
            background: result.valid ? "rgba(16,185,129,.08)" : "rgba(239,68,68,.08)" }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: ".82rem", fontWeight: 700,
              color: result.valid ? "#10b981" : "#ef4444", marginBottom: result.error ? 6 : 0 }}>
              {result.valid ? "✓ Connection successful" : "✗ Connection failed"}
            </div>
            {result.message && <div style={{ fontFamily: "var(--mono)", fontSize: ".72rem",
              color: "var(--text)", marginTop: 4 }}>{result.message}</div>}
            {result.error && <div style={{ fontFamily: "var(--mono)", fontSize: ".72rem",
              color: "#ef4444", marginTop: 4 }}>{result.error}</div>}
          </div>
        )}

        <button type="submit" disabled={busy} className="btn"
          style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#fff",
            fontWeight: 700, minWidth: 160, marginTop: 4 }}>
          {busy ? "Testing…" : "🔌 Test Connection"}
        </button>
      </form>
    </div>
  );
}

// Shared styles for admin forms
const ADM_LABEL = { fontFamily: "var(--mono)", fontSize: ".72rem", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: ".06em", color: "var(--muted)", marginBottom: 4, display: "block" };
const ADM_INPUT = { fontFamily: "var(--mono)", fontSize: ".8rem", padding: "8px 10px",
  background: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border)",
  borderRadius: 6, width: "100%", boxSizing: "border-box" };
const ADM_HINT  = { fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--muted)", marginTop: 4 };
const ADM_FIELD = { marginBottom: 16 };

function UserFormFields({ form, onChange }) {
  return (<>
    <div style={ADM_FIELD}>
      <label style={ADM_LABEL}>Name <span style={{ color: "#ef4444" }}>*</span></label>
      <input style={ADM_INPUT} name="name" value={form.name} onChange={onChange}
        placeholder="John Doe" required />
    </div>
    <div style={ADM_FIELD}>
      <label style={ADM_LABEL}>Email</label>
      <input style={ADM_INPUT} name="email" value={form.email} onChange={onChange}
        placeholder="user@coca-cola.com" type="email" />
      <div style={ADM_HINT}>Required for human users. Leave blank for Service Principal accounts.</div>
    </div>
    <div style={ADM_FIELD}>
      <label style={ADM_LABEL}>Role</label>
      <select style={ADM_INPUT} name="role" value={form.role} onChange={onChange}>
        <option value="user">user</option>
        <option value="admin">admin</option>
        <option value="readonly">readonly</option>
      </select>
    </div>
    <div style={{ ...ADM_FIELD, display: "flex", alignItems: "center", gap: 10 }}>
      <input type="checkbox" name="is_active" id="ua_is_active"
        checked={form.is_active} onChange={onChange} style={{ width: 16, height: 16, cursor: "pointer" }} />
      <label htmlFor="ua_is_active" style={{ ...ADM_LABEL, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: ".8rem", cursor: "pointer" }}>
        Active
      </label>
    </div>
    <div style={ADM_FIELD}>
      <label style={ADM_LABEL}>Client ID <span style={{ fontWeight: 400, textTransform: "none" }}>(Service Principal only)</span></label>
      <input style={ADM_INPUT} name="client_id" value={form.client_id} onChange={onChange}
        placeholder="e27483ee-f7e2-4e37-ae6b-2b62ef47cc16" />
      <div style={ADM_HINT}>Azure AD application client_id for SPN accounts. Leave blank for human users.</div>
    </div>
  </>);
}

function UserAdminSection() {
  const EMPTY_CREATE = { name: "", email: "", role: "user", is_active: true, client_id: "" };

  // List state
  const [users,         setUsers]        = useState([]);
  const [total,         setTotal]        = useState(0);
  const [listLoading,   setListLoading]  = useState(false);
  const [listError,     setListError]    = useState(null);
  const [search,        setSearch]       = useState("");
  const [inclInactive,  setInclInactive] = useState(false);

  // Create modal state
  const [showCreate,  setShowCreate]  = useState(false);
  const [createForm,  setCreateForm]  = useState(EMPTY_CREATE);
  const [createBusy,  setCreateBusy]  = useState(false);
  const [createError, setCreateError] = useState(null);

  // Edit panel state
  const [editUser,  setEditUser]  = useState(null);  // the user object being edited
  const [editForm,  setEditForm]  = useState({});
  const [editBusy,  setEditBusy]  = useState(false);
  const [editError, setEditError] = useState(null);
  const [editOk,    setEditOk]    = useState(false);

  async function loadUsers() {
    setListLoading(true); setListError(null);
    try {
      const r = await apiFetch(`${API}/api/onboarding/v1/admin/users?include_inactive=${inclInactive}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.message || `HTTP ${r.status}`);
      setUsers(d.users || []);
      setTotal(d.total || 0);
    } catch (e) { setListError(e.message); }
    finally { setListLoading(false); }
  }

  React.useEffect(() => { loadUsers(); }, [inclInactive]);

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) ||
           (u.email || "").toLowerCase().includes(q) ||
           (u.role || "").toLowerCase().includes(q);
  });

  // Create
  function handleCreateChange(e) {
    const { name, value, type, checked } = e.target;
    setCreateForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  }
  async function handleCreate(e) {
    e.preventDefault();
    setCreateBusy(true); setCreateError(null);
    const body = {
      name:      createForm.name.trim(),
      role:      createForm.role || "user",
      is_active: createForm.is_active,
      ...(createForm.email.trim()     && { email:     createForm.email.trim() }),
      ...(createForm.client_id.trim() && { client_id: createForm.client_id.trim() }),
    };
    try {
      const r = await apiFetch(`${API}/api/onboarding/v1/admin/users`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.message || `HTTP ${r.status}`);
      setShowCreate(false); setCreateForm(EMPTY_CREATE);
      loadUsers();
    } catch (e) { setCreateError(e.message); }
    finally { setCreateBusy(false); }
  }

  // Edit
  function openEdit(user) {
    setEditUser(user);
    setEditForm({ name: user.name || "", email: user.email || "", role: user.role || "user",
      is_active: user.is_active, client_id: user.client_id || "" });
    setEditError(null); setEditOk(false);
  }
  function handleEditChange(e) {
    const { name, value, type, checked } = e.target;
    setEditForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  }
  async function handleUpdate(e) {
    e.preventDefault();
    setEditBusy(true); setEditError(null); setEditOk(false);
    const body = {
      name:      editForm.name.trim() || null,
      role:      editForm.role || null,
      is_active: editForm.is_active,
      client_id: editForm.client_id.trim() || "",
    };
    try {
      const r = await apiFetch(`${API}/api/onboarding/v1/admin/users/${editUser.user_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.message || `HTTP ${r.status}`);
      setEditOk(true);
      setUsers(prev => prev.map(u => u.user_id === editUser.user_id ? { ...u, ...d } : u));
      setEditUser({ ...editUser, ...d });
    } catch (e) { setEditError(e.message); }
    finally { setEditBusy(false); }
  }

  const ROLE_COLOR = { admin: "#8b5cf6", readonly: "#64748b", user: "#10b981" };

  return (
    <div style={{ display: "flex", gap: 0, height: "100%" }}>
      {/* ── User list ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Toolbar */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}>
          <h2 style={{ fontFamily: "var(--mono)", fontSize: ".85rem", fontWeight: 700,
            textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)", margin: 0, flex: "0 0 auto" }}>
            User Admin
          </h2>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, email, role…"
            style={{ ...ADM_INPUT, width: 240, flex: "0 0 auto" }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "var(--mono)",
            fontSize: ".75rem", color: "var(--muted)", cursor: "pointer", flex: "0 0 auto" }}>
            <input type="checkbox" checked={inclInactive} onChange={e => setInclInactive(e.target.checked)} />
            Show inactive
          </label>
          <button className={`btn ${listLoading ? "spin" : ""}`} onClick={loadUsers} style={{ flex: "0 0 auto" }}>
            {Ico.refresh}
          </button>
          <button className="btn" onClick={() => { setShowCreate(true); setCreateError(null); }}
            style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#fff",
              fontWeight: 700, flex: "0 0 auto", marginLeft: "auto" }}>
            + New User
          </button>
        </div>

        {listError && <div style={{ fontFamily: "var(--mono)", fontSize: ".75rem", color: "#ef4444",
          padding: "10px 14px", background: "var(--surface2)", borderRadius: 8, marginBottom: 14 }}>{listError}</div>}

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "22%" }} /><col style={{ width: "30%" }} />
              <col style={{ width: "12%" }} /><col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} /><col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr>
                {["Name", "Email", "Role", "Active", "Created", ""].map((h, i) => (
                  <th key={i} style={{ fontFamily: "var(--mono)", fontSize: ".62rem", fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: ".08em", color: "var(--muted)",
                    padding: "6px 10px", textAlign: "left", borderBottom: "1px solid var(--border)",
                    whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listLoading
                ? <tr><td colSpan={6} style={{ padding: 20, fontFamily: "var(--mono)", fontSize: ".75rem", color: "var(--muted)" }}>Loading…</td></tr>
                : filtered.length === 0
                ? <tr><td colSpan={6} style={{ padding: 20, fontFamily: "var(--mono)", fontSize: ".75rem", color: "var(--muted)" }}>No users found.</td></tr>
                : filtered.map(u => (
                  <tr key={u.user_id}
                    onClick={() => openEdit(u)}
                    style={{ cursor: "pointer", background: editUser?.user_id === u.user_id ? "var(--surface2)" : "transparent",
                      opacity: u.is_active ? 1 : 0.45 }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--surface2)"}
                    onMouseLeave={e => e.currentTarget.style.background = editUser?.user_id === u.user_id ? "var(--surface2)" : "transparent"}
                  >
                    <td style={{ fontFamily: "var(--mono)", fontSize: ".75rem", padding: "8px 10px",
                      borderBottom: "1px solid var(--border)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.name}
                    </td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: ".72rem", padding: "8px 10px",
                      borderBottom: "1px solid var(--border)", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.email}
                    </td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: ".7rem", padding: "8px 10px", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ background: (ROLE_COLOR[u.role] || "#64748b") + "22",
                        color: ROLE_COLOR[u.role] || "#64748b", border: `1px solid ${ROLE_COLOR[u.role] || "#64748b"}44`,
                        borderRadius: 4, padding: "2px 7px", fontWeight: 700, fontSize: ".65rem",
                        textTransform: "uppercase", letterSpacing: ".06em" }}>{u.role}</span>
                    </td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: ".75rem", padding: "8px 10px", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ color: u.is_active ? "#10b981" : "#ef4444", fontWeight: 700 }}>
                        {u.is_active ? "Yes" : "No"}
                      </span>
                    </td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--muted)",
                      padding: "8px 10px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>
                      {u.created_date ? new Date(u.created_date).toLocaleDateString() : "—"}
                    </td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", textAlign: "right" }}>
                      <span style={{ fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--accent)" }}>Edit →</span>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
          {!listLoading && <div style={{ fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--muted)", marginTop: 10 }}>
            {filtered.length} of {total} user{total !== 1 ? "s" : ""}
            {search ? ` matching "${search}"` : ""}
          </div>}
        </div>
      </div>

      {/* ── Edit panel (slide in from right) ── */}
      {editUser && (
        <div style={{ width: 360, borderLeft: "1px solid var(--border)", padding: "24px 20px",
          flexShrink: 0, overflowY: "auto", position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".85rem", fontWeight: 700, color: "var(--text)" }}>
                {editUser.name}
              </div>
              <div style={{ fontFamily: "var(--mono)", fontSize: ".68rem", color: "var(--muted)", marginTop: 2 }}>
                {editUser.email}
              </div>
            </div>
            <button onClick={() => setEditUser(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)",
                fontFamily: "var(--mono)", fontSize: "1rem", lineHeight: 1 }}>✕</button>
          </div>

          <div style={{ fontFamily: "var(--mono)", fontSize: ".65rem", color: "var(--muted)",
            marginBottom: 16, padding: "6px 10px", background: "var(--surface2)", borderRadius: 6,
            wordBreak: "break-all" }}>
            ID: {editUser.user_id}
          </div>

          <form onSubmit={handleUpdate}>
            <UserFormFields form={editForm} onChange={handleEditChange} />

            {editError && <div style={{ fontFamily: "var(--mono)", fontSize: ".72rem", color: "#ef4444",
              background: "var(--surface2)", border: "1px solid #ef4444", borderRadius: 6,
              padding: "8px 12px", marginBottom: 12 }}>{editError}</div>}

            {editOk && <div style={{ fontFamily: "var(--mono)", fontSize: ".72rem", color: "#10b981",
              background: "var(--surface2)", border: "1px solid #10b981", borderRadius: 6,
              padding: "8px 12px", marginBottom: 12 }}>✓ User updated successfully.</div>}

            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" disabled={editBusy} className="btn"
                style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#fff",
                  fontWeight: 700, flex: 1 }}>
                {editBusy ? "Saving…" : "Save Changes"}
              </button>
              <button type="button" className="btn" onClick={() => setEditUser(null)}>
                Cancel
              </button>
            </div>
          </form>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)",
            fontFamily: "var(--mono)", fontSize: ".65rem", color: "var(--muted)" }}>
            <div>Created: {editUser.created_date ? new Date(editUser.created_date).toLocaleString() : "—"}</div>
            {editUser.modified_date && <div>Modified: {new Date(editUser.modified_date).toLocaleString()}</div>}
          </div>
        </div>
      )}

      {/* ── Create User modal ── */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,.55)",
          display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border2)", borderRadius: 10,
            padding: "28px 28px 22px", width: 460, maxWidth: "95vw", boxShadow: "0 16px 48px rgba(0,0,0,.35)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontFamily: "var(--mono)", fontSize: ".85rem", fontWeight: 700,
                textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)", margin: 0 }}>
                New User
              </h3>
              <button onClick={() => setShowCreate(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)",
                  fontFamily: "var(--mono)", fontSize: "1rem" }}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <UserFormFields form={createForm} onChange={handleCreateChange} />
              {createError && <div style={{ fontFamily: "var(--mono)", fontSize: ".72rem", color: "#ef4444",
                background: "var(--surface2)", border: "1px solid #ef4444", borderRadius: 6,
                padding: "8px 12px", marginBottom: 12 }}>{createError}</div>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" disabled={createBusy} className="btn"
                  style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#fff", fontWeight: 700 }}>
                  {createBusy ? "Creating…" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── OnboardingTestTab (admin only) ────────────────────────────────────────────
const ONBOARDING_ENDPOINTS = [
  { label: "GET /v1/partners",       method: "GET",  path: "/api/onboarding/v1/partners" },
  { label: "GET /v1/flows",          method: "GET",  path: "/api/onboarding/v1/flows" },
  { label: "GET /v1/connections",    method: "GET",  path: "/api/onboarding/v1/connections" },
  { label: "GET /v1/routing-rules",  method: "GET",  path: "/api/onboarding/v1/routing-rules" },
  { label: "GET /v1/contacts",       method: "GET",  path: "/api/onboarding/v1/contacts" },
  { label: "GET /v1/tags",           method: "GET",  path: "/api/onboarding/v1/tags" },
  { label: "GET /v1/file-statuses",  method: "GET",  path: "/api/onboarding/v1/file-statuses" },
];

function OnboardingTestTab() {
  const [selected, setSelected]   = useState(ONBOARDING_ENDPOINTS[0]);
  const [response, setResponse]   = useState(null);
  const [status,   setStatus]     = useState(null);
  const [elapsed,  setElapsed]    = useState(null);
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState(null);

  async function runTest() {
    setLoading(true); setResponse(null); setError(null); setStatus(null); setElapsed(null);
    const t0 = Date.now();
    try {
      const r    = await apiFetch(API + selected.path);
      const ms   = Date.now() - t0;
      const text = await r.text();
      setStatus(r.status);
      setElapsed(ms);
      try { setResponse(JSON.parse(text)); } catch { setResponse(text); }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const statusColor = status >= 200 && status < 300 ? "#10b981" : "#ef4444";

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900 }}>
      <h2 style={{ fontFamily: "var(--mono)", fontSize: ".85rem", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)", marginBottom: 20 }}>
        Onboarding API — Test Console
      </h2>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <select
          value={selected.path}
          onChange={e => setSelected(ONBOARDING_ENDPOINTS.find(ep => ep.path === e.target.value))}
          style={{ fontFamily: "var(--mono)", fontSize: ".78rem", padding: "7px 10px",
            background: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border)",
            borderRadius: 6, minWidth: 320 }}
        >
          {ONBOARDING_ENDPOINTS.map(ep => (
            <option key={ep.path} value={ep.path}>{ep.label}</option>
          ))}
        </select>

        <button
          className="btn"
          onClick={runTest}
          disabled={loading}
          style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#fff",
            fontWeight: 600, minWidth: 90 }}
        >
          {loading ? "Running…" : "▶ Run"}
        </button>

        {status != null && (
          <span style={{ fontFamily: "var(--mono)", fontSize: ".78rem",
            color: statusColor, fontWeight: 700 }}>
            {status} · {elapsed}ms
          </span>
        )}
      </div>

      {error && (
        <div style={{ fontFamily: "var(--mono)", fontSize: ".75rem", color: "#ef4444",
          background: "var(--surface2)", border: "1px solid #ef4444", borderRadius: 8,
          padding: "12px 16px", marginBottom: 16 }}>
          {error}
        </div>
      )}

      {response != null && (
        <pre style={{ fontFamily: "var(--mono)", fontSize: ".72rem", color: "var(--text)",
          background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8,
          padding: "16px", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
          maxHeight: 520, overflowY: "auto", margin: 0 }}>
          {JSON.stringify(response, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ── DashboardTab ──────────────────────────────────────────────────────────────
function DashboardTab({ hours, from, to, refreshTick, isAdmin }) {
  const [data, setData]           = React.useState(null);
  const [loading, setLoading]     = React.useState(true);
  const [error, setError]         = React.useState(null);
  const [activity, setActivity]   = React.useState(null);
  const [actLoading, setActLoad]  = React.useState(true);

  React.useEffect(() => {
    setLoading(true); setError(null);
    apiFetch(API + "/api/dashboard?" + new URLSearchParams(rangeParams(hours, from, to)))
      .then(function(r) {
        if (!r.ok) return r.text().then(function(t) { throw new Error("HTTP " + r.status + ": " + t.slice(0,200)); });
        return r.json();
      })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function(e) { setError(e.message); setLoading(false); });
  }, [hours, from, to, refreshTick]);

  React.useEffect(() => {
    setActLoad(true);
    apiFetch(API + "/api/dashboard/activity?" + new URLSearchParams(rangeParams(hours, from, to)))
      .then(function(r) { return r.json(); })
      .then(function(d) { setActivity(d); setActLoad(false); })
      .catch(function() { setActivity({ available: false, reason: "Request failed" }); setActLoad(false); });
  }, [hours, from, to, refreshTick]);

  var PALETTE = {
    completed:  { top:"#34d399",bot:"#059669",glow:"rgba(5,150,105,0.20)"   },
    delivered:  { top:"#6ee7b7",bot:"#10b981",glow:"rgba(16,185,129,0.20)"  },
    failed:     { top:"#f87171",bot:"#dc2626",glow:"rgba(220,38,38,0.20)"   },
    error:      { top:"#f87171",bot:"#dc2626",glow:"rgba(220,38,38,0.20)"   },
    processing: { top:"#60a5fa",bot:"#2563eb",glow:"rgba(37,99,235,0.20)"   },
    pending:    { top:"#fbbf24",bot:"#d97706",glow:"rgba(217,119,6,0.20)"   },
    queued:     { top:"#a78bfa",bot:"#7c3aed",glow:"rgba(124,58,237,0.20)"  },
    cancelled:  { top:"#94a3b8",bot:"#475569",glow:"rgba(71,85,105,0.20)"   },
    retrying:   { top:"#fb923c",bot:"#ea580c",glow:"rgba(234,88,12,0.20)"   },
  };
  var EXTRA=[
    {top:"#38bdf8",bot:"#0284c7",glow:"rgba(2,132,199,0.20)"},
    {top:"#f472b6",bot:"#db2777",glow:"rgba(219,39,119,0.20)"},
    {top:"#a3e635",bot:"#65a30d",glow:"rgba(101,163,13,0.20)"},
  ];
  var _ei=0,_dp={};
  function pal(st) {
    var k=(st||"").toLowerCase();
    if (PALETTE[k]) return PALETTE[k];
    if (!_dp[k]) { _dp[k]=EXTRA[_ei%EXTRA.length]; _ei++; }
    return _dp[k];
  }
  function bot(st) { return pal(st).bot; }

  // ── BarChart ────────────────────────────────────────────────────────────────
  // Labels are absolutely-positioned HTML divs centred exactly on each bar's
  // x-coordinate, so they always align and can wrap onto multiple lines.
  function BarChart(props) {
    var slices = props.slices;
    var ref = React.useRef(null);
    var _s = React.useState(400); var w = _s[0]; var setW = _s[1];
    React.useEffect(function() {
      if (!ref.current) return;
      var ro = new ResizeObserver(function(e) { if (e[0]) setW(e[0].contentRect.width || 400); });
      ro.observe(ref.current);
      setW(ref.current.offsetWidth || 400);
      return function() { ro.disconnect(); };
    }, []);

    if (!slices || !slices.length) {
      return <div ref={ref} style={{height:160,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--muted)",fontFamily:"var(--mono)",fontSize:".72rem"}}>No data</div>;
    }

    // Layout constants
    var H=170, pL=44, pR=12, pT=22, pB=8;
    var plW=w-pL-pR, plH=H-pT-pB, n=slices.length;
    var bW=Math.max(16, Math.min(52, Math.floor(plW/n)-10));
    var gW=n>1?(plW-bW*n)/(n+1):(plW-bW)/2;
    var max=Math.max.apply(null, slices.map(function(d){return Number(d.count);}).concat([1]));

    // Centre x of each bar in SVG coordinate space
    var centres=slices.map(function(d,i){
      return pL + (n>1 ? gW+i*(bW+gW) : (plW-bW)/2) + bW/2;
    });

    var grid=[0.25,0.5,0.75,1].map(function(p,i){
      var y=pT+plH-plH*p, val=Math.round(max*p);
      return <g key={i}>
        <line x1={pL} y1={y} x2={pL+plW} y2={y} stroke="var(--border)" strokeWidth="1" strokeDasharray="3 5"/>
        <text x={pL-5} y={y+3.5} textAnchor="end" style={{fontFamily:"var(--mono)",fontSize:"8px",fill:"var(--muted)"}}>
          {val>=1000?(val/1000).toFixed(1)+"k":val}
        </text>
      </g>;
    });

    var bars=slices.map(function(d,i){
      var p=pal(d.status), cnt=Number(d.count), bh=Math.max(4,Math.round(cnt/max*plH));
      var x=centres[i]-bW/2, y=pT+plH-bh;
      var gid="bg"+i+"_"+d.status.replace(/[^a-z0-9]/g,"");
      return <g key={i}>
        <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={p.top}/><stop offset="100%" stopColor={p.bot}/></linearGradient></defs>
        <rect x={x+3} y={y+5} width={bW} height={bh} fill={p.glow} rx="4" filter="url(#dbblur)"/>
        <rect x={x} y={y} width={bW} height={bh} fill={"url(#"+gid+")"} rx="4"/>
        <text x={centres[i]} y={y-5} textAnchor="middle" style={{fontFamily:"var(--mono)",fontSize:"8.5px",fontWeight:700,fill:"var(--text)"}}>
          {cnt>=1000?(cnt/1000).toFixed(1)+"k":cnt.toLocaleString()}
        </text>
      </g>;
    });

    // Label width: use bar width so adjacent labels don't overlap
    var labelW = bW + Math.max(0, gW - 4);

    return (
      <div ref={ref} style={{width:"100%"}}>
        <svg width={w} height={H} style={{display:"block",overflow:"visible"}}>
          <defs><filter id="dbblur" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="4"/></filter></defs>
          <line x1={pL} y1={pT+plH} x2={pL+plW} y2={pT+plH} stroke="var(--border)" strokeWidth="1.5"/>
          <line x1={pL} y1={pT} x2={pL} y2={pT+plH} stroke="var(--border)" strokeWidth="1.5"/>
          {grid}{bars}
        </svg>
        {/* Absolutely-positioned labels centred exactly on each bar */}
        <div style={{position:"relative", height:36, marginTop:4}}>
          {slices.map(function(d,i){
            return (
              <div key={i} style={{
                position:"absolute",
                left: centres[i],
                transform: "translateX(-50%)",
                width: labelW,
                textAlign: "center",
                fontFamily: "var(--mono)",
                fontSize: ".63rem",
                color: "var(--muted)",
                textTransform: "capitalize",
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}>
                {d.status}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function PieChart(props) {
    var slices=props.slices, size=props.size||140;
    var total=slices.reduce(function(a,d){return a+Number(d.count);},0);
    if (!total) return <div style={{width:size,height:size,borderRadius:"50%",background:"var(--surface2)",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--muted)",fontSize:".72rem"}}>No data</div>;
    var r=size/2,cum=-Math.PI/2;
    return <svg width={size} height={size} viewBox={"0 0 "+size+" "+size}>
      {slices.map(function(d,i){
        var ang=Number(d.count)/total*2*Math.PI;
        var x1=r+r*Math.cos(cum),y1=r+r*Math.sin(cum); cum+=ang;
        var x2=r+r*Math.cos(cum),y2=r+r*Math.sin(cum);
        var p=pal(d.status),gid="pg"+i+"_"+d.status.replace(/[^a-z0-9]/g,"");
        return <g key={i}>
          <defs><linearGradient id={gid} x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={p.top}/><stop offset="100%" stopColor={p.bot}/></linearGradient></defs>
          <path d={"M "+r+" "+r+" L "+x1+" "+y1+" A "+r+" "+r+" 0 "+(ang>Math.PI?1:0)+" 1 "+x2+" "+y2+" Z"} fill={"url(#"+gid+")"} stroke="var(--surface)" strokeWidth="2.5"/>
        </g>;
      })}
    </svg>;
  }

  function Legend(props) {
    if (!props.slices||!props.slices.length) return <span style={{fontFamily:"var(--mono)",fontSize:".67rem",color:"var(--muted)"}}>No data</span>;
    return <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {props.slices.map(function(d,i){
        return <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:10,height:10,borderRadius:2,background:bot(d.status),flexShrink:0}}/>
          <span style={{fontFamily:"var(--mono)",fontSize:".68rem",color:"var(--text)",textTransform:"capitalize",flex:1}}>{d.status}</span>
          <span style={{fontFamily:"var(--mono)",fontSize:".68rem",color:"var(--muted)",fontWeight:600}}>{Number(d.count).toLocaleString()}</span>
        </div>;
      })}
    </div>;
  }

  function StatCard(props) {
    return <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,
      padding:"18px 22px",display:"flex",flexDirection:"column",gap:5,flex:1,minWidth:170,
      position:"relative",overflow:"hidden"}}>
      {props.accent && <div style={{position:"absolute",top:0,left:0,right:0,height:3,
        borderRadius:"10px 10px 0 0",background:"linear-gradient(90deg,"+props.accent+",transparent)"}}/>}
      <div style={{fontFamily:"var(--mono)",fontSize:".6rem",color:"var(--muted)",textTransform:"uppercase",letterSpacing:".1em"}}>{props.label}</div>
      <div style={{fontFamily:"var(--mono)",fontSize:"2rem",fontWeight:700,color:props.accent||"var(--text)",lineHeight:1}}>{props.value!=null?props.value:"—"}</div>
      {props.sub && <div style={{fontFamily:"var(--mono)",fontSize:".66rem",color:"var(--muted)"}}>{props.sub}</div>}
    </div>;
  }

  function ChartCard(props) {
    return <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,
      padding:"18px 22px",flex:props.flex||1,minWidth:props.minWidth||220}}>
      <div style={{fontFamily:"var(--mono)",fontSize:".6rem",color:"var(--muted)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:16}}>{props.label}</div>
      {props.children}
    </div>;
  }

  function SectionLabel(props) {
    return <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
      <div style={{width:3,height:14,borderRadius:2,background:"var(--accent)",flexShrink:0}}/>
      <span style={{fontFamily:"var(--mono)",fontSize:".67rem",fontWeight:700,textTransform:"uppercase",letterSpacing:".12em",color:"var(--accent)"}}>{props.children}</span>
    </div>;
  }

  if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:300,color:"var(--muted)",fontFamily:"var(--mono)",fontSize:".8rem"}}>Loading dashboard...</div>;
  if (error) return <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:300,gap:8}}>
    <div style={{color:"#dc2626",fontFamily:"var(--mono)",fontSize:".8rem",fontWeight:700}}>Dashboard Error</div>
    <div style={{color:"var(--muted)",fontFamily:"var(--mono)",fontSize:".72rem",maxWidth:500,textAlign:"center"}}>{error}</div>
  </div>;

  var usedPct=data.routing_rules_total>0?Math.round(data.routing_rules_utilized/data.routing_rules_total*100):0;
  var opt=TIME_OPTIONS.find(function(o){return o.hours===hours;});
  var periodLabel=from?fmtRangeDate(from)+" – "+fmtRangeDate(to):(opt?opt.label:"Last "+hours+" hours");

  function fmtSeconds(s) {
    if (s == null) return "—";
    if (s < 60) return s + "s";
    if (s < 3600) return Math.round(s/60) + "m";
    return (s/3600).toFixed(1) + "h";
  }

  function RankList(props) {
    var rows = props.rows || [];
    if (!rows.length) return <div style={{fontFamily:"var(--mono)",fontSize:".72rem",color:"var(--muted)"}}>No data</div>;
    var max = Math.max.apply(null, rows.map(function(r){return Number(r.count);}));
    return <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {rows.map(function(r,i){
        var pct = max > 0 ? Math.round(Number(r.count)/max*100) : 0;
        var label = props.labelFn ? props.labelFn(r) : (r.label || r.tab || "");
        return <div key={i} style={{display:"flex",flexDirection:"column",gap:3}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontFamily:"var(--mono)",fontSize:".68rem",color:"var(--text)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"80%"}}>{label}</span>
            <span style={{fontFamily:"var(--mono)",fontSize:".68rem",color:"var(--muted)",fontWeight:700,flexShrink:0,marginLeft:8}}>{Number(r.count).toLocaleString()}</span>
          </div>
          <div style={{height:4,borderRadius:2,background:"var(--border)",overflow:"hidden"}}>
            <div style={{height:"100%",width:pct+"%",borderRadius:2,background:props.barColor||"var(--accent)",transition:"width .4s"}}/>
          </div>
        </div>;
      })}
    </div>;
  }

  return (
    <div className="tab-body" style={{display:"flex",flexDirection:"column",gap:28}}>
      <section>
        <SectionLabel>Files Processed — {periodLabel}</SectionLabel>
        <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:20}}>
          <StatCard label="Files Processed" value={data.files_period.toLocaleString()}
            sub={periodLabel} accent="var(--accent)"/>
        </div>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <ChartCard label="Status Breakdown" flex="0 0 auto" minWidth={230}>
            <div style={{display:"flex",gap:20,alignItems:"center"}}>
              <PieChart slices={data.files_by_status_period} size={140}/>
              <Legend slices={data.files_by_status_period}/>
            </div>
          </ChartCard>
          <ChartCard label="Files by Status">
            <BarChart slices={data.files_by_status_period}/>
          </ChartCard>
        </div>
      </section>

      <div style={{borderTop:"1px solid var(--border)"}}/>

      <section>
        <SectionLabel>Lifetime</SectionLabel>
        <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
          <StatCard label="Total Files Processed"
            value={data.files_lifetime.toLocaleString()} sub="all time" accent="#8b5cf6"/>
          <StatCard label="Pipelines Configured"
            value={data.pipelines_total.toLocaleString()} sub="active routing rules" accent="#0ea5e9"/>
          <StatCard label="Pipelines Active"
            value={data.routing_rules_utilized.toLocaleString()}
            sub={usedPct+"% of "+data.routing_rules_total.toLocaleString()+" total pipelines"} accent="#f59e0b"/>
        </div>
      </section>

      <div style={{borderTop:"1px solid var(--border)"}}/>

      {/* ── Phase 1: File Health (admin only) ── */}
      {isAdmin && <section>
        <SectionLabel>File Health — {periodLabel}</SectionLabel>
        <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:20}}>
          <StatCard label="Failure Rate" accent="#dc2626"
            value={(data.failure_rate != null ? data.failure_rate : "—") + (data.failure_rate != null ? "%" : "")}
            sub={data.failed_count != null ? data.failed_count.toLocaleString() + " of " + (data.total_count||0).toLocaleString() + " files" : ""}/>
          <StatCard label="Stuck Files" accent="#f59e0b"
            value={data.stuck_files != null ? data.stuck_files.toLocaleString() : "—"}
            sub="non-terminal, stale 2+ hours"/>
          <StatCard label="Avg Processing Time" accent="#10b981"
            value={fmtSeconds(data.avg_processing_seconds)}
            sub="delivered / completed files"/>
        </div>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <ChartCard label="Top Failing Partners" flex="1" minWidth={240}>
            <RankList rows={data.top_failing_partners} barColor="#dc2626"/>
          </ChartCard>
          <ChartCard label="Top Active Pipelines" flex="1" minWidth={280}>
            <RankList rows={data.top_active_pipelines}
              labelFn={function(r){return (r.inbound||"?")+" → "+(r.outbound||"?");}}
              barColor="#0ea5e9"/>
          </ChartCard>
        </div>
      </section>}

      {isAdmin && <div style={{borderTop:"1px solid var(--border)"}}/>}

      {/* ── Phase 2: User Activity + AD Users (admin only) ── */}
      {isAdmin && <section>
        <SectionLabel>User Activity — {periodLabel}</SectionLabel>
        {actLoading && <div style={{fontFamily:"var(--mono)",fontSize:".75rem",color:"var(--muted)"}}>Loading activity data…</div>}
        {!actLoading && activity && !activity.available && (
          <div style={{fontFamily:"var(--mono)",fontSize:".72rem",color:"var(--muted)",padding:"12px 16px",background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:8}}>
            App Insights telemetry unavailable: {activity.reason}
          </div>
        )}
        {!actLoading && activity && activity.available && (<>
          <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:20}}>
            <StatCard label="Active Users" accent="#8b5cf6"
              value={activity.active_users != null ? activity.active_users.toLocaleString() : "—"}
              sub="unique logins"/>
            <StatCard label="Reprocess Actions" accent="#f59e0b"
              value={activity.reprocess_actions != null ? activity.reprocess_actions.toLocaleString() : "—"}
              sub="confirmed batches"/>
            <StatCard label="Files Reprocessed" accent="#dc2626"
              value={activity.files_reprocessed != null ? activity.files_reprocessed.toLocaleString() : "—"}
              sub="total files reset"/>
            <StatCard label="File Downloads" accent="#10b981"
              value={activity.downloads != null ? activity.downloads.toLocaleString() : "—"}
              sub="download events"/>
          </div>

          <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:20}}>
            {activity.tab_usage && activity.tab_usage.length > 0 && (
              <ChartCard label="Tab Usage" flex="0 1 320px" minWidth={220}>
                <RankList rows={activity.tab_usage} labelFn={function(r){return r.label||r.tab||"";}} barColor="#8b5cf6"/>
              </ChartCard>
            )}
          </div>

          {/* ── User table ── */}
          {activity.users && activity.users.length > 0 && (
            <UserActivityTable users={activity.users} hours={hours} from={from} to={to}/>
          )}
        </>)}
      </section>}
    </div>
  );
}

// ── UserActivityTable + drill-down panel ──────────────────────────────────────
function UserActivityTable({ users, hours, from, to }) {
  var [selectedUpn, setSelectedUpn] = React.useState(null);
  var [drill, setDrill]             = React.useState(null);
  var [drillLoading, setDrillLoad]  = React.useState(false);
  var [drillError, setDrillErr]     = React.useState(null);

  function openDrill(upn) {
    if (selectedUpn === upn) { setSelectedUpn(null); setDrill(null); return; }
    setSelectedUpn(upn);
    setDrill(null);
    setDrillErr(null);
    setDrillLoad(true);
    apiFetch(API + "/api/dashboard/users/" + encodeURIComponent(upn) + "?" + new URLSearchParams(from ? rangeParams(hours, from, to) : { hours: Math.max(hours, 168) }))
      .then(function(r) { return r.json(); })
      .then(function(d) { setDrill(d); setDrillLoad(false); })
      .catch(function(e) { setDrillErr(e.message); setDrillLoad(false); });
  }

  function fmtTs(ts) {
    if (!ts) return "—";
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  }

  var TH = { fontFamily:"var(--mono)", fontSize:".62rem", fontWeight:700, textTransform:"uppercase",
    letterSpacing:".08em", color:"var(--muted)", padding:"6px 12px", textAlign:"left",
    borderBottom:"1px solid var(--border)", whiteSpace:"nowrap" };
  var TD = { fontFamily:"var(--mono)", fontSize:".7rem", color:"var(--text)",
    padding:"7px 12px", borderBottom:"1px solid var(--border)", verticalAlign:"middle" };
  var TDm = Object.assign({}, TD, { color:"var(--muted)" });

  return (
    <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,overflow:"hidden"}}>
      <div style={{padding:"14px 18px 10px",fontFamily:"var(--mono)",fontSize:".6rem",fontWeight:700,
        textTransform:"uppercase",letterSpacing:".12em",color:"var(--muted)"}}>
        AD Users ({users.length})
      </div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"auto"}}>
          <thead>
            <tr>
              <th style={TH}>User (UPN)</th>
              <th style={Object.assign({},TH,{textAlign:"center"})}>Logins</th>
              <th style={Object.assign({},TH,{textAlign:"center"})}>Tab Views</th>
              <th style={Object.assign({},TH,{textAlign:"center"})}>Downloads</th>
              <th style={Object.assign({},TH,{textAlign:"center"})}>Reprocesses</th>
              <th style={TH}>Last Seen</th>
              <th style={{...TH,width:32}}></th>
            </tr>
          </thead>
          <tbody>
            {users.map(function(u, i) {
              var isOpen = selectedUpn === u.upn;
              return [
                <tr key={u.upn}
                  style={{cursor:"pointer", background: isOpen ? "rgba(139,92,246,0.08)" : (i%2===0?"transparent":"rgba(255,255,255,0.02)"),
                    transition:"background .15s"}}
                  onClick={function(){openDrill(u.upn);}}>
                  <td style={TD}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:24,height:24,borderRadius:"50%",background:"linear-gradient(135deg,#8b5cf6,#6d28d9)",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontFamily:"var(--mono)",fontSize:".65rem",fontWeight:700,color:"#fff",flexShrink:0}}>
                        {(u.upn||"?")[0].toUpperCase()}
                      </div>
                      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:260}}>{u.upn}</span>
                    </div>
                  </td>
                  <td style={{...TDm,textAlign:"center"}}>{u.login_count||0}</td>
                  <td style={{...TDm,textAlign:"center"}}>{u.tab_views||0}</td>
                  <td style={{...TDm,textAlign:"center"}}>{u.downloads||0}</td>
                  <td style={{...TDm,textAlign:"center",color:u.reprocesses>0?"#f59e0b":"var(--muted)"}}>{u.reprocesses||0}</td>
                  <td style={TDm}>{fmtTs(u.last_seen)}</td>
                  <td style={{...TD,textAlign:"center",color:"var(--muted)",fontSize:".8rem",userSelect:"none"}}>
                    {isOpen ? "▲" : "▼"}
                  </td>
                </tr>,
                isOpen && (
                  <tr key={u.upn+"_drill"}>
                    <td colSpan={7} style={{padding:0,background:"var(--surface)"}}>
                      <UserDrillPanel upn={u.upn} loading={drillLoading} error={drillError} data={drill}/>
                    </td>
                  </tr>
                )
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UserDrillPanel({ upn, loading, error, data }) {
  function fmtTs(ts) {
    if (!ts) return "—";
    try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
  }

  if (loading) return (
    <div style={{padding:"20px 24px",fontFamily:"var(--mono)",fontSize:".75rem",color:"var(--muted)"}}>
      Loading details for {upn}…
    </div>
  );
  if (error) return (
    <div style={{padding:"16px 24px",fontFamily:"var(--mono)",fontSize:".72rem",color:"#dc2626"}}>
      Error: {error}
    </div>
  );
  if (!data || !data.available) return (
    <div style={{padding:"16px 24px",fontFamily:"var(--mono)",fontSize:".72rem",color:"var(--muted)"}}>
      {data ? data.reason : "No data"}
    </div>
  );

  var sectionStyle = { padding:"16px 24px 20px", borderTop:"1px solid var(--border)" };
  var labelStyle   = { fontFamily:"var(--mono)", fontSize:".58rem", fontWeight:700, textTransform:"uppercase",
    letterSpacing:".1em", color:"var(--accent)", marginBottom:10 };
  var TD2 = { fontFamily:"var(--mono)", fontSize:".69rem", color:"var(--text)", padding:"5px 10px",
    borderBottom:"1px solid var(--border)" };
  var TDm2 = Object.assign({}, TD2, { color:"var(--muted)" });
  var TH2  = Object.assign({}, TD2, { color:"var(--muted)", fontSize:".6rem", fontWeight:700,
    textTransform:"uppercase", letterSpacing:".07em" });

  return (
    <div>

      {/* Event breakdown */}
      {data.event_breakdown && data.event_breakdown.length > 0 && (
        <div style={sectionStyle}>
          <div style={labelStyle}>Event Breakdown</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {data.event_breakdown.map(function(e,i){
              return <div key={i} style={{background:"var(--surface2)",border:"1px solid var(--border)",
                borderRadius:7,padding:"8px 14px",display:"flex",flexDirection:"column",gap:2,minWidth:100}}>
                <div style={{fontFamily:"var(--mono)",fontSize:".62rem",color:"var(--muted)",textTransform:"capitalize"}}>{e.event_type||e.name}</div>
                <div style={{fontFamily:"var(--mono)",fontSize:"1.3rem",fontWeight:700,color:"var(--text)",lineHeight:1}}>{Number(e.count).toLocaleString()}</div>
              </div>;
            })}
          </div>
        </div>
      )}

      {/* Reprocessed files */}
      {data.reprocessed_files && data.reprocessed_files.length > 0 && (
        <div style={sectionStyle}>
          <div style={labelStyle}>Reprocessed Files ({data.reprocessed_files.length})</div>
          <div style={{border:"1px solid var(--border)",borderRadius:6,overflow:"hidden"}}>
            {/* Fixed header — outside the scroll container so it never moves */}
            <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
              <colgroup>
                <col style={{width:"32%"}}/><col style={{width:"22%"}}/><col style={{width:"28%"}}/><col style={{width:"18%"}}/>
              </colgroup>
              <thead>
                <tr style={{background:"var(--surface2)"}}>
                  <th style={TH2}>File Name</th>
                  <th style={TH2}>File Index ID</th>
                  <th style={TH2}>Correlation ID</th>
                  <th style={TH2}>Timestamp</th>
                </tr>
              </thead>
            </table>
            {/* Scrollable body — separate from header */}
            <div style={{maxHeight:200,overflowY:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
                <colgroup>
                  <col style={{width:"32%"}}/><col style={{width:"22%"}}/><col style={{width:"28%"}}/><col style={{width:"18%"}}/>
                </colgroup>
                <tbody>
                  {data.reprocessed_files.map(function(f,i){
                    return <tr key={i} style={{background:i%2===0?"transparent":"rgba(255,255,255,0.02)"}}>
                      <td style={{...TD2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={f.file_name||""}>{f.file_name||"—"}</td>
                      <td style={{...TDm2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.file_index_id||"—"}</td>
                      <td style={{...TDm2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.tracking_correlation_id||"—"}</td>
                      <td style={TDm2}>{fmtTs(f.timestamp)}</td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recent events timeline */}
      {data.recent_events && data.recent_events.length > 0 && (
        <div style={sectionStyle}>
          <div style={labelStyle}>Recent Events (last {data.recent_events.length})</div>
          <div style={{border:"1px solid var(--border)",borderRadius:6,overflow:"hidden"}}>
            {/* Fixed header */}
            <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
              <colgroup>
                <col style={{width:"18%"}}/><col style={{width:"18%"}}/><col style={{width:"64%"}}/>
              </colgroup>
              <thead>
                <tr style={{background:"var(--surface2)"}}>
                  <th style={TH2}>Timestamp</th>
                  <th style={TH2}>Event</th>
                  <th style={TH2}>Details</th>
                </tr>
              </thead>
            </table>
            {/* Scrollable body */}
            <div style={{maxHeight:220,overflowY:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
                <colgroup>
                  <col style={{width:"18%"}}/><col style={{width:"18%"}}/><col style={{width:"64%"}}/>
                </colgroup>
                <tbody>
                  {data.recent_events.map(function(e,i){
                    var dims = {};
                    try { dims = JSON.parse(e.props || "{}"); } catch(_) {}
                    var details = Object.entries(dims)
                      .filter(function(kv){return kv[0]!=="upn"&&kv[0]!=="triggeredBy"&&kv[0]!=="oid";})
                      .map(function(kv){return kv[0]+": "+kv[1];}).join(" · ");
                    return <tr key={i} style={{background:i%2===0?"transparent":"rgba(255,255,255,0.02)"}}>
                      <td style={TDm2}>{fmtTs(e.timestamp)}</td>
                      <td style={TD2}>{e.event_name||e.name}</td>
                      <td style={{...TDm2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                        title={details}>{details||"—"}</td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function safeStr(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}
export default function App() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  _msalInstance = instance;

  // Fetch access profile once user is authenticated
  const [accessProfile, setAccessProfile] = React.useState({ role: "none", partners: [] });
  React.useEffect(function() {
    if (!isAuthenticated) return;
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    const oid = account?.idTokenClaims?.oid || account?.localAccountId || "unknown";
    const upn = account?.username || "unknown";

    // Set AI user context and track login
    aiSetUser(oid, upn);
    trackEvent("UserLogin", { oid, upn, environment: ENV_LABEL || "unknown" });

    apiFetch(API + "/api/me")
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d && d.access) setAccessProfile(d.access);
      })
      .catch(function(e) { console.warn("[useAccess] failed:", e); });
  }, [isAuthenticated]);

  var isAdmin    = accessProfile.role === "admin";
  var isReadonly = accessProfile.role === "admin" || accessProfile.role === "readonly";
  var isPartner  = accessProfile.role === "partner";

  var isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  var handleLogin = function() {
    console.log("[MSAL] handleLogin called, isLocalhost:", isLocalhost);
    if (isLocalhost) {
      instance.loginPopup(loginRequest).then(function(authResult) {
        console.log("[MSAL] Popup success:", authResult);
        if (authResult && authResult.account) {
          instance.setActiveAccount(authResult.account);
          console.log("[MSAL] Active account set:", authResult.account.username);
        }
        window.location.reload();
      }).catch(function(err) {
        console.error("[MSAL] Popup error:", err);
      });
    } else {
      instance.loginRedirect(loginRequest);
    }
  };

  const [hours, setHoursRaw]       = useUrlState("hours", "24");
  const [rangeFrom, setRangeFrom]   = useUrlState("from", "");
  const [rangeTo, setRangeTo]       = useUrlState("to", "");
  const isCustom = hours === CUSTOM_RANGE && !!rangeFrom;
  // Switch preset or custom range. Custom range is cleared whenever a preset is chosen.
  const setHours = React.useCallback((h, f = "", t = "") => {
    if (h === CUSTOM_RANGE) {
      const now = new Date();
      setRangeFrom(f || new Date(now.getTime() - 24 * 3600000).toISOString());
      setRangeTo(t || now.toISOString());
    } else { setRangeFrom(""); setRangeTo(""); }
    setHoursRaw(h);
  }, [setRangeFrom, setRangeTo, setHoursRaw]);
  // Edit one end of the custom range, clamped to [now - 90 days, now] and keeping from < to
  const updateRange = (which, localValue) => {
    const d = new Date(localValue);
    if (!localValue || isNaN(d)) return;
    const now = Date.now(), floor = now - MAX_RANGE_DAYS * 86400000;
    let t = Math.min(Math.max(d.getTime(), floor), now);
    if (which === "from") setRangeFrom(new Date(Math.min(t, new Date(rangeTo).getTime() - 60000)).toISOString());
    else                  setRangeTo(new Date(Math.max(t, new Date(rangeFrom).getTime() + 60000)).toISOString());
  };
  const [tab, setTabRaw]            = useUrlState("tab",   "files");
  const setTab = React.useCallback((next) => {
    setTabRaw(next);
    trackEvent("TabView", { tab: next });
  }, [setTabRaw]);
  React.useEffect(() => { trackEvent("TabView", { tab }); }, []);
  const [darkMode, setDarkMode]     = useState(() => localStorage.getItem("theme") !== "light");
  const [refreshMs, setRefreshMs]   = useState(0);
  const [refreshTick, setRefreshTick] = useState(0); // increment to trigger child refresh
  const [globalSearch, setGlobalSearch] = useState("");
  const [gsResults, setGsResults]   = useState(null); // null = closed
  const [gsLoading, setGsLoading]   = useState(false);
  const dGlobal = useDebounce(globalSearch, 500);

  // Persist dark mode preference
  useEffect(() => {
    localStorage.setItem("theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  // Auto-refresh countdown
  const { countdown } = useAutoRefresh(
    () => setRefreshTick(t => t + 1),
    refreshMs,
    refreshMs > 0
  );

  // Global search — hits all endpoints simultaneously
  useEffect(() => {
    if (!dGlobal || dGlobal.length < 2) { setGsResults(null); return; }
    setGsLoading(true);
    const q = encodeURIComponent(dGlobal);
    Promise.all([
      apiFetch(`${API}/api/files?search=${q}&pageSize=5&hours=2160`).then(r=>r.json()).catch(()=>({data:[]})),
      apiFetch(`${API}/api/partners?search=${q}&pageSize=5`).then(r=>r.json()).catch(()=>({data:[]})),
      apiFetch(`${API}/api/flows?search=${q}&pageSize=5`).then(r=>r.json()).catch(()=>({data:[]})),
      apiFetch(`${API}/api/connections?search=${q}&pageSize=5`).then(r=>r.json()).catch(()=>({data:[]})),
      apiFetch(`${API}/api/pipelines?search=${q}&pageSize=5`).then(r=>r.json()).catch(()=>({data:[]})),
      apiFetch(`${API}/api/contacts?search=${q}&pageSize=5`).then(r=>r.json()).catch(()=>({data:[]})),
    ]).then(([files, partners, flows, connections, pipelines, contacts]) => {
      setGsResults({
        "Files":       { data: files.data,       total: files.total,       tab: "files"       },
        "Partners":    { data: partners.data,     total: partners.total,    tab: "partners"    },
        "Flows":       { data: flows.data,        total: flows.total,       tab: "flows"       },
        "Connections": { data: connections.data,  total: connections.total, tab: "connections" },
        "Pipelines":   { data: pipelines.data,    total: pipelines.total,   tab: "pipelines"   },
        "Contacts":    { data: contacts.data,     total: contacts.total,    tab: "contacts"    },
      });
      setGsLoading(false);
    });
  }, [dGlobal]);

  const handleGsSelect = (targetTab) => {
    setTab(targetTab);
    setGlobalSearch("");
    setGsResults(null);
  };

  // Primary label per section
  const GS_LABEL = {
    Files:       r => r.file_name || r.current_file_name_display || r.current_file_name,
    Partners:    r => r.partner_name,
    Flows:       r => r.flow_name,
    Connections: r => r.connection_name,
    Pipelines:   r => `${r.inbound_partner_name || "?"} → ${r.outbound_partner_name || "?"}`,
    Contacts:    r => maskName(r.name),
  };

  // ── Theme: Clean Corporate — Coke Red accent only ────────────────────────
  // Single unified palette (dark mode toggle kept but maps to same neutral base)
  const theme = darkMode ? {
    // Dark variant — neutral dark greys, no red tinting
    "--bg":        "#111318",
    "--surface":   "#1c1f26",
    "--surface2":  "#23272f",
    "--surface3":  "#2a2e38",
    "--border":    "#2e3340",
    "--border2":   "#3d4355",
    "--accent":    "#E8000D",   // Coke Red — used sparingly
    "--accent2":   "#ff4d52",   // lighter red for links/filenames
    "--dim":       "rgba(232,0,13,0.10)",
    "--text":      "#e8eaf0",
    "--text2":     "#9aa0b4",
    "--muted":     "#555d72",
    "--danger":    "#ff4d52",
    "--warn":      "#f59e0b",
    "--success":   "#10b981",
    "--fail-bg":   "rgba(255,77,82,0.07)",
    "--fail-bdr":  "rgba(255,77,82,0.35)",
    "--coke-red":  "#E8000D",
    "--coke-hdr":  "#1c1f26",   // dark header in dark mode
    "--status-delivered-text": "#34d399",
  } : {
    // Light variant — clean white/grey, Coke Red accent only
    "--bg":        "#f4f5f7",
    "--surface":   "#ffffff",
    "--surface2":  "#f8f9fb",
    "--surface3":  "#f0f2f5",
    "--border":    "#e2e5ec",
    "--border2":   "#c8cdd8",
    "--accent":    "#E8000D",   // Coke Red
    "--accent2":   "#c7000b",   // deeper red for hover/active
    "--dim":       "rgba(232,0,13,0.06)",
    "--text":      "#0f1623",
    "--text2":     "#4a5568",
    "--muted":     "#9aa3b2",
    "--danger":    "#dc2626",
    "--warn":      "#d97706",
    "--success":   "#059669",
    "--fail-bg":   "rgba(220,38,38,0.04)",
    "--fail-bdr":  "rgba(220,38,38,0.25)",
    "--coke-red":  "#E8000D",
    "--coke-hdr":  "#E8000D",   // red header in light mode
    "--status-delivered-text": "#047857",
  };

  const themeStyle = Object.entries(theme).map(([k,v]) => `${k}:${v}`).join(";");

  if (inProgress === "startup" || inProgress === "handleRedirect") {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: "100vh", background: "#0f1117",
        fontFamily: "'Inter', -apple-system, sans-serif",
        color: "#94a3b8", fontSize: ".9rem"
      }}>Loading...</div>
    );
  }

  // Suppress content until access profile is resolved (avoids flash of wrong data)
  if (isAuthenticated && accessProfile.role === "none" && !isPartner) {
    // Still loading access profile — show brief spinner
    // (role "none" with no partners means not yet fetched OR genuinely no access)
    // We give it a moment via the useEffect above before showing "no access"
  }

  if (!isAuthenticated) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "100vh",
        background: "#0f1117", gap: 28,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }}>
        {inProgress !== "none" ? (
          <div style={{ color: "#94a3b8", fontSize: ".9rem" }}>Signing in...</div>
        ) : (
          <>
            {/* Coca-Cola wordmark stripe */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "#E8000D", borderRadius: 8, padding: "8px 22px",
            }}>
              {/* Ribbon wave icon */}
              <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="20" cy="20" rx="18" ry="18" fill="white" fillOpacity="0.15"/>
                <path d="M8 20 Q14 12 20 20 Q26 28 32 20" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none"/>
              </svg>
              <span style={{ color: "#fff", fontWeight: 800, fontSize: "1.05rem", letterSpacing: ".03em" }}>
                The Coca‑Cola Company
              </span>
            </div>

            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#f1f5f9", marginBottom: 8, letterSpacing: "-.02em" }}>
                KO File Tracker Application
              </div>
              <div style={{ fontSize: ".9rem", color: "#64748b" }}>
                Sign in with your Microsoft work account to continue
              </div>
            </div>

            {/* Microsoft-branded sign-in button */}
            <button
              onClick={handleLogin}
              style={{
                display: "flex", alignItems: "center", gap: 0,
                background: "#fff", color: "#000",
                border: "1px solid #8c8c8c", borderRadius: 0,
                padding: 0, cursor: "pointer",
                fontFamily: "'Segoe UI', sans-serif",
                fontSize: ".94rem", fontWeight: 600,
                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                overflow: "hidden",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f3f3"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              {/* Microsoft logo block */}
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "10px 12px", background: "#fff" }}>
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
              </span>
              <span style={{ padding: "10px 20px 10px 8px", color: "#5e5e5e", fontWeight: 600, letterSpacing: ".01em" }}>
                Sign in with Coke Credentials
              </span>
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <AccessContext.Provider value={accessProfile}>
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;1,400&family=JetBrains+Mono:wght@400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root { ${themeStyle};
          --font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --mono: 'JetBrains Mono', 'Cascadia Code', monospace;
          --r:    6px;
          --r2:   10px;
          --ease: 0.13s ease;
          --shadow-sm: 0 1px 2px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04);
          --shadow:    0 2px 6px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04);
          --shadow-lg: 0 8px 28px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
        }
        body {
          background: var(--bg); color: var(--text);
          font-family: var(--font); font-size: 14px;
          line-height: 1.5; min-height: 100vh;
          -webkit-font-smoothing: antialiased;
          transition: background var(--ease), color var(--ease);
        }

        /* ── Header ── */
        .hdr {
          display: flex; align-items: center; gap: 0;
          height: 52px;
          background: var(--coke-hdr);
          position: sticky; top: 0; z-index: 200;
          box-shadow: 0 1px 0 rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.12);
        }
        /* Left brand block */
        .hdr-logo {
          display: flex; align-items: center; gap: 0;
          padding: 0 20px 0 24px;
          border-right: 1px solid rgba(255,255,255,0.15);
          height: 100%; flex-shrink: 0;
        }
        .hdr-wordmark {
          display: flex; flex-direction: column; gap: 0;
        }
        .hdr-wordmark-top {
          font-size: .62rem; font-weight: 600; letter-spacing: .14em;
          text-transform: uppercase; color: rgba(255,255,255,0.65);
          line-height: 1;
        }
        .hdr-wordmark-btm {
          font-size: .95rem; font-weight: 700; letter-spacing: -.01em;
          color: #ffffff; line-height: 1.2;
        }
        .hdr-wordmark-btm em { font-style: normal; font-weight: 400; color: rgba(255,255,255,0.75); }

        /* Live indicator */
        .hdr-pulse {
          width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
          background: #ffffff; margin: 0 14px 0 16px;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.2);
          animation: pulse 2.4s ease-in-out infinite;
        }
        @keyframes pulse { 0%,100% { opacity:1; box-shadow:0 0 0 2px rgba(255,255,255,0.2); }
                           50%      { opacity:.45; box-shadow:0 0 0 4px rgba(255,255,255,0.08); } }

        /* Global search */
        .hdr-search { flex: 1; max-width: 380px; position: relative; }
        .hdr-search-inp {
          width: 100%; height: 32px;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: var(--r);
          color: #fff; font-family: var(--font); font-size: .82rem;
          padding: 0 32px 0 34px; outline: none;
          transition: background var(--ease), border-color var(--ease), box-shadow var(--ease);
        }
        .hdr-search-inp::placeholder { color: rgba(255,255,255,0.5); }
        .hdr-search-inp:focus {
          background: rgba(255,255,255,0.22);
          border-color: rgba(255,255,255,0.55);
          box-shadow: 0 0 0 3px rgba(255,255,255,0.1);
        }
        .hdr-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:rgba(255,255,255,0.55); pointer-events:none; display:flex; }
        .hdr-search-clear { position:absolute; right:8px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:rgba(255,255,255,0.55); display:flex; padding:2px; }
        .hdr-search-clear:hover { color:#fff; }

        /* Global search dropdown */
        .gs-dropdown {
          position:absolute; top:calc(100% + 6px); left:0; right:0;
          background:var(--surface);
          border:1px solid var(--border);
          border-radius:var(--r2);
          box-shadow:var(--shadow-lg);
          z-index:300; overflow:hidden;
          animation:dropIn .13s ease;
        }
        @keyframes dropIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:none} }
        .gs-section { border-bottom:1px solid var(--border); }
        .gs-section:last-child { border-bottom:none; }
        .gs-section-hdr {
          display:flex; align-items:center; justify-content:space-between;
          padding:7px 14px 5px;
          font-size:.68rem; font-weight:600; text-transform:uppercase;
          letter-spacing:.07em; color:var(--muted);
        }
        .gs-section-hdr button { background:none; border:none; cursor:pointer; font-size:.72rem; color:var(--accent); font-weight:600; padding:0; }
        .gs-section-hdr button:hover { text-decoration:underline; }
        .gs-row {
          display:flex; align-items:center; gap:9px;
          padding:7px 14px; cursor:pointer; font-size:.82rem; color:var(--text2);
          transition:background var(--ease);
        }
        .gs-row:hover { background:var(--surface2); color:var(--text); }
        .gs-dot { width:5px; height:5px; border-radius:50%; background:var(--accent); flex-shrink:0; }
        .gs-empty { padding:20px; text-align:center; color:var(--muted); font-size:.82rem; }

        /* Header right controls */
        .hdr-controls { display:flex; align-items:center; gap:8px; padding:0 20px 0 16px; margin-left:auto; }

        /* Refresh ring */
        .refresh-ring { position:relative; width:28px; height:28px; flex-shrink:0; }
        .refresh-ring svg { transform:rotate(-90deg); }
        .refresh-ring-bg { fill:none; stroke:rgba(255,255,255,0.2); stroke-width:2.5; }
        .refresh-ring-fg { fill:none; stroke:rgba(255,255,255,0.85); stroke-width:2.5; stroke-linecap:round; transition:stroke-dashoffset .95s linear; }
        .refresh-ring-num { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-family:var(--mono); font-size:.56rem; font-weight:600; color:rgba(255,255,255,0.85); }

        /* Mode toggle */
        .mode-btn {
          width:32px; height:32px; border-radius:var(--r);
          border:1px solid rgba(255,255,255,0.25);
          background:rgba(255,255,255,0.1);
          cursor:pointer; display:flex; align-items:center; justify-content:center;
          font-size:14px; transition:background var(--ease), border-color var(--ease); flex-shrink:0;
        }
        .mode-btn:hover { background:rgba(255,255,255,0.2); border-color:rgba(255,255,255,0.45); }

        /* sel-time in header */
        .sel-time {
          appearance:none; height:32px;
          background:rgba(255,255,255,0.12); border:1px solid rgba(255,255,255,0.25);
          border-radius:var(--r); color:#fff;
          font-family:var(--font); font-size:.78rem; font-weight:500;
          padding:0 26px 0 9px; cursor:pointer; outline:none;
          transition:background var(--ease), border-color var(--ease);
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='5' viewBox='0 0 9 5'%3E%3Cpath d='M1 1l3.5 3 3.5-3' stroke='rgba(255,255,255,0.6)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat:no-repeat; background-position:right 8px center;
        }
        .sel-time option { background:#1c1f26; color:#e8eaf0; }
        .sel-time:hover,.sel-time:focus { background:rgba(255,255,255,0.2); border-color:rgba(255,255,255,0.5); }

        /* ── Tab nav ── */
        .tab-nav {
          display:flex; align-items:center;
          padding:0 24px;
          background:var(--surface);
          border-bottom:1px solid var(--border);
          overflow-x:auto; overflow-y:hidden;
          box-shadow:var(--shadow-sm);
        }
        .tab-nav::-webkit-scrollbar { display:none; }
        .tab-btn {
          background:none; border:none; border-bottom:2px solid transparent;
          padding:14px 16px; margin-bottom:-1px;
          font-family:var(--font); font-size:.82rem; font-weight:500;
          color:var(--muted); cursor:pointer; white-space:nowrap;
          transition:color var(--ease), border-color var(--ease);
          letter-spacing:0;
        }
        .tab-btn:hover { color:var(--text); }
        .tab-btn.active { color:var(--accent); border-bottom-color:var(--accent); font-weight:600; }

        /* ── Filter bar ── */
        .filter-bar {
          display:flex; align-items:center; gap:8px; flex-wrap:wrap;
          padding:12px 24px;
          background:var(--surface);
          border-bottom:1px solid var(--border);
        }
        .input-wrap { position:relative; flex:1; min-width:200px; }
        .input-wrap.narrow { flex:0 1 170px; min-width:130px; }
        .input-icon { position:absolute; left:9px; top:50%; transform:translateY(-50%); color:var(--muted); display:flex; pointer-events:none; }
        .inp {
          width:100%; height:32px;
          background:var(--surface2); border:1px solid var(--border);
          border-radius:var(--r); color:var(--text);
          font-family:var(--font); font-size:.82rem;
          padding:0 26px 0 30px; outline:none;
          transition:border-color var(--ease), box-shadow var(--ease);
        }
        .inp::placeholder { color:var(--muted); }
        .inp:focus { border-color:var(--accent); box-shadow:0 0 0 3px var(--dim); background:var(--surface); }
        .clear-btn {
          position:absolute; right:7px; top:50%; transform:translateY(-50%);
          background:none; border:none; cursor:pointer;
          color:var(--muted); display:flex; padding:2px;
          transition:color var(--ease);
        }
        .clear-btn:hover { color:var(--text); }
        .select-wrap { position:relative; }
        .sel {
          appearance:none; height:32px;
          background:var(--surface2); border:1px solid var(--border);
          border-radius:var(--r); color:var(--text);
          font-family:var(--font); font-size:.82rem; font-weight:400;
          padding:0 28px 0 10px; cursor:pointer; outline:none;
          transition:border-color var(--ease), box-shadow var(--ease);
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='5' viewBox='0 0 9 5'%3E%3Cpath d='M1 1l3.5 3 3.5-3' stroke='%239aa3b2' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat:no-repeat; background-position:right 9px center;
        }
        .sel:hover,.sel:focus { border-color:var(--accent); box-shadow:0 0 0 3px var(--dim); }
        .btn {
          display:inline-flex; align-items:center; gap:5px; height:32px;
          background:var(--surface2); border:1px solid var(--border);
          border-radius:var(--r); color:var(--text2);
          font-family:var(--font); font-size:.82rem; font-weight:500;
          padding:0 12px; cursor:pointer; white-space:nowrap;
          transition:all var(--ease);
        }
        .btn:hover { border-color:var(--accent); color:var(--accent); background:var(--dim); }
        .btn.spin svg { animation:spin .75s linear infinite; }
        @keyframes spin { to{transform:rotate(360deg)} }
        .result-count {
          margin-left:auto; font-size:.78rem; color:var(--muted);
          display:flex; align-items:center; gap:8px;
        }
        .result-count strong { color:var(--text); font-weight:600; }
        .filter-badge {
          background:var(--dim); color:var(--accent);
          border:1px solid var(--accent); border-radius:99px;
          font-size:.68rem; font-weight:600; padding:1px 8px;
        }

        /* ── Table ── */
        .tab-body { display:flex; flex-direction:column; flex:1; min-height:0; padding-bottom:48px; }
        .tbl-wrap { flex:1; overflow-x:auto; padding:0 24px 12px; }
        table { width:100%; border-collapse:collapse; font-size:.82rem; margin-top:0; }
        thead th {
          background:var(--surface);
          padding:10px 14px;
          text-align:left;
          font-size:.72rem; font-weight:600;
          text-transform:uppercase; letter-spacing:.05em;
          color:var(--muted);
          border-bottom:2px solid var(--border);
          white-space:nowrap; user-select:none;
          position:sticky; top:0;
          transition:color var(--ease);
        }
        thead th:hover { color:var(--text); }
        thead th.sorted { color:var(--accent); }
        .th-inner { display:inline-flex; align-items:center; gap:4px; }
        tbody tr {
          border-bottom:1px solid var(--border);
          transition:background var(--ease);
          animation:rowIn .18s ease both;
        }
        tbody tr:hover { background:var(--surface2); }
        tbody tr.row-expanded { background:var(--surface2); }
        tbody tr.row-failed { background:var(--fail-bg); border-left:3px solid var(--fail-bdr); }
        tbody tr.row-failed:hover { filter:brightness(0.97); }
        tbody tr.row-selected { background:rgba(232,0,13,0.06); border-left:3px solid var(--accent); }
        @keyframes rowIn { from{opacity:0;transform:translateY(2px)} to{opacity:1;transform:none} }
        td {
          padding:10px 14px; color:var(--text);
          font-size:.82rem;
          max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
        }
        .filename-cell { color:var(--accent2); }

        /* ── Expanded row ── */
        tr.expanded-row { background:var(--bg) !important; }
        tr.expanded-row:hover { background:var(--bg) !important; }
        tr.expanded-row td { padding:0; }
        .detail-panel {
          padding:18px 24px;
          border-top:1px solid var(--border);
          border-bottom:1px solid var(--border);
        }
        .detail-grid {
          display:grid;
          grid-template-columns:repeat(auto-fill, minmax(240px,1fr));
          gap:12px 28px;
        }
        .detail-field { display:flex; flex-direction:column; gap:3px; }
        .detail-label { font-size:.66rem; font-weight:600; color:var(--muted); text-transform:uppercase; letter-spacing:.06em; }
        .detail-value { font-family:var(--mono); font-size:.78rem; color:var(--text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

        /* ── Icon btn ── */
        .icon-btn { background:none; border:none; cursor:pointer; color:var(--muted); display:inline-flex; padding:2px; transition:color var(--ease); }
        .icon-btn:hover { color:var(--accent); }

        /* ── Skeleton ── */
        @keyframes shimmer { 0%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes slideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }

        /* ── Empty / error ── */
        .empty-state {
          display:flex; align-items:center; justify-content:center;
          padding:60px 20px; color:var(--muted); font-size:.82rem; text-align:center;
        }
        .empty-state.error { color:var(--danger); }

        /* ── Pagination ── */
        .pagination {
          display:flex; align-items:center; gap:3px;
          justify-content:flex-end; padding:10px 24px;
          border-top:1px solid var(--border);
        }
        .pg-btn {
          background:var(--surface2); border:1px solid var(--border);
          border-radius:var(--r); color:var(--text2);
          font-size:.78rem; padding:3px 9px; cursor:pointer;
          transition:all var(--ease); min-width:30px; text-align:center;
        }
        .pg-btn:hover:not(:disabled) { border-color:var(--accent); color:var(--accent); }
        .pg-btn:disabled { opacity:.35; cursor:not-allowed; }
        .pg-btn.active { background:var(--accent); border-color:var(--accent); color:#fff; font-weight:600; }
        .pg-info { font-size:.74rem; color:var(--muted); margin-left:8px; }

        /* ── Modal ── */
        .modal-overlay {
          position:fixed; inset:0; z-index:500;
          background:rgba(0,0,0,0.45);
          display:flex; align-items:center; justify-content:center;
          padding:24px; backdrop-filter:blur(3px);
          animation:fadeIn .15s ease;
        }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        .modal-box {
          background:var(--surface); border:1px solid var(--border);
          border-radius:var(--r2);
          width:100%; max-width:800px; max-height:86vh;
          display:flex; flex-direction:column;
          box-shadow:var(--shadow-lg);
          animation:slideUp .18s ease;
        }
        .modal-hdr {
          display:flex; align-items:center; justify-content:space-between;
          padding:14px 20px; border-bottom:1px solid var(--border);
        }
        .modal-title { font-weight:600; font-size:.92rem; }
        .modal-body { overflow-y:auto; overflow-x:auto; flex:1; padding:18px 20px; }
      `}</style>

      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>

        {/* ── Header ── */}
        <header className="hdr">
          <div className="hdr-logo">
            <div className="hdr-wordmark">
              <div className="hdr-wordmark-top">The Coca‑Cola Company</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div className="hdr-wordmark-btm">File Service <em>Tracker</em></div>
                {ENV_LABEL && (
                  <span style={{
                    background: ENV_LABEL === "PROD" ? "rgba(220,38,38,0.85)" : ENV_LABEL === "UAT" ? "rgba(217,119,6,0.85)" : "rgba(37,99,235,0.85)",
                    color: "#fff", borderRadius: 4, padding: "1px 7px",
                    fontSize: ".6rem", fontWeight: 700, letterSpacing: ".08em",
                    fontFamily: "var(--mono)", textTransform: "uppercase", flexShrink: 0,
                  }}>{ENV_LABEL}</span>
                )}
              </div>
            </div>
          </div>
          <div className="hdr-pulse" title="Live" />

          {/* Global search */}
          <div className="hdr-search">
            <span className="hdr-search-icon">{Ico.search}</span>
            <input
              className="hdr-search-inp"
              placeholder="Search across all tabs…"
              value={globalSearch}
              onChange={e => setGlobalSearch(e.target.value)}
            />
            {globalSearch && (
              <button className="hdr-search-clear" onClick={() => { setGlobalSearch(""); setGsResults(null); }}>
                {Ico.x}
              </button>
            )}
            {/* Dropdown results */}
            {(gsLoading || gsResults) && (
              <div className="gs-dropdown">
                {gsLoading && <div className="gs-empty">Searching all tabs…</div>}
                {gsResults && Object.entries(gsResults).map(([section, { data, total, tab: targetTab }]) => (
                  data.length === 0 ? null : (
                    <div key={section} className="gs-section">
                      <div className="gs-section-hdr">
                        <span>{section} ({total})</span>
                        <button onClick={() => handleGsSelect(targetTab)}>View all →</button>
                      </div>
                      {data.slice(0, 3).map((row, i) => (
                        <div key={i} className="gs-row" onClick={() => handleGsSelect(targetTab)}>
                          <div className="gs-dot" />
                          <span>{GS_LABEL[section]?.(row) || "—"}</span>
                        </div>
                      ))}
                    </div>
                  )
                ))}
                {gsResults && Object.values(gsResults).every(s => s.data.length === 0) && (
                  <div className="gs-empty">No results found for "{globalSearch}"</div>
                )}
              </div>
            )}
          </div>

          <div className="hdr-controls">
            {/* Time range */}
            <select className="sel-time" value={isCustom ? CUSTOM_RANGE : hours} onChange={e => setHours(e.target.value)}>
              {TIME_OPTIONS.map(o => <option key={o.hours} value={o.hours}>{o.label}</option>)}
              <option value={CUSTOM_RANGE}>Custom range…</option>
            </select>
            {isCustom && (
              <>
                <input type="datetime-local" className="sel-time" title="From (max 90 days back)"
                  value={toLocalInput(rangeFrom)}
                  min={toLocalInput(new Date(Date.now() - MAX_RANGE_DAYS * 86400000))}
                  max={toLocalInput(new Date())}
                  onChange={e => updateRange("from", e.target.value)} />
                <span style={{ color: "rgba(255,255,255,0.7)", fontSize: ".75rem" }}>to</span>
                <input type="datetime-local" className="sel-time" title="To"
                  value={toLocalInput(rangeTo)}
                  min={toLocalInput(new Date(Date.now() - MAX_RANGE_DAYS * 86400000))}
                  max={toLocalInput(new Date())}
                  onChange={e => updateRange("to", e.target.value)} />
              </>
            )}

            {/* Auto-refresh selector */}
            <select className="sel-time" value={refreshMs} onChange={e => setRefreshMs(Number(e.target.value))}>
              {REFRESH_OPTIONS.map(o => <option key={o.ms} value={o.ms}>{o.label}</option>)}
            </select>

            {/* Countdown ring */}
            {refreshMs > 0 && (() => {
              const r = 11, circ = 2 * Math.PI * r;
              const pct = countdown / (refreshMs / 1000);
              const offset = circ * (1 - pct);
              return (
                <div className="refresh-ring">
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <circle className="refresh-ring-bg" cx="14" cy="14" r={r} />
                    <circle className="refresh-ring-fg" cx="14" cy="14" r={r}
                      strokeDasharray={circ} strokeDashoffset={offset} />
                  </svg>
                  <div className="refresh-ring-num">{countdown}</div>
                </div>
              );
            })()}

            {/* Dark/light toggle */}
            <button className="mode-btn" onClick={() => setDarkMode(d => !d)} title="Toggle theme">
              {darkMode ? "☀️" : "🌙"}
            </button>

            {/* Logout */}
            <button
              title="Sign out"
              onClick={() => {
                const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
                trackEvent("UserLogout", { upn: account?.username || "unknown" });
                instance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
              }}
              style={{ display: "flex", alignItems: "center", gap: 5, fontSize: ".78rem", fontWeight: 600, letterSpacing: ".01em", padding: "4px 10px", borderRadius: 5, color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap", background: "none", border: "none", cursor: "pointer" }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign out
            </button>
          </div>
        </header>

        {/* ── Tab nav ── */}
        <nav className="tab-nav">
          {[
            ["dashboard",   "Dashboard"],
            ["files",       "Files Processed"],
            ["pipelines",   "Pipelines"],
            ["partners",    "Partners"],
            ["connections", "Connections"],
            ["flows",       "Flows"],
            ["contacts",    "Contacts"],
            ["messages",    "Message Hub"],
          ].map(([id, label]) => (
            <button key={id} className={`tab-btn ${tab === id ? "active" : ""}`} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
          {isAdmin && (
            <button className={`tab-btn ${tab === "administration" ? "active" : ""}`} onClick={() => setTab("administration")}>
              Administration
            </button>
          )}
          {isAdmin && (
            <button className={`tab-btn ${tab === "api-test" ? "active" : ""}`} onClick={() => setTab("api-test")}
              style={{ color: tab === "api-test" ? "var(--accent)" : "var(--muted)", fontStyle: "italic" }}>
              API Test
            </button>
          )}
        </nav>

        {/* ── Tab content ── */}
        {tab === "dashboard"   && <DashboardTab   hours={Number(hours) || 24} from={isCustom ? rangeFrom : ""} to={isCustom ? rangeTo : ""} refreshTick={refreshTick} isAdmin={isAdmin} />}
        {tab === "files"       && <FilesTab       hours={Number(hours) || 24} from={isCustom ? rangeFrom : ""} to={isCustom ? rangeTo : ""} setHours={setHours} refreshTick={refreshTick} />}
        {tab === "pipelines"   && <PipelinesTab   hours={Number(hours) || 24} from={isCustom ? rangeFrom : ""} to={isCustom ? rangeTo : ""} setHours={setHours} refreshTick={refreshTick} />}
        {tab === "partners"    && <PartnersTab                          refreshTick={refreshTick} />}
        {tab === "connections" && <ConnectionsTab                       refreshTick={refreshTick} />}
        {tab === "flows"       && <FlowsTab                            refreshTick={refreshTick} />}
        {tab === "contacts"    && <ContactsTab                         refreshTick={refreshTick} />}
        {tab === "messages"    && <MessageHubTab  hours={Number(hours) || 24} from={isCustom ? rangeFrom : ""} to={isCustom ? rangeTo : ""} refreshTick={refreshTick} />}
        {tab === "administration" && isAdmin && <AdministrationTab />}
        {tab === "api-test"      && isAdmin && <OnboardingTestTab />}
      </div>
    </>
    </AccessContext.Provider>
  );
}